/**
 * TODAS las llamadas a la API de Softfi, en un solo lugar.
 *
 * Cuando quieras saber qué le pide el frontend al backend, este es el archivo.
 * Están agrupadas por módulo, en el mismo orden que en el backend.
 */

import { api, descargar } from "./api";
import type * as Consultorio from "@/features/consultorio/tipos";
import type {
  IngredienteAConfirmar,
  RecetaAConfirmar,
  RecetasLeidas,
  ResultadoDeRecetas,
  BusinessTemplate,
  CashCategory,
  CashDirection,
  CashSession,
  CashSessionRow,
  CatalogoDePermisos,
  Category,
  Cotizacion,
  CotizacionRow,
  ResumenDeCotizaciones,
  InstallResult,
  OnboardingStatus,
  Currency,
  Customer,
  DailySummary,
  Devolucion,
  DevolucionIn,
  DevolucionRow,
  Invitation,
  InvitationPreview,
  Item,
  ItemListRow,
  Location,
  Me,
  Member,
  AvisoDePago,
  EstadoDeSuscripcion,
  ComisionPlataforma,
  ConexionPasarela,
  MetricasPlataforma,
  Movement,
  NegocioCreado,
  NegocioFila,
  Page,
  PagoIn,
  PagoPlataforma,
  PaymentMethod,
  PlanPlataforma,
  ProveedorPasarela,
  Purchase,
  PurchaseRow,
  Rate,
  RatesSnapshot,
  Sale,
  SaleRow,
  Session,
  StockRow,
  StockSummary,
  Supplier,
  Tax,
  Tenant,
  ResumenProceso,
  SocioPlataforma,
  TenantCurrency,
  TenantSettings,
  VentaDevolvible,
  VerificacionPago,
  Propuesta,
  FacturaEnFoto,
  FacturaLeida,
  CodigoEscaneado,
  FilaAConfirmar,
  ResultadoImportacion,
  ListaDePrecios,
  ConteoDeTraslados,
  Traslado,
  Empaque,
  TrasladoFila,
  Apartado,
  ApartadoDetalle,
  ResumenApartados,
  CodigoDescuento,
  QuienAplicaDescuento,
  DescuentoCalculado,
  ReporteDeCuentas,
  CostoDeSucursales,
  SucursalDelPanel,
  SucursalesDelNegocio,
} from "./types";

// =============================================================================
//  AUTENTICACIÓN
// =============================================================================

/**
 * Los filtros de varios valores del registro de ventas (2026-10-16), separados
 * por comas: `metodos=a,b`. Dentro de uno basta con cumplir cualquiera; entre
 * filtros, todos. En `listas`, «normal» es sin lista.
 */
export type FiltrosDelRegistro = {
  metodos?: string;
  vendedores?: string;
  listas?: string;
  estados?: string;
};

export const auth = {
  /**
   * Si alguien puede registrarse solo, o las cuentas nuevas van por WhatsApp.
   *
   * Es público —se pregunta antes de tener cuenta— y es lo mismo que lee la
   * página principal para decidir qué botones enseña.
   */
  comoEsElRegistro: () =>
    api<{ registro_abierto: boolean; dias_de_prueba: number; whatsapp: string }>(
      "/publico/landing",
      { publico: true },
    ),

  registrarNegocio: (datos: {
    full_name: string;
    email: string;
    password: string;
    business_name: string;
    business_type: string;
    modo_operacion?: string;
    primary_currency_code: string;
    country_code?: string;
    timezone?: string;
    phone?: string | null;
  }) => api<Session>("/auth/register-business", { method: "POST", body: datos, publico: true }),

  entrar: (email: string, password: string) =>
    api<Session>("/auth/login", {
      method: "POST",
      body: { email, password, device_info: navigator.userAgent.slice(0, 200) },
      publico: true,
    }),

  yo: () => api<Me>("/auth/me"),

  /** Confirma el correo con el código de 6 dígitos. */
  verificarCorreo: (codigo: string) =>
    api<{ message: string }>("/auth/verificar", { method: "POST", body: { codigo } }),

  /**
   * Pide otro código.
   *
   * `codigo_de_desarrollo` solo llega cuando el servidor NO tiene SMTP
   * configurado, para poder trabajar sin montar un correo. En producción
   * siempre viene vacío.
   */
  reenviarCodigo: () =>
    api<{ mensaje: string; codigo_de_desarrollo?: string; ya_estaba?: boolean }>(
      "/auth/reenviar-codigo",
      { method: "POST" },
    ),

  /**
   * El enlace para confirmar la cuenta escribiéndonos por WhatsApp.
   *
   * ⚠️ Es al revés que un código normal: el cliente NOS escribe. Así no se le
   * paga a Meta por cada código y, sobre todo, el número de Softfi deja de
   * mandar mensajes automáticos a desconocidos — que es el patrón que banean.
   *
   * De paso verifica mejor: el teléfono llega desde WhatsApp, así que no puede
   * ser falso ni estar mal tecleado.
   */
  enlaceDeWhatsapp: () =>
    api<{
      codigo?: string;
      texto?: string;
      numero?: string;
      enlace?: string;
      ya_estaba?: boolean;
      mensaje?: string;
    }>("/auth/confirmar-por-whatsapp", { method: "POST" }),

  /**
   * Pide el enlace de recuperación.
   *
   * ⚠️ Responde SIEMPRE lo mismo, exista o no la cuenta. La pantalla no debe
   * intentar deducir si el correo está registrado — es a propósito, para que
   * nadie pueda averiguar quiénes son clientes de Softfi probando correos.
   */
  olvideClave: (email: string) =>
    api<{ message: string }>("/auth/forgot-password", {
      method: "POST",
      body: { email },
      publico: true,
    }),

  restablecerClave: (token: string, nueva: string) =>
    api<{ message: string }>("/auth/reset-password", {
      method: "POST",
      body: { token, new_password: nueva },
      publico: true,
    }),

  cambiarNegocio: (tenant_id: string) =>
    api<Session>("/auth/switch-tenant", { method: "POST", body: { tenant_id } }),

  salir: (refresh_token: string) =>
    api<{ message: string }>("/auth/logout", { method: "POST", body: { refresh_token } }),

  cambiarClave: (current_password: string, new_password: string) =>
    api<{ message: string }>("/auth/change-password", {
      method: "POST",
      body: { current_password, new_password },
    }),
};

// =============================================================================
//  INVITACIONES (públicas)
// =============================================================================

export const invitaciones = {
  ver: (token: string) =>
    api<InvitationPreview>("/invitations/preview", { params: { token }, publico: true }),

  aceptar: (datos: { token: string; full_name?: string; password?: string }) =>
    api<Session>("/invitations/accept", { method: "POST", body: datos, publico: true }),
};

// =============================================================================
//  EL NEGOCIO
// =============================================================================

/**
 * La suscripción, desde el lado del NEGOCIO.
 *
 * ⚠️ No confundir con `plataforma`, que es el panel de Softfi. Aquí solo hay
 * una cosa: avisar de que ya se pagó. Y avisar no desbloquea nada — lo abre
 * una persona confirmándolo, porque cualquiera puede escribir una referencia
 * inventada.
 */
export const suscripcion = {
  avisarPago: (datos: {
    monto: string;
    metodo: string;
    referencia: string;
    pagado_el?: string | null;
    nota?: string | null;
  }) =>
    api<AvisoDePago>("/tenant/suscripcion/avisar-pago", { method: "POST", body: datos }),

  /** El aviso que está en revisión, o `null`. Para no mandarlo tres veces. */
  miAviso: () => api<AvisoDePago | null>("/tenant/suscripcion/mi-aviso"),

  /** Si la cuenta está al día o suspendida. Contesta también estando bloqueada. */
  estado: () => api<EstadoDeSuscripcion>("/tenant/suscripcion/estado"),
};

export const negocio = {
  obtener: () => api<Tenant>("/tenant"),

  /**
   * No devuelve nada: su respuesta trae la huella del acceso, y con eso basta
   * (ver `registrarHuellaDeAcceso` en `api.ts`). Se pide cada poco.
   */
  revisarAcceso: () => api<void>("/tenant/acceso"),

  actualizar: (cambios: Partial<Tenant>) =>
    api<Tenant>("/tenant", { method: "PATCH", body: cambios }),

  actualizarAjustes: (cambios: TenantSettings) =>
    api<Tenant>("/tenant/settings", { method: "PATCH", body: cambios }),

  completarOnboarding: () => api<Tenant>("/tenant/complete-onboarding", { method: "POST" }),

  /** El logo: sale en todo lo que se imprime en hoja carta y en el recibo. */
  subirLogo: (archivo: File) =>
    api<Tenant>("/tenant/logo", { method: "POST", body: conArchivo(archivo) }),

  quitarLogo: () => api<Tenant>("/tenant/logo", { method: "DELETE" }),

  /**
   * Mandarnos una sugerencia.
   *
   * El servidor contesta que sí aunque el correo no salga: quien acaba de
   * escribir su idea no puede hacer nada con «falló el envío», y un error rojo
   * al mandar una sugerencia es la última que manda esa persona.
   */
  sugerir: (texto: string) =>
    api<{ message: string }>("/tenant/sugerencias", { method: "POST", body: { texto } }),

  sucursales: () => api<Location[]>("/tenant/locations"),

  /** Los puntos donde se cobra. Cada uno lleva su propio turno y su arqueo. */
  cajas: () => api<import("./types").Register[]>("/tenant/registers"),

  /**
   * Otra caja en el mismo local.
   *
   * ⚠️ Hace falta cuando cobran dos personas a la vez: con un solo turno el
   * arqueo mezcla el dinero de ambas y el faltante del cierre no es de ninguna.
   */
  crearCaja: (datos: { location_id: string; name: string }) =>
    api<import("./types").Register>("/tenant/registers", { method: "POST", body: datos }),

  editarCaja: (id: string, datos: { name?: string; is_active?: boolean }) =>
    api<import("./types").Register>(`/tenant/registers/${id}`, {
      method: "PATCH",
      body: datos,
    }),

  /**
   * Abre otro local del MISMO negocio.
   *
   * Tiene su propio catálogo (desde el 2026-09-27): nace vacío, o con el de
   * `copiar_catalogo_de` copiado y vinculado. El inventario, la caja y los
   * reportes también van por separado. Responde **402** si el plan no lo incluye.
   */
  crearSucursal: (datos: {
    nombre: string;
    direccion?: string | null;
    telefono?: string | null;
    /** `almacen` (2026-10-01): guarda y despacha, sin caja ni ventas. Gratis. */
    tipo?: "sucursal" | "almacen";
    copiar_catalogo_de?: string | null;
    /** Nace con el catálogo sincronizado con estas (2026-10-07). O esto o copiar. */
    sincronizar_con?: string[] | null;
    precios_iguales?: boolean;
  }) => api<Location>("/tenant/locations", { method: "POST", body: datos }),

  /**
   * Su catálogo: independiente o sincronizado con otras sucursales
   * (2026-10-07). Sin `confirmar` no cambia nada: dice lo que haría.
   */
  catalogoDeSucursal: (
    id: string,
    datos: {
      modo: "independiente" | "sincronizado";
      con?: string[];
      precios_iguales?: boolean;
      prevalece?: string | null;
      confirmar?: boolean;
    },
  ) =>
    api<import("./types").ResumenDeSincronizacion>(`/tenant/locations/${id}/catalogo`, {
      method: "PUT",
      body: datos,
    }),

  /**
   * Lo que cuestan las sucursales adicionales (2026-10-01): el precio de cada
   * una al mes, cuántas se cobran, cuántas esperan aprobación y el total.
   */
  costoDeSucursales: () => api<CostoDeSucursales>("/tenant/locations/costo"),

  /** Vuelve a pedir una sucursal que Softfi no aprobó: pasa otra vez a esperar. */
  solicitarSucursal: (id: string) =>
    api<Location>(`/tenant/locations/${id}/solicitar`, { method: "POST" }),

  /** Trae a esta sucursal, vinculado, lo de `desde` que aquí todavía no está. */
  copiarCatalogo: (sucursalId: string, desde: string) =>
    api<{ copiados: number; saltados: string[] }>(
      `/tenant/locations/${sucursalId}/copiar-catalogo`,
      { method: "POST", body: { desde } },
    ),

  editarSucursal: (
    id: string,
    cambios: { nombre?: string; direccion?: string | null; telefono?: string | null; activa?: boolean },
  ) => api<Location>(`/tenant/locations/${id}`, { method: "PATCH", body: cambios }),

  /**
   * Crea otro negocio con la misma cuenta.
   *
   * Responde **402** si el plan no lo incluye, no 403: es "hace falta pagar",
   * no "no tienes permiso". El frontend lo distingue para ofrecer subir de
   * plan en vez de enseñar un error rojo.
   */
  crear: (datos: {
    nombre: string;
    business_type?: string;
    primary_currency_code?: string;
    country_code?: string;
    timezone?: string;
  }) => api<Tenant>("/tenant/negocios", { method: "POST", body: datos }),
};

// =============================================================================
//  EL IMPORTADOR DE INVENTARIO
// =============================================================================

/**
 * ⚠️ Son SIEMPRE dos pasos, y el orden importa: `analizar` propone y **no
 * guarda nada**, `confirmar` guarda lo que la persona revisó.
 *
 * Si algún día alguien junta los dos «para que sea más rápido», un catálogo mal
 * leído entra entero antes de que nadie lo mire — y no se nota el primer día,
 * se nota tres semanas después con ventas encima que ya no se arreglan.
 */
export const importador = {
  /** Un Excel con ejemplos y los títulos que se reconocen. */
  plantillaDeRecetas: () => descargar("/tenant/importador/recetas/plantilla"),

  /** Lee una hoja de recetas. Solo Excel o CSV, sin IA. No guarda nada. */
  analizarRecetas: (archivo: File) => {
    const datos = new FormData();
    datos.append("archivo", archivo);
    return api<RecetasLeidas>("/tenant/importador/recetas/analizar", { method: "POST", body: datos });
  },

  /** Guarda UN lote. Con el mismo `loteId`, un reintento no guarda dos veces. */
  confirmarRecetas: (
    recetas: RecetaAConfirmar[],
    ingredientes: IngredienteAConfirmar[],
    tambienEn: string[],
    loteId: string,
  ) =>
    api<ResultadoDeRecetas>("/tenant/importador/recetas/confirmar", {
      method: "POST",
      body: {
        recetas,
        ingredientes,
        crear_categorias: true,
        tambien_en: tambienEn,
        lote_id: loteId,
      },
    }),

  /**
   * `columnas` solo se manda la SEGUNDA vez: cuando Softfi no supo que era
   * cada columna, preguntó, y el usuario ya lo eligió a mano.
   */
  /** `para: "existencias"`: Cargar existencias, que no necesita columna de precio. */
  analizarArchivo: (archivo: File, columnas?: Record<string, number>, para?: "existencias") => {
    const datos = new FormData();
    datos.append("archivo", archivo);
    if (columnas) datos.append("columnas", JSON.stringify(columnas));
    if (para) datos.append("para", para);
    return api<Propuesta>("/tenant/importador/analizar", { method: "POST", body: datos });
  },

  /**
   * La factura del proveedor, para llenar una entrada. Solo Excel o CSV y sin
   * IA; busca cada fila por código de barras, SKU o nombre. No guarda nada.
   */
  leerFactura: (archivo: File, locationId: string | null) => {
    const datos = new FormData();
    datos.append("archivo", archivo);
    if (locationId) datos.append("location_id", locationId);
    return api<FacturaLeida>("/tenant/importador/factura", { method: "POST", body: datos });
  },

  /**
   * La factura en fotos (las páginas) o en PDF, leída con IA (2026-10-29,
   * Premium). El proveedor elegido ayuda a reconocer lo aprendido de él.
   */
  leerFacturaEnFoto: (archivos: File[], locationId: string | null, supplierId: string | null) => {
    const datos = new FormData();
    for (const a of archivos) datos.append("archivos", a);
    if (locationId) datos.append("location_id", locationId);
    if (supplierId) datos.append("supplier_id", supplierId);
    return api<FacturaEnFoto>("/tenant/importador/factura/foto", { method: "POST", body: datos });
  },

  /**
   * Lo que hace el lector (o la cámara) en la ventana de Entrada. 404 si el
   * código no está en el catálogo.
   */
  codigoDeEntrada: (code: string, locationId: string | null) =>
    api<CodigoEscaneado>("/tenant/importador/factura/codigo", {
      params: { code, location_id: locationId ?? undefined },
    }),

  analizarTexto: (texto: string) =>
    api<Propuesta>("/tenant/importador/analizar-texto", { method: "POST", body: { texto } }),

  /**
   * Guarda lo revisado en la sucursal de este aparato. `tambienEn`: las OTRAS
   * sucursales que también tendrán estos productos, copiados y enlazados (sin
   * existencia: la de la hoja es de esta).
   */
  /**
   * Guarda UN lote. `loteId` es su número: con el mismo número, un reintento
   * de un lote que ya se guardó devuelve lo de la primera vez en vez de crear
   * los productos otra vez (2026-09-29).
   */
  confirmar: (filas: FilaAConfirmar[], tambienEn: string[] = [], loteId?: string) =>
    api<ResultadoImportacion>("/tenant/importador/confirmar", {
      method: "POST",
      body: { filas, crear_categorias: true, tambien_en: tambienEn, lote_id: loteId ?? null },
    }),

  /**
   * Qué producto del catálogo es cada fila de la lista, y cuánto hay hoy en
   * la sucursal. No guarda nada. Ver `CargarExistenciasPage`.
   */
  cotejarExistencias: (
    locationId: string | null,
    filas: { linea: number; nombre: string; codigo_barras?: string | null; sku?: string | null }[],
  ) =>
    api<
      {
        linea: number;
        item_id: string | null;
        variant_id: string | null;
        en_catalogo: string | null;
        por: "codigo_barras" | "sku" | "nombre" | null;
        existencia_actual: string | null;
      }[]
    >("/tenant/importador/existencias/cotejar", {
      method: "POST",
      body: { location_id: locationId, filas },
    }),

  /** Pone (o suma) la existencia de una sucursal, y crea lo que no existe. */
  guardarExistencias: (datos: {
    location_id: string | null;
    /** Solo contar: lo que llega comprado va por Entradas. Ver `existencias.py`. */
    modo: "poner";
    filas: (FilaAConfirmar & { item_id?: string | null; variant_id?: string | null })[];
  }) =>
    api<{
      actualizados: number;
      creados: number;
      fallados: number;
      errores: { nombre: string; motivo: string }[];
      listas_creadas?: string[];
      proveedores_creados?: string[];
      avisos?: string[];
    }>(
      "/tenant/importador/existencias/guardar",
      { method: "POST", body: { ...datos, crear_categorias: true } },
    ),
};

// =============================================================================
//  LISTAS DE PRECIOS
// =============================================================================

/**
 * «Detal», «Mayor», «Efectivo», «Binance»… las formas de cobrar del negocio.
 *
 * ⚠️ Un producto NO necesita precio en cada lista: lo que no se pone se cobra
 * al precio normal. Es lo que hace que esto se use — con 400 productos y 3
 * listas, obligar a llenar 1.200 casillas significa que nadie lo activa.
 */
export const listasDePrecios = {
  listar: (soloActivas = false) =>
    api<ListaDePrecios[]>("/tenant/listas-de-precios", {
      params: { solo_activas: soloActivas },
    }),

  crear: (nombre: string, orden = 100) =>
    api<ListaDePrecios>("/tenant/listas-de-precios", {
      method: "POST",
      body: { nombre, orden },
    }),

  actualizar: (id: string, cambios: { nombre?: string; orden?: number; activa?: boolean }) =>
    api<ListaDePrecios>(`/tenant/listas-de-precios/${id}`, { method: "PATCH", body: cambios }),

  /**
   * Borra la lista y sus precios.
   *
   * ⚠️ Las ventas que se cobraron con ella NO se tocan: cada linea guarda el
   * precio que se cobro, no un enlace a la lista.
   */
  borrar: (id: string) =>
    api<void>(`/tenant/listas-de-precios/${id}`, { method: "DELETE" }),

  /**
   * Cambiar el precio y el costo de varios productos de un golpe.
   *
   * ⚠️ Solo se toca lo que venga en cada cambio: mandar el costo no borra el
   * precio de mayor, y al revés. Un `null` dentro de `precios` BORRA el precio
   * de esa lista —el producto vuelve a valer lo normal ahí—, que no es lo
   * mismo que un cero.
   *
   * Máximo 300 por llamada, igual que el servidor.
   */
  guardarEnLote: (cambios: unknown[]) =>
    api<{ guardados: number }>("/tenant/listas-de-precios/en-lote", {
      method: "PATCH",
      body: { cambios },
    }),

  /**
   * Subirle o bajarle un porcentaje a TODO el catálogo.
   *
   * ═══════════════════════════════════════════════════════════════════════════
   *  ⚠️ POR QUÉ ESTO LO HACE EL SERVIDOR Y NO LA PANTALLA
   * ═══════════════════════════════════════════════════════════════════════════
   *
   * Porque la pantalla solo tiene delante los productos de una página. Antes el
   * ajuste se hacía aquí, recorriéndolos: con un catálogo de 3.440 productos —172
   * páginas— pulsar «Subir un 10 %» cambiaba veinte y decía que había subido los
   * precios. El resto se seguía vendiendo al precio viejo.
   *
   * «Súbele un 10 % a todo» es una frase sobre el catálogo entero, y el catálogo
   * entero solo lo tiene el servidor.
   *
   * @param porcentaje Positivo sube, negativo baja.
   * @param listaId    `null` = el precio normal. Con id, deja esa lista a ese
   *                   porcentaje del precio normal (es una regla: aplicarla dos
   *                   veces da lo mismo).
   * @param q          El buscador tal cual. Con «monster» escrito toca todos los
   *                   monster del catálogo, no los que se estén viendo.
   *
   * ⚠️ GUARDA DE INMEDIATO. No deja borrador que revisar, porque con miles de
   * productos no hay nada que revisar a ojo. Hay que preguntar ANTES de llamar.
   */
  ajusteMasivo: (
    porcentaje: number,
    listaId: string | null,
    q?: string,
    sucursalId?: string,
    /**
     * Los filtros de la pantalla (2026-10-18): «súbele un 10 % a todo lo de
     * Polar», «a todo lo que le compro a Distribuidora Andes».
     */
    filtros: { marca?: string; sin_marca?: boolean; category_id?: string; supplier_id?: string } = {},
  ) =>
    api<{ tocados: number }>("/tenant/listas-de-precios/ajuste-masivo", {
      method: "POST",
      body: {
        porcentaje: String(porcentaje),
        lista_id: listaId,
        q: q || null,
        marca: filtros.marca ?? null,
        sin_marca: Boolean(filtros.sin_marca),
        category_id: filtros.category_id ?? null,
        supplier_id: filtros.supplier_id ?? null,
        // Vacía = la del aparato. La manda «aplicar también en las otras
        // sucursales»: el mismo porcentaje, una sucursal por vez.
        location_id: sucursalId ?? null,
      },
    }),
};

// =============================================================================
//  EQUIPO
// =============================================================================

export const equipo = {
  listar: () => api<Member[]>("/tenant/members"),

  /**
   * Qué permisos existen, explicados, y qué trae cada rol de fábrica.
   *
   * Es fijo —no depende del negocio— pero se pregunta al servidor a propósito:
   * ver `CatalogoDePermisos` en `types.ts`.
   */
  catalogoDePermisos: () => api<CatalogoDePermisos>("/tenant/permisos"),

  actualizar: (
    membershipId: string,
    cambios: {
      role?: string;
      status?: string;
      display_name?: string;
      can_provide_services?: boolean;
      default_commission_type?: string;
      default_commission_value?: string | null;
      /** Lo que se le da además de su rol. Reemplaza la lista entera. */
      permisos_extra?: string[];
      /** Lo que se le quita de su rol. Las dos vacías = «vuelve a lo normal». */
      permisos_menos?: string[];
      /** Dónde trabaja. Vacía = todas las sucursales. */
      sucursales?: string[];
    },
  ) => api<Member>(`/tenant/members/${membershipId}`, { method: "PATCH", body: cambios }),

  /** `sucursales` vacía = todas. */
  invitar: (email: string, role: string, sucursales: string[] = []) =>
    api<Invitation>("/tenant/invitations", {
      method: "POST",
      body: { email, role, sucursales },
    }),

  listarInvitaciones: () => api<Invitation[]>("/tenant/invitations"),

  anularInvitacion: (id: string) =>
    api<{ message: string }>(`/tenant/invitations/${id}`, { method: "DELETE" }),
};

// =============================================================================
//  MONEDAS Y TASAS
// =============================================================================

export const monedas = {
  catalogoGlobal: () => api<Currency[]>("/currencies", { publico: true }),

  listar: () => api<TenantCurrency[]>("/tenant/currencies"),

  habilitar: (datos: {
    currency_code: string;
    initial_rate: string;
    accepts_payment?: boolean;
    allows_change?: boolean;
    rounding_mode?: string;
    rounding_increment?: string | null;
  }) => api<TenantCurrency[]>("/tenant/currencies", { method: "POST", body: datos }),

  configurar: (
    code: string,
    cambios: {
      is_active?: boolean;
      accepts_payment?: boolean;
      allows_change?: boolean;
      rounding_mode?: string;
      rounding_increment?: string | null;
      display_order?: number;
      tasa_automatica?: boolean;
    },
  ) => api<TenantCurrency[]>(`/tenant/currencies/${code}`, { method: "PATCH", body: cambios }),

  /** La última tasa del BCV que se supo. `null` si todavía no se pudo consultar. */
  tasaBCV: () => api<import("@/lib/types").TasaBCV | null>("/tenant/currencies/bcv"),

  /**
   * Trae la tasa del BCV ahora mismo.
   *
   * Normalmente no hace falta —la tarea de la hora la trae sola—, pero está
   * para el momento de encender la casilla y para cuando el dueño quiere ver
   * el número ya.
   */
  actualizarConBCV: () =>
    api<TenantCurrency[]>("/tenant/currencies/bcv/actualizar", { method: "POST" }),

  /**
   * Usar la tasa de mañana en cuanto el BCV la publica (en la tarde), o
   * esperar a que empiece su día. Se aplica en el acto.
   */
  usarLaDeManana: (usar: boolean) =>
    api<TenantCurrency[]>("/tenant/currencies/bcv/manana", { method: "PUT", body: { usar } }),

  /**
   * Cambia la moneda en la que están los precios del negocio.
   *
   * ⚠️ Solo antes de la primera venta. Cambiarla NO convierte ningún número:
   * los precios se quedan igual y pasan a leerse en otra moneda. Con ventas
   * hechas, un corte vendido por 8 dólares pasaría a leerse como 8 pesos y
   * todos los reportes anteriores dejarían de significar nada — sin dar un
   * solo error. Por eso el servidor lo cierra en cuanto hay ventas.
   */
  cambiarPrincipal: (
    code: string,
    opciones?: { tasa?: string; tasa_inversa?: string; convertir?: boolean },
  ) => api<Tenant>(`/tenant/currencies/principal/${code}`, { method: "PUT", body: opciones ?? {} }),

  /** Lo que implica cambiar la moneda principal, antes de hacerlo (2026-10-03). */
  vistaPreviaPrincipal: (code: string) =>
    api<import("./types").VistaPreviaCambioDeMoneda>(`/tenant/currencies/principal/${code}/vista-previa`),

  /** Cuándo cambió la moneda principal, y con qué tasa. El último primero. */
  cambiosDePrincipal: () => api<import("./types").CambioDeMoneda[]>("/tenant/currencies/principal/cambios"),

  desactivar: (code: string) =>
    api<{ message: string }>(`/tenant/currencies/${code}`, { method: "DELETE" }),
};

export const tasas = {
  vigentes: () => api<RatesSnapshot>("/tenant/rates/current"),

  /** `location_id`: la tasa propia de esa sucursal (2026-10-07). Sin él, la general. */
  registrar: (currency_code: string, rate: string, note?: string, location_id?: string) =>
    api<RatesSnapshot>("/tenant/rates", {
      method: "POST",
      body: { currency_code, rate, note, ...(location_id ? { location_id } : {}) },
    }),

  /**
   * TODAS las sucursales que cobran, con `puede_entrar`: se le pone tasa propia
   * solo a las suyas. La lista del selector trae solo las suyas (2026-10-07).
   */
  sucursales: () =>
    api<{ id: string; name: string; puede_entrar: boolean }[]>("/tenant/rates/sucursales"),

  /** La sucursal deja su tasa propia y cobra con la general. No se borra nada. */
  volverALaGeneral: (currency_code: string, location_id: string) =>
    api<RatesSnapshot>("/tenant/rates/volver-a-la-general", {
      method: "POST",
      body: { currency_code, location_id },
    }),

  registrarVarias: (lista: { currency_code: string; rate: string }[]) =>
    api<RatesSnapshot>("/tenant/rates/bulk", { method: "POST", body: { rates: lista } }),

  /**
   * «Hoy siguen igual»: vuelve a sellar las tasas que ya están, con fecha de hoy.
   *
   * ⚠️ Para el dueño cuya tasa no cambia. Sin esto convive con un aviso naranja
   * permanente —que a los tres días ya no lee, incluido el día que sí importa—
   * o se inventa un cambio de un céntimo para encender el botón de guardar,
   * moviendo todos los precios del catálogo por nada.
   */
  confirmar: () => api<RatesSnapshot>("/tenant/rates/confirmar", { method: "POST" }),

  /**
   * El historial de una moneda, por páginas.
   *
   * ⚠️ Antes eran las cincuenta últimas y ya: con una tasa al día, siete
   * semanas, y lo de antes no se podía ver.
   */
  historial: (currency: string, page: number, size: number) =>
    api<Page<Rate>>("/tenant/rates/history", { params: { currency, page, size } }),

  convertir: (amount: string, from_currency: string, to_currency: string) =>
    api<{
      amount: string;
      from_currency: string;
      to_currency: string;
      result_exact: string;
      result_rounded: string;
      from_rate: string;
      to_rate: string;
    }>("/tenant/rates/convert", {
      method: "POST",
      body: { amount, from_currency, to_currency },
    }),
};

// =============================================================================
//  CATÁLOGO
// =============================================================================

export const impuestos = {
  listar: (soloActivos = false, scope?: "producto" | "pedido") =>
    api<Tax[]>("/tenant/taxes", { params: { only_active: soloActivos, scope } }),

  crear: (datos: {
    name: string;
    rate_percent: string;
    is_inclusive: boolean;
    is_default?: boolean;
    scope?: "producto" | "pedido";
    currency_codes?: string[];
  }) => api<Tax>("/tenant/taxes", { method: "POST", body: datos }),

  actualizar: (
    id: string,
    cambios: {
      name?: string;
      rate_percent?: string;
      is_inclusive?: boolean;
      is_default?: boolean;
      is_active?: boolean;
      scope?: "producto" | "pedido";
      currency_codes?: string[];
    },
  ) => api<Tax>(`/tenant/taxes/${id}`, { method: "PATCH", body: cambios }),

  desactivar: (id: string) =>
    api<{ message: string }>(`/tenant/taxes/${id}`, { method: "DELETE" }),

  /**
   * Marca o desmarca muchos productos exentos de un impuesto, de una vez: por
   * lista o una categoría entera. Solo toca ESE impuesto — lo demás de cada
   * producto se queda como estaba.
   */
  marcarExentos: (
    id: string,
    datos: { exento: boolean; item_ids?: string[]; category_id?: string },
  ) =>
    api<{ cambiados: number; productos_exentos: number }>(`/tenant/taxes/${id}/exentos`, {
      method: "POST",
      body: datos,
    }),
};

export const categorias = {
  listar: (soloActivas = false) =>
    api<Category[]>("/tenant/categories", { params: { only_active: soloActivas } }),

  /** Con `parent_id`, una subcategoría dentro de esa: «Placas madres › AMD». */
  crear: (datos: {
    name: string;
    color?: string | null;
    icon?: string | null;
    parent_id?: string | null;
  }) => api<Category>("/tenant/categories", { method: "POST", body: datos }),

  /** `parent_id: null` la saca arriba; un id la mete dentro de esa. */
  actualizar: (
    id: string,
    cambios: {
      name?: string;
      color?: string | null;
      is_active?: boolean;
      display_order?: number;
      parent_id?: string | null;
    },
  ) => api<Category>(`/tenant/categories/${id}`, { method: "PATCH", body: cambios }),

  desactivar: (id: string) =>
    api<{ message: string }>(`/tenant/categories/${id}`, { method: "DELETE" }),

  /**
   * Vuelve a encenderla, con las subcategorías que se apagaron con ella y sus
   * productos de vuelta (2026-10-07). Las desactivadas antes de esa fecha
   * vuelven solas, sin sus productos: no quedó apuntado cuáles eran.
   */
  reactivar: (id: string) =>
    api<{ message: string }>(`/tenant/categories/${id}/reactivar`, { method: "POST" }),
};

/** Lo que el servidor dice que pasará al eliminar un producto. */
export type AntesDeEliminar = {
  item_id: string;
  nombre: string;
  unidad: string;
  existencias: { sucursal: string; cantidad: string }[];
  total: string;
  /** Lo que vale esa existencia al costo. Vacío si quien pregunta no ve costos. */
  valor: string | null;
  /** Sus presentaciones («Caja»): se eliminan con él. */
  cajas: string[];
  /** Combos y recetas que lo llevan: con alguno, no se puede eliminar. */
  usado_en: string[];
};

/** «Ordenar» del Catálogo (2026-10-18). */
export type OrdenDelCatalogo = "nombre" | "nombre_desc" | "precio_asc" | "precio_desc" | "recientes";

export type FiltrosCatalogo = {
  q?: string;
  /** Solo los exentos de este impuesto. Lo pide la pantalla de Impuestos. */
  exento_de?: string;
  kind?: "product" | "service";
  category_id?: string;
  only_active?: boolean;
  only_pos?: boolean;
  /**
   * Solo los que están en cero o sin precio.
   *
   * ⚠️ Para lo que queda a medias tras una carga masiva. Un producto en cero se
   * vende y se cobra regalado **sin dar ningún error**: el arqueo cuadra, el
   * reporte cuadra, y nadie se entera. Es de lo poco en Softfi que puede costar
   * dinero sin dejar rastro.
   */
  sin_precio?: boolean;
  /** Solo los de esos grupos (`0a,3f`). Ver `features/pos/porHuellas.ts`. */
  grupos?: string;
  /**
   * Solo las recetas y combos: los productos hechos de otras cosas.
   *
   * ⚠️ No es un `kind`. Por dentro un combo ES un producto, así que se filtra
   * mirando si tiene piezas — no se puede pedir por tipo.
   */
  solo_combos?: boolean;
  /** Solo los desactivados: Catálogo → Desactivados, para volver a activarlos. */
  solo_desactivados?: boolean;
  /** Solo los de esta marca, o los que no tienen (2026-10-18). Ver `filtrosDeProductos`. */
  marca?: string;
  sin_marca?: boolean;
  /** Solo los que se le compran a este proveedor (2026-10-18). */
  supplier_id?: string;
  /** Solo lo que hay o lo que no hay en la sucursal. Sin servicios ni combos. */
  existencia?: "disponible" | "agotado";
  /** Sin él, el orden que le dio el dueño. */
  orden?: OrdenDelCatalogo;
  /**
   * Que traiga cuánto hay de cada uno (`existencia`). Lo pide Vender para la
   * esquina de cada tarjeta.
   */
  con_existencia?: boolean;
  /** De qué sucursal es esa existencia. Vacío = la principal. */
  location_id?: string;
  /**
   * Que traiga también la caja, la tira… de cada producto, como filas aparte.
   * Solo Vender las pide: el resto no las enseña como productos sueltos.
   */
  con_presentaciones?: boolean;
  page?: number;
  size?: number;
};

export const catalogo = {
  listar: (filtros: FiltrosCatalogo = {}) =>
    api<Page<ItemListRow>>("/tenant/items", { params: filtros }),

  /** Las marcas que ya tienen los productos, para sugerirlas al escribir. */
  marcas: () => api<string[]>("/tenant/items/marcas"),

  /**
   * En qué más se cuentan unos productos: su bulto y sus cajas de venta
   * (2026-10-03). `{item_id: medidas}`; los que no tienen, no salen.
   */
  medidas: (ids: string[]) =>
    api<Record<string, { nombre: string; unidades: string; es_bulto: boolean }[]>>(
      "/tenant/items/medidas",
      { params: { ids: ids.join(",") } },
    ),

  /**
   * De qué impuestos está exento cada producto (2026-10-20): para que la
   * entrada con IVA dé el mismo total que el servidor. Hasta 200 por vez; los
   * que no tienen exenciones no salen.
   */
  exentos: (ids: string[]) =>
    api<Record<string, string[]>>("/tenant/items/exentos", { params: { ids: ids.join(",") } }),

  /**
   * Las huellas del catálogo, por grupos, con los mismos filtros que la caja.
   * Ver `features/pos/porHuellas.ts`.
   */
  huellas: (
    filtros: Pick<
      FiltrosCatalogo,
      "only_pos" | "only_active" | "con_existencia" | "con_presentaciones" | "location_id"
    >,
  ) => api<import("@/features/pos/porHuellas").Huellas>("/tenant/items/huellas", { params: filtros }),

  obtener: (id: string) => api<Item>(`/tenant/items/${id}`),

  /**
   * La función del escáner.
   *
   * ⚠️ Si el código lo imprimió una balanza, la respuesta trae además el
   * **peso** (o el importe) que llevaba la etiqueta dentro. El punto de venta
   * agrega la línea ya con esa cantidad: el cajero no escribe nada.
   */
  buscarPorCodigo: (code: string) =>
    api<{
      item: ItemListRow;
      variant: import("./types").Variant;
      de_balanza: boolean;
      /** El peso de la etiqueta, como texto. Es una cantidad. */
      cantidad: string | null;
      /**
       * El importe de la etiqueta, cuando la balanza imprime precio.
       *
       * ⚠️ Cuando viene, manda sobre el precio del catálogo: es el que el
       * cliente tiene delante impreso en el sticker.
       */
      importe: string | null;
    }>("/tenant/items/lookup", { params: { code } }),

  crear: (datos: Record<string, unknown>) =>
    api<Item>("/tenant/items", { method: "POST", body: datos }),

  actualizar: (id: string, cambios: Record<string, unknown>) =>
    api<Item>(`/tenant/items/${id}`, { method: "PATCH", body: cambios }),

  /**
   * DESACTIVA: deja de salir en Vender, en el catálogo web y en el consultor.
   * Se vuelve a activar con `activar`. No elimina (ver `eliminar`).
   */
  desactivar: (id: string) =>
    api<{ message: string }>(`/tenant/items/${id}`, { method: "DELETE" }),

  activar: (id: string) => api<Item>(`/tenant/items/${id}/activar`, { method: "POST" }),

  /** Lo que pasará al eliminarlo: la existencia que se da de baja, sus cajas, dónde se usa. */
  antesDeEliminar: (id: string) =>
    api<AntesDeEliminar>(`/tenant/items/${id}/eliminar`),

  /**
   * ELIMINA: desaparece de todas las pantallas, su existencia se da de baja
   * con un movimiento de salida, y sus códigos quedan libres. Las ventas de
   * antes lo siguen mostrando. No se deshace desde la app.
   */
  eliminar: (id: string) =>
    api<AntesDeEliminar>(`/tenant/items/${id}/eliminar`, { method: "POST" }),

  agregarVariante: (itemId: string, datos: Record<string, unknown>) =>
    api<Item>(`/tenant/items/${itemId}/variants`, { method: "POST", body: datos }),

  editarVariante: (itemId: string, variantId: string, datos: Record<string, unknown>) =>
    api<Item>(`/tenant/items/${itemId}/variants/${variantId}`, { method: "PUT", body: datos }),

  quitarVariante: (itemId: string, variantId: string) =>
    api<Item>(`/tenant/items/${itemId}/variants/${variantId}`, { method: "DELETE" }),

  // ── Tallas y colores ───────────────────────────────────────────────────────

  /** Escalas para empezar. No cambian nunca: se piden una vez y se guardan. */
  ayudasDeMatriz: () => api<AyudasDeMatriz>("/tenant/items/ayudas-de-matriz"),

  /**
   * Guarda la matriz **completa**, como el mapa del salón.
   *
   * La respuesta dice qué pasó con lo que ya estaba, y hay que enseñarlo: si la
   * dueña quita un color y no le decimos que esa casilla se desactivó con sus
   * 8 piezas dentro, concluye que Softfi se comió el inventario.
   */
  guardarMatriz: (itemId: string, matriz: MatrizIn) =>
    api<ResultadoMatriz>(`/tenant/items/${itemId}/matriz`, { method: "PUT", body: matriz }),

  quitarMatriz: (itemId: string) =>
    api<Item>(`/tenant/items/${itemId}/matriz`, { method: "DELETE" }),

  /**
   * La cuadrícula con **dos números por casilla**: lo que hay y lo que salió.
   *
   * ⚠️ Van juntos porque por separado llevan a decisiones opuestas: un cero con
   * doce ventas es «repón ya» y un cero sin ventas es «nunca lo tuviste».
   */
  matrizDeInventario: (itemId: string, locationId?: string | null) =>
    api<MatrizDeInventario>(`/tenant/items/${itemId}/tallas`, {
      params: { location_id: locationId ?? undefined },
    }),

  // ── Combos ─────────────────────────────────────────────────────────────────

  verCombo: (itemId: string) => api<Combo>(`/tenant/items/${itemId}/combo`),

  /**
   * Guarda las piezas **enteras**, igual que la matriz de tallas.
   *
   * Mandar solo lo que cambió obligaría a esta pantalla a llevar la cuenta, y
   * ese es justo el cálculo que se hace mal: la pieza que se quitó aquí se
   * queda en el servidor y sigue saliendo del inventario en cada venta.
   */
  guardarCombo: (itemId: string, combo: ComboIn) =>
    api<Combo>(`/tenant/items/${itemId}/combo`, { method: "PUT", body: combo }),

  quitarCombo: (itemId: string) =>
    api<Item>(`/tenant/items/${itemId}/combo`, { method: "DELETE" }),

  /** Cada ingrediente con en cuántas recetas de la sucursal va y en cuántas es fijo. */
  ingredientesDeRecetas: () =>
    api<IngredienteDeRecetas[]>("/tenant/items/ingredientes-de-recetas"),

  /** Lo deja fijo, o se puede quitar: en todas sus recetas o, con `recetaId`, solo en esa. */
  marcarIngredienteFijo: (ingredienteId: string, fijo: boolean, recetaId?: string) =>
    api<IngredienteDeRecetas>(`/tenant/items/ingredientes-de-recetas/${ingredienteId}/fijo`, {
      method: "PUT",
      body: { fijo, receta_id: recetaId ?? null },
    }),

  // ── Cada sucursal con su catálogo (2026-09-27) ─────────────────────────────

  /**
   * Pone el producto en el catálogo de otras sucursales, VINCULADO, con todo lo
   * suyo y sin existencia. Cada sucursal por separado: la que choca (ya tiene
   * otro producto con ese código) vuelve como `choca`, para emparejarlos.
   */
  copiarASucursales: (itemId: string, sucursales: string[]) =>
    api<CopiaResultado[]>(`/tenant/items/${itemId}/copiar`, {
      method: "POST",
      body: { sucursales },
    }),

  /**
   * «¿Aplicar también en las otras sucursales?» → sí: lleva a las copias los
   * campos elegidos tal como están ahora. `sucursales` vacía = todas.
   */
  aplicarEnSucursales: (datos: {
    items: string[];
    campos: CampoAplicable[];
    sucursales?: string[] | null;
  }) =>
    api<{ copias: number; saltados: string[] }>("/tenant/items/aplicar-en-sucursales", {
      method: "POST",
      body: datos,
    }),

  /**
   * «No es el mismo producto»: sale de su grupo de copias de otras sucursales
   * y el enlace automático ya no lo vuelve a juntar.
   */
  separar: (itemId: string) => api<Item>(`/tenant/items/${itemId}/separar`, { method: "POST" }),

  /** Dos productos que ya existen, cada uno en su sucursal, son el mismo. */
  emparejar: (itemId: string, con: string) =>
    api<Item>(`/tenant/items/${itemId}/emparejar`, { method: "POST", body: { con } }),

  /**
   * «¿Es el mismo?»: productos de OTRAS sucursales con el mismo código, SKU o
   * nombre que aquí todavía no están.
   */
  parecidos: (filtros: { nombre?: string; sku?: string; barcode?: string }) =>
    api<Parecido[]>("/tenant/items/parecidos", { params: filtros }),
};

/** Lo que se puede llevar de un producto a sus copias de otras sucursales. */
export type CampoAplicable =
  | "name"
  | "category_id"
  | "description"
  | "marca"
  | "sku"
  | "barcode"
  | "base_price"
  | "precios";

export type CopiaResultado = {
  location_id: string;
  sucursal: string;
  estado: "copiado" | "ya_estaba" | "choca";
  item_id: string | null;
  mensaje: string | null;
  /** Si choca: el producto de esa sucursal con el mismo código, para emparejar. */
  choca_con: string | null;
  choca_nombre: string | null;
};

export type Parecido = {
  item_id: string;
  name: string;
  location_id: string;
  sucursal: string;
  por: "codigo_barras" | "sku" | "nombre";
  base_price: string;
  sku: string | null;
  barcode: string | null;
  category_name: string | null;
};

export type CasillaDeMatriz = {
  variant_id: string;
  nombre: string;
  valor_1: string | null;
  valor_2: string | null;
  /** Como texto: es una cantidad. */
  existencia: string;
  vendidas: string;
  estado: "hay" | "ultima" | "agotado";
  /** Vacío = va bien, no hay nada que decidir. */
  sugerencia: "reponer" | "no_rota" | null;
};

export type MatrizDeInventario = {
  item_id: string;
  nombre: string;
  eje_1_nombre: string | null;
  eje_2_nombre: string | null;
  valores_1: string[];
  valores_2: string[];
  casillas: CasillaDeMatriz[];
  /** Vacíos sin el permiso de ver costos: el margen no es del mostrador. */
  costo: string | null;
  precio: string;
  utilidad_pct: string | null;
  total_existencia: string;
  reponer: string[];
  sin_rotacion: string[];
  dias: number;
};

export type PlantillaDeTalla = {
  clave: string;
  grupo: string;
  nombre: string;
  /** De dónde salió esa lista, para poder reconocerla o descartarla. */
  donde: string;
  valores: string[];
};

export type AyudasDeMatriz = {
  plantillas: PlantillaDeTalla[];
  colores: { nombre: string; hex: string }[];
  ejes_sugeridos: string[];
};

export type MatrizIn = {
  eje_1: { nombre: string; valores: string[] };
  eje_2?: { nombre: string; valores: string[] } | null;
};

export type ResultadoMatriz = {
  item: Item;
  creadas: number;
  conservadas: number;
  borradas: number;
  /** Las que salieron de la matriz pero tenían existencia o ya se vendieron. */
  desactivadas: string[];
};

// ── Combos ───────────────────────────────────────────────────────────────────

export type ComponenteDeCombo = {
  item_id: string;
  variant_id: string;
  nombre: string;
  /** Como texto: es una cantidad. */
  cantidad: string;
  /**
   * Cómo se vende esta pieza: `kg`, `g`, `l`, `unit`…
   *
   * Sin ella, al reabrir una receta la casilla enseña «0,1» pelado y no hay
   * forma de saber si son cien gramos de queso o un décimo de paquete.
   */
  unidad: string;
  /** Lo que hay de esta pieza ahora, sumando todas las sucursales. */
  existencia: string;
  /**
   * Cuántos combos salen de esta pieza sola.
   *
   * ⚠️ Es lo que convierte «salen 3 desayunos» en algo que se puede arreglar:
   * señala **cuál** de las piezas se está acabando.
   */
  alcanza_para: string;
  /**
   * `false` si la pieza no lleva «controlar existencia» (la bolsa de la cesta):
   * no frena el combo ni sale del inventario al venderlo.
   */
  se_cuenta?: boolean;
  /** Cuánto pierde de peso, en %. Ya va sumada en el costo y en «alcanza para». */
  merma?: string | null;
  /**
   * Lo que cuesta UNA unidad de venta de la pieza, y a cuánto se vende suelta.
   *
   * Es lo que deja a la pantalla de editar hacer la misma cuenta que la de
   * crear mientras se escribe. El costo va vacío sin permiso de ver costos.
   */
  costo_unitario?: string | null;
  precio_unitario?: string;
  /** De qué impuestos está exenta la pieza. */
  impuestos_exentos?: string[];
  /** De qué variación del plato es. Vacío = la llevan todas. */
  variacion_id?: string | null;
  /** Si el cliente puede pedir el plato sin esta pieza: «sin tomate». */
  se_puede_quitar?: boolean;
};

/** Un ingrediente y en cuántas recetas va. `fijas`: en cuántas no se puede quitar. */
export type IngredienteDeRecetas = {
  item_id: string;
  nombre: string;
  recetas: number;
  fijas: number;
  /** Las recetas que lo llevan, y si en cada una es fijo. */
  platos: { item_id: string; nombre: string; fijo: boolean }[];
};

/** Una variación del plato con lo que cuesta armarla. */
export type VariacionDeCombo = {
  id: string;
  nombre: string;
  precio: string;
  /** Lo de todas más lo suyo. Vacío si falta algún costo o sin permiso. */
  costo: string | null;
  cuantos_se_pueden_armar: string | null;
  /** El ingrediente que antes se acaba en esta variación. */
  frena: string | null;
};

export type ComboIn = {
  componentes: { item_id: string; variant_id?: string | null; cantidad: string }[];
};

export type Combo = {
  item_id: string;
  nombre: string;
  precio: string;
  componentes: ComponenteDeCombo[];
  /**
   * Lo que costaría comprando las piezas por separado.
   *
   * ⚠️ Se enseña al lado del precio porque es la única forma de saber si el
   * combo tiene sentido. Un «Desayuno» a $4,50 cuando las piezas sueltas suman
   * $4,00 no es una oferta.
   */
  suma_de_las_partes: string;
  /** Lo que se ahorra el cliente. Negativo = el combo sale MÁS caro. */
  ahorro: string;
  /** La suma de lo que cuestan las piezas. Vacío sin permiso de ver costos. */
  costo: string | null;
  /**
   * Con la existencia de ahora.
   *
   * Es el dato que ningún inventario da: con 40 empanadas y 3 cafés salen
   * **3** desayunos, no 40.
   *
   * `null` si ninguna pieza se cuenta: no hay tope (no es «0», que diría
   * agotado).
   */
  cuantos_se_pueden_armar: string | null;
  /** «Proteína», «Tamaño». Vacío = el plato no tiene variaciones. */
  pregunta?: string | null;
  variaciones?: VariacionDeCombo[];
};

// =============================================================================
//  PUNTO DE VENTA
// =============================================================================

export const metodosPago = {
  /**
   * Qué formas de cobro usa de verdad este negocio, de más a menos.
   *
   * Pares método + moneda de los últimos 30 días. Es lo que ordena los atajos
   * de «Cobro rápido»: en vez de un orden fijo igual para todos, cada negocio
   * ve primero lo que usa. Un negocio nuevo devuelve lista vacía y la pantalla
   * se queda con su orden de siempre.
   */
  masUsados: () =>
    api<{ payment_method_id: string; currency_code: string; usos: number }[]>(
      "/tenant/payment-methods/mas-usados",
    ),

  /**
   * Los datos que Softfi sabe pedir y validar. No cambia nunca durante una
   * sesión, así que se pide una vez y se guarda.
   */
  catalogoDeCampos: () =>
    api<import("@/features/pos/CamposDePago").CampoDePago[]>("/tenant/payment-methods/fields"),

  /** Lo que conviene pedir en un método, según su nombre o su tipo. */
  camposSugeridos: (nombre: string, tipo: string) =>
    api<{ campos: string[]; obligatorios: string[] }>("/tenant/payment-methods/fields/sugeridos", {
      params: { nombre, tipo },
    }),

  /**
   * Los métodos de cobro del negocio.
   *
   * ═══════════════════════════════════════════════════════════════════════
   *  ⚠️ CADA VARIANTE NECESITA SU PROPIA CLAVE DE CACHÉ
   * ═══════════════════════════════════════════════════════════════════════
   *
   * Cinco pantallas guardaban el resultado bajo la MISMA clave
   * `["metodos-pago"]`, unas llamando `listar(true)` —solo activos— y otras
   * `listar()` —todos—. React Query cachea por clave, así que **la primera
   * pantalla que se abriera decidía lo que veían las demás**.
   *
   * En la práctica: abres una devolución (que pide todos), vas a «Por cobrar»
   * (que quiere solo activos), y ahí te ofrece un método DESACTIVADO para
   * registrar el abono. Lo eliges, y el servidor lo rechaza con
   * `method_inactive` — un error que no explica nada porque la pantalla nunca
   * debió ofrecerlo.
   *
   * Las claves son ahora `["metodos-pago", "activos"]` y
   * `["metodos-pago", "todos"]`. Las invalidaciones que ya existían usan
   * `["metodos-pago"]`, que React Query trata como PREFIJO: siguen limpiando
   * las dos sin tocarlas.
   */
  listar: (soloActivos = false) =>
    api<PaymentMethod[]>("/tenant/payment-methods", { params: { only_active: soloActivos } }),

  crear: (datos: {
    name: string;
    type: string;
    allowed_currency_code?: string | null;
    requires_reference?: boolean;
    affects_cash_drawer?: boolean;
    can_give_change?: boolean;
    campos?: string[];
    campos_obligatorios?: string[];
    /** En qué sucursales se usa. Vacía = en todas. */
    sucursales?: string[];

    // ── Solo para los de tipo `credit` ─────────────────────────────────────
    /** `cliente` = fiado de siempre · `tercero` = paga una financiera. */
    credito_quien_debe?: "cliente" | "tercero";
    /** Cómo se llama esa financiera: «Cashea». El servidor la crea como cliente. */
    credito_tercero_nombre?: string | null;
    /** Qué porcentaje se queda. Cada negocio negocia el suyo. */
    credito_comision?: string;
    /** `cuotas` = cobras según paga el cliente · `liquidacion` = te adelantan todo. */
    credito_cuando_pagan?: "cuotas" | "liquidacion";
    credito_dias_cuota?: number;
    credito_cuotas?: number;
    /** `true` = no preguntar al cobrar, usar siempre `credito_cuotas`. */
    credito_cuotas_fijas?: boolean;
    /** Qué % paga el cliente de inicial. Parte la venta en dos cobros. */
    credito_inicial_pct?: string;
    /** `true` = ese % es siempre el mismo y no se pregunta al cobrar. */
    credito_inicial_fija?: boolean;
  }) => api<PaymentMethod>("/tenant/payment-methods", { method: "POST", body: datos }),

  actualizar: (id: string, cambios: Record<string, unknown>) =>
    api<PaymentMethod>(`/tenant/payment-methods/${id}`, { method: "PATCH", body: cambios }),

  /**
   * Borrar de verdad, no desactivar.
   *
   * El servidor solo lo permite si **nunca se cobró con él**; si ya se usó
   * responde `payment_method_in_use` y hay que desactivarlo. La razón es que el
   * recibo de una venta de hace un año tiene que poder reimprimirse igual.
   *
   * ⚠️ Esto existía en el backend desde el principio y la pantalla nunca lo
   * llamó: quien creaba un método por error se quedaba con él en la lista para
   * siempre, solo que en gris. Una función a la que no llega ningún botón es
   * exactamente igual que una que no existe.
   */
  borrar: (id: string) =>
    api<{ message: string }>(`/tenant/payment-methods/${id}`, { method: "DELETE" }),
};

export const clientes = {
  /**
   * Los clientes, **paginados**.
   *
   * ⚠️ Antes cortaba en cincuenta sin decirlo: un negocio con tres mil veía los
   * de la A a la B y no había siguiente página. La única salida era el
   * buscador — y para eso hay que saber el nombre.
   */
  buscar: (
    filtros: {
      q?: string;
      lista_precios_id?: string;
      /** Solo los de esos grupos (`0a,3f`). Ver `features/pos/porHuellas.ts`. */
      grupos?: string;
      page?: number;
      size?: number;
    } = {},
  ) => api<Page<Customer>>("/tenant/customers", { params: filtros }),

  /** Las huellas de los clientes, por grupos. Ver `features/pos/porHuellas.ts`. */
  huellas: () => api<import("@/features/pos/porHuellas").Huellas>("/tenant/customers/huellas"),

  /**
   * Con qué documento se identifica un cliente EN ESTE PAÍS.
   *
   * ⚠️ No es una lista fija y no puede serlo: en Venezuela el RIF lleva una
   * letra pegada (V, E, J) y en Argentina esa letra no existe — hay DNI y
   * CUIT, que son documentos distintos.
   *
   * No cambia durante una sesión, así que se pide una vez.
   */
  tiposDeDocumento: () =>
    api<{ pais: string; tipos: import("./types").TipoDeDocumento[] }>(
      "/tenant/customers/tipos-de-documento",
    ),

  /**
   * El cliente con ese documento, escrito como sea; `null` si no hay ninguno.
   * Lo usa Clientes antes de registrar: crear con un documento ya registrado
   * devuelve al cliente de siempre, y eso hay que decirlo en vez de dar por
   * registrado a uno nuevo.
   */
  porDocumento: (documento: string) =>
    api<Customer | null>(`/tenant/customers/by-document/${encodeURIComponent(documento)}`),

  /**
   * El nombre es lo único obligatorio (salvo los datos que exija el negocio,
   * ver `lib/obligatoriosCliente.ts`).
   *
   * Todo lo demás se puede completar después en la ficha del cliente. Aquí solo
   * están los campos que se piden con alguien esperando de pie en el mostrador
   * — la dirección y el correo no: nadie dicta una dirección en una cola.
   */
  crear: (datos: {
    /**
     * El id que elige el dispositivo, para poder registrar sin internet.
     *
     * ⚠️ Con esto, la venta que quedó guardada apuntando a ese cliente sigue
     * valiendo cuando el cliente por fin llega al servidor: el id es el mismo.
     * Y de paso, reenviar la creación no crea un duplicado — el servidor
     * reconoce el id y devuelve el que ya tenía.
     *
     * Vacío desde una pantalla con internet: lo pone el servidor.
     */
    client_uuid?: string;
    name: string;
    phone?: string | null;
    /** Qué clase de documento. Los que valen dependen del país del negocio. */
    tax_id_tipo?: string | null;
    /** Cédula o RIF, escrito como sea. El servidor lo normaliza para buscarlo. */
    tax_id?: string | null;
    /** Dónde vive. Opcional incluso en el mostrador. */
    address?: string | null;
    /** Desde la pantalla de Clientes (2026-10-05), que crea con la ficha completa. */
    email?: string | null;
    notes?: string | null;
    lista_precios_id?: string | null;
  }) => api<Customer>("/tenant/customers", { method: "POST", body: datos }),

  /**
   * Cambiar un cliente. Solo lo que se envíe.
   *
   * Sirve sobre todo para ponerle una lista de precios fija a alguien que YA es
   * cliente, que es el caso normal: nadie sabe el primer día que doña Carmen va
   * a acabar comprando al mayor.
   */
  actualizar: (
    id: string,
    cambios: {
      name?: string;
      phone?: string | null;
      email?: string | null;
      tax_id_tipo?: string | null;
      tax_id?: string | null;
      /** Dónde vive. Se rellena aquí, no en el mostrador. */
      address?: string | null;
      notes?: string | null;
      lista_precios_id?: string | null;
      is_active?: boolean;
    },
  ) => api<Customer>(`/tenant/customers/${id}`, { method: "PATCH", body: cambios }),

  /**
   * Las cifras de arriba de la pantalla de Clientes: registrados (del negocio
   * entero), quién compró, el mejor cliente y lo pendiente a crédito (de la
   * sucursal elegida). Todo en los últimos 30 días. Pide `reports.view`.
   */
  resumen: () => api<ResumenDeClientes>("/tenant/customers/resumen"),

  /** Los clientes activos en Excel, con las columnas que se pueden volver a importar. */
  exportar: () => descargar("/tenant/customers/exportar"),

  /**
   * Lee un Excel de clientes SIN guardar nada: qué columna es cada campo
   * (reconocido por su título, no se elige a mano) y qué pasará con cada fila.
   */
  leerExcel: (archivo: File) => {
    const datos = new FormData();
    datos.append("archivo", archivo);
    return api<VistaPreviaDeClientes>("/tenant/customers/importar/leer", { method: "POST", body: datos });
  },

  /**
   * Revisa otra vez las filas corregidas en pantalla, sin guardar nada
   * (2026-10-19). Cada una se revisa sola: lo repetido dentro del archivo lo
   * calcula la pantalla con `documento_clave`.
   */
  revisarFilas: (filas: FilaDeClienteIn[]) =>
    api<FilaDeCliente[]>("/tenant/customers/importar/revisar", {
      method: "POST",
      body: { filas },
    }),

  /**
   * Importa UN LOTE de filas revisadas. Nunca duplica: al ya registrado le
   * completa lo vacío. Con `loteId`, un reintento del mismo lote devuelve lo de
   * la primera vez en vez de guardarlo otra vez.
   */
  importar: (filas: FilaDeClienteIn[], loteId?: string) =>
    api<ResultadoDeImportarClientes>("/tenant/customers/importar", {
      method: "POST",
      body: { filas, lote_id: loteId ?? null },
    }),
};

export type ResumenDeClientes = {
  moneda: string;
  registrados: number;
  registrados_30: number;
  compraron_30: number;
  /** Los 30 días anteriores: para la comparación. */
  compraron_30_antes: number;
  primera_vez_30: number;
  mejor_cliente: string | null;
  mejor_cliente_total: string;
  mejor_cliente_compras: number;
  vendido_con_cliente_30: string;
  vendido_total_30: string;
  /** El crédito está encendido o queda algo por cobrar. */
  credito_en_uso: boolean;
  por_cobrar: string;
  cuentas_pendientes: number;
  /** Solo clientes: las financieras como Cashea van aparte. */
  clientes_con_deuda: number;
  financieras_con_deuda: string[];
  /** Lo vencido sin cobrar (fecha acordada o, sin ella, 30 días). */
  vencido_30: string;
};

/** Un campo de cliente que se reconoce en el Excel. */
export type CampoDeCliente =
  | "nombre"
  | "apellido"
  | "tipo_documento"
  | "documento"
  | "telefono"
  | "correo"
  | "direccion"
  | "lista_precios"
  | "notas";

export type FilaDeCliente = {
  /** El número de la fila en el Excel. */
  fila: number;
  nombre: string;
  tipo_documento: string | null;
  documento: string | null;
  telefono: string | null;
  correo: string | null;
  direccion: string | null;
  lista_precios_id: string | null;
  lista_precios_nombre: string | null;
  notas: string | null;
  /** «nuevo», «existente» (se completan sus datos) o «error» (no entra). */
  estado: "nuevo" | "existente" | "error";
  avisos: string[];
  errores: string[];
  existente_id: string | null;
  existente_nombre: string | null;
  /** Los datos vacíos que se le completan a un cliente ya registrado. */
  completa: string[];
  /** El documento como se compara («V-12.345.678» → «12345678»). */
  documento_clave: string | null;
  /**
   * Al leer el Excel: la fila donde ese documento salió primero. La pantalla
   * lo recalcula al corregir o quitar filas.
   */
  repetido_de: number | null;
};

export type VistaPreviaDeClientes = {
  /** Con `campo` nulo, la columna no se importa: su título no se reconoce. */
  columnas: { indice: number; titulo: string; campo: CampoDeCliente | null }[];
  filas: FilaDeCliente[];
  nuevos: number;
  existentes: number;
  con_error: number;
};

export type FilaDeClienteIn = Pick<
  FilaDeCliente,
  "fila" | "nombre" | "tipo_documento" | "documento" | "telefono" | "correo" | "direccion" | "lista_precios_id" | "notas"
>;

export type ResultadoDeImportarClientes = {
  creados: number;
  actualizados: number;
  sin_cambios: number;
  con_error: number;
  errores: { fila: number; nombre: string; motivo: string }[];
};

export type VentaIn = {
  client_uuid: string;
  items: {
    item_id: string;
    variant_id?: string | null;
    qty: string;
    discount?: string;
    staff_user_id?: string | null;
    /**
     * De cuánto es la recarga, en los servicios de monto variable.
     *
     * ⚠️ SE MANDA EL MONTO, NO EL PRECIO. «Recárgame 500» y el servidor saca
     * lo que se cobra: 500 más el 50% son 750. Mandando el precio ya hecho,
     * cualquiera con las herramientas del navegador abiertas podría cobrar
     * una recarga de 500 a 500 —y la venta cuadraría, porque de la cuenta
     * saldría lo que él dijera—. Es la misma razón por la que el precio de un
     * producto tampoco viaja en la venta.
     */
    monto?: string;
    /**
     * El cajero pulsó «cobrar al mayor» en esta línea.
     *
     * ⚠️ Es un SÍ, no un monto: el precio lo saca el servidor del producto.
     * Y no es automático a propósito — Softfi avisa, no decide.
     */
    /**
     * El precio por unidad que cobró ESTE aparato. Solo en las ventas sin
     * internet: el servidor las registra a ese precio aunque el catálogo haya
     * cambiado mientras tanto, y apunta el del catálogo si no coincide. En una
     * venta con internet no se manda y el precio lo pone el servidor.
     */
    unit_price?: string;
    /** Los extras elegidos («+ Queso»): solo los ids, el precio lo pone el servidor. */
    opcion_ids?: string[];
    /** «Sin tomate»: `variant_id` de los ingredientes que no se descuentan. */
    sin?: string[];
  }[];
  payments: PagoIn[];
  customer_id?: string | null;

  /**
   * Con qué lista de precios se cobra: «Mayor», «Efectivo», «Binance».
   *
   * Vacío = los precios normales del catálogo. Los productos que no tengan
   * precio propio en esa lista se cobran igualmente a su precio normal.
   */
  lista_precios_id?: string | null;
  /**
   * En qué local se hizo la venta.
   *
   * Si va vacío el servidor usa la sucursal principal — que es lo correcto para
   * un negocio de un solo local, y por eso este campo es opcional.
   */
  location_id?: string | null;
  note?: string | null;
  discount_total?: string;

  /**
   * Un cupón: «PROMO10», «NAVIDAD».
   *
   * ⚠️ SE MANDA EL CÓDIGO, NUNCA EL MONTO. El servidor lo busca, comprueba que
   * sirve y calcula cuánto rebaja. Si el monto viniera de aquí, cualquiera con
   * las herramientas del navegador abiertas podría mandarse un 100% de
   * descuento y la venta quedaría registrada como normal.
   */
  codigo_descuento?: string | null;

  /**
   * Qué impuestos de PEDIDO encendió el cajero (el IGTF y parientes).
   *
   * ⚠️ SE MANDAN LOS IDENTIFICADORES, NUNCA EL MONTO — el mismo criterio que
   * el cupón. El servidor busca cada impuesto, comprueba que sigue activo y
   * calcula cuánto sale sobre lo que se pagó en las monedas que alcanza.
   */
  order_tax_ids?: string[];

  /**
   * De qué cotización salió esta venta.
   *
   * ⚠️ NO CAMBIA NI UN CÉNTIMO. Los precios se calculan con el catálogo de hoy
   * como en cualquier venta; esto solo deja apuntado que aquel presupuesto
   * acabó cerrándose, que es de donde sale la tasa de cierre.
   */
  cotizacion_id?: string | null;

  /**
   * «Te lo guardo y me lo pagas por partes».
   *
   * ⚠️ La mercancía SALE del inventario igual que en cualquier venta: se guarda
   * en la trastienda y ya no se le puede vender a otro. Lo único que cambia es
   * que no se entrega hasta que esté pagada.
   *
   * Exige cliente: una bolsa guardada semanas sin nombre ni teléfono es de
   * nadie.
   */
  es_apartado?: boolean;

  created_at?: string;
  rates?: Record<string, string>;
  /**
   * La moneda principal con la que este aparato puso los precios (2026-10-03).
   * Si el negocio la cambió mientras estaba sin internet, el servidor convierte
   * la venta a la de hoy.
   */
  moneda?: string;
};

export const pos = {
  vender: (venta: VentaIn) => api<Sale>("/pos/sales", { method: "POST", body: venta }),

  obtenerVenta: (id: string) => api<Sale>(`/pos/sales/${id}`),

  ventasDeHoy: (filtros: { q?: string; page?: number; size?: number } = {}) =>
    api<Page<SaleRow>>("/pos/sales/today", { params: filtros }),

  /**
   * El registro de ventas, **paginado**.
   *
   * ⚠️ `q` se busca EN EL SERVIDOR. Antes se descargaban 500 ventas y se
   * filtraban en el navegador, así que una referencia de hace tres meses no
   * aparecía nunca: no estaba entre las descargadas. Con páginas sería aún
   * peor — se filtrarían solo las cincuenta que se están mirando.
   */
  ventas: (
    filtros: {
      desde?: string;
      hasta?: string;
      status?: string;
      q?: string;
      /** Solo las cobradas, aunque sea en parte, con este método de pago. */
      metodo?: string;
      page?: number;
      size?: number;
    } & FiltrosDelRegistro = {},
  ) => api<Page<SaleRow>>("/pos/sales", { params: filtros }),

  /** Quiénes vendieron en la sucursal y las listas de precios: para los filtros. */
  opcionesDelRegistro: () =>
    api<{
      vendedores: { id: string; nombre: string }[];
      listas: { id: string; nombre: string; activa: boolean }[];
    }>("/pos/sales/filtros"),

  /**
   * Los totales del registro, **sobre lo filtrado y no sobre la página**.
   *
   * Sumar las filas descargadas dejó de servir en cuanto hubo páginas: diría lo
   * que llevan las cincuenta que se ven, no la semana.
   */
  resumenDeVentas: (
    filtros: {
      desde?: string;
      hasta?: string;
      status?: string;
      q?: string;
      metodo?: string;
    } & FiltrosDelRegistro = {},
  ) =>
    api<{
      cuantas: number;
      anuladas: number;
      facturado: string;
      impuestos: string;
      descuentos: string;
      vendido: string;
      fiado: string;
      cobrado: string;
      /**
       * Si alguna venta del RANGO lleva estos datos.
       *
       * ⚠️ Deciden qué columnas se dibujan, y tiene que decidirse sobre el
       * rango entero y no sobre la página: si no, la tabla gana y pierde
       * columnas al pasar de página.
       */
      hay_cliente: boolean;
      hay_referencia: boolean;
      hay_nota: boolean;
      /** Alguna se cobró con una lista de precios: se dibuja «Precio». */
      hay_lista: boolean;
    }>("/pos/sales/resumen", { params: filtros }),

  resumenDeHoy: () => api<DailySummary>("/pos/summary/today"),

  anular: (id: string, reason: string) =>
    api<Sale>(`/pos/sales/${id}/void`, { method: "POST", body: { reason } }),

  /**
   * Corrige una venta de hoy: la original se anula y queda esta, enlazadas.
   * El cuerpo es el de una venta nueva. Ver `corregir_venta` en el servidor.
   */
  corregir: (id: string, venta: VentaIn) =>
    api<Sale>(`/pos/sales/${id}/corregir`, { method: "POST", body: venta }),

  /** Envía un lote de ventas guardadas sin conexión. */
  sincronizar: (sales: VentaIn[]) =>
    api<{
      accepted: number;
      duplicated: number;
      failed: number;
      results: {
        client_uuid: string;
        ok: boolean;
        sale_id: string | null;
        number: string | null;
        duplicate: boolean;
        error_code: string | null;
        error_message: string | null;
      }[];
    }>("/pos/sync", { method: "POST", body: { sales } }),
};

export type CotizacionIn = {
  items: {
    item_id: string;
    variant_id?: string | null;
    qty: string;
    unit_price?: string | null;
    discount?: string;
    staff_user_id?: string | null;
    opcion_ids?: string[];
    /** «Sin tomate»: `variant_id` de los ingredientes quitados. */
    sin?: string[];
  }[];
  customer_id?: string | null;
  lista_precios_id?: string | null;
  nota?: string | null;
  discount_total?: string;
  order_tax_ids?: string[];
  dias_de_validez?: number;
};

/**
 * Cotizaciones: el precio por escrito, antes de que haya venta.
 *
 * ⚠️ No mueven nada. No descuentan inventario, no registran pagos y no salen en
 * ningún reporte de ventas. Los precios los calcula el servidor con el MISMO
 * código que una venta, así que lo que dice el papel es lo que cobra la caja.
 */
export const cotizaciones = {
  crear: (datos: CotizacionIn) =>
    api<Cotizacion>("/cotizaciones", { method: "POST", body: datos }),

  /**
   * Las del rango por páginas, con la búsqueda en el servidor. Antes llegaban
   * hasta 500 y la pantalla buscaba entre ellas: la 501 no salía ni buscándola.
   */
  listar: (filtros: {
    page: number;
    size: number;
    desde?: string;
    hasta?: string;
    estado?: string;
    q?: string;
  }) => api<Page<CotizacionRow>>("/cotizaciones", { params: filtros }),

  obtener: (id: string) => api<Cotizacion>(`/cotizaciones/${id}`),

  resumen: (filtros: { desde?: string; hasta?: string } = {}) =>
    api<ResumenDeCotizaciones>("/cotizaciones/resumen", { params: filtros }),

  borrar: (id: string) => api<void>(`/cotizaciones/${id}`, { method: "DELETE" }),

  /**
   * El costo del delivery de un pedido de la página (2026-11-02): lo escribe
   * quien atiende, según la dirección. Entra como una línea «Delivery»; cero
   * la quita.
   */
  delivery: (id: string, costo: string) =>
    api<Cotizacion>(`/cotizaciones/${id}/delivery`, { method: "PUT", body: { costo } }),
};

/**
 * Devoluciones. Todo pide permiso `pos.refund`: dueño y supervisor, nunca un
 * cajero. Devolver es sacar plata del cajón sin que entre nada a cambio.
 */
export const devoluciones = {
  /** Qué queda por devolver de una venta, restando devoluciones anteriores. */
  paraVenta: (saleId: string) => api<VentaDevolvible>(`/pos/returns/for-sale/${saleId}`),

  crear: (datos: DevolucionIn) =>
    api<Devolucion>("/pos/returns", { method: "POST", body: datos }),

  /**
   * El historial por páginas, con las cifras de TODAS. `inicio_de_hoy` es la
   * medianoche del aparato con su zona: «hoy» es el día de quien mira.
   */
  historial: (filtros: { page: number; size: number; inicio_de_hoy: string }) =>
    api<HistorialDeDevoluciones>("/pos/returns", { params: filtros }),

  obtener: (id: string) => api<Devolucion>(`/pos/returns/${id}`),
};

/**
 * La pasarela de verificación de pagos.
 *
 * Conectar cuentas pide `tenant.manage` (es la clave de API del negocio);
 * resolver discrepancias pide `pos.refund` (es el mismo criterio que devolver
 * dinero). Son dos trabajos distintos y por eso son dos permisos distintos.
 */
export const pasarela = {
  proveedores: () => api<ProveedorPasarela[]>("/tenant/pasarela/proveedores"),

  conexiones: () => api<ConexionPasarela[]>("/tenant/pasarela/conexiones"),

  conectar: (datos: {
    payment_method_id: string;
    proveedor: string;
    credenciales: Record<string, string>;
    ventana_minutos?: number;
    tolerancia?: string;
  }) => api<ConexionPasarela>("/tenant/pasarela/conexiones", { method: "PUT", body: datos }),

  probar: (id: string) =>
    api<{ ok: boolean; mensaje: string }>(`/tenant/pasarela/conexiones/${id}/probar`, {
      method: "POST",
    }),

  desconectar: (id: string) =>
    api<void>(`/tenant/pasarela/conexiones/${id}`, { method: "DELETE" }),

  /** Consulta a los bancos y resuelve lo que toque. Es idempotente. */
  procesar: () => api<ResumenProceso>("/tenant/pasarela/procesar", { method: "POST" }),

  /** Por páginas: antes eran las cien más recientes, y lo viejo quedaba debajo del corte. */
  bandeja: (page: number, size: number) =>
    api<Page<VerificacionPago>>("/tenant/pasarela/bandeja", { params: { page, size } }),

  resolver: (id: string, aceptado: boolean, nota: string) =>
    api<VerificacionPago>(`/tenant/pasarela/bandeja/${id}/resolver`, {
      method: "POST",
      body: { aceptado, nota },
    }),

  deLaVenta: (saleId: string) => api<VerificacionPago[]>(`/tenant/pasarela/venta/${saleId}`),
};

/**
 * El panel de administración de Softfi.
 *
 * ⚠️ Todo esto vive detrás de `platform_admins`: un usuario normal recibe 403.
 * El frontend NO decide quién entra — solo esconde el menú. Quien decide es el
 * backend, porque esconder un botón nunca ha protegido nada.
 */
export type AjustesDePlataforma = {
  avisos_de_registro: string[];
  registro_abierto: boolean;
  /** Cuántos días dura la prueba gratis. Lo lee también la página principal. */
  dias_de_prueba: number;
  /** Días de gracia tras vencer, antes de la pantalla de «Suscripción vencida». */
  dias_de_gracia: number;
  /** Lo que cuesta al mes cada sucursal adicional, en dólares. De fábrica, 15. */
  precio_sucursal?: string;
  verificacion: "correo" | "whatsapp" | "ambas";
  /** El WhatsApp del dueño de Softfi para los avisos técnicos. Vacío = solo correo. */
  whatsapp_de_avisos: string;
  /**
   * La versión que TODOS los aparatos tienen que tener (fecha de compilación).
   * `null` = nadie está obligado. Ver `features/admin/ActualizarTodos.tsx`.
   */
  version_minima?: string | null;
};

export const plataforma = {
  /**
   * Abre una sesión dentro del negocio de un cliente, para ver lo que él ve.
   *
   * ⚠️ No crea ninguna membresía: el permiso se comprueba contra
   * `platform_admins` en cada petición y muere con la sesión.
   */
  entrarAlNegocio: (tenant_id: string) =>
    api<Session>(`/plataforma/negocios/${tenant_id}/entrar`, { method: "POST" }),

  metricas: () => api<MetricasPlataforma>("/plataforma/metricas"),

  negocios: (filtros: { buscar?: string; estado?: string; limite?: number } = {}) =>
    api<NegocioFila[]>("/plataforma/negocios", { params: filtros }),

  /**
   * Una página de negocios. Las pantallas las piden TODAS (ver `todosLosNegocios`
   * en `admin/listasDelPanel.ts`): se ordenan por los días que les quedan, y
   * con un tope el que vence mañana podía quedar fuera.
   */
  negociosPorPaginas: (page: number, size: number) =>
    api<Page<NegocioFila>>("/plataforma/negocios", { params: { page, size } }),

/**
   * Aprobar, rechazar, suspender, reactivar y extender.
   *
   * ⚠️ El motivo del rechazo SE LE MANDA al cliente por correo. Un «no» sin
   * explicación no se puede corregir, y mucho de lo que se rechaza de entrada
   * es alguien que rellenó mal el formulario.
   */
  aprobar: (id: string) => api<{ ok: boolean }>(NEG(id) + "/aprobar", { method: "POST" }),

  rechazar: (id: string, motivo: string) =>
    api<{ ok: boolean }>(NEG(id) + "/rechazar", { method: "POST", body: { motivo } }),

  suspender: (id: string, motivo: string) =>
    api<{ ok: boolean }>(NEG(id) + "/suspender", { method: "POST", body: { motivo } }),

  reactivar: (id: string) => api<{ ok: boolean }>(NEG(id) + "/reactivar", { method: "POST" }),

  extender: (id: string, dias: number) =>
    api<{ ok: boolean; vence: string }>(NEG(id) + "/extender", {
      method: "POST",
      body: { dias },
    }),

  /**
   * Quita un negocio del panel.
   *
   * ⚠️ `definitivo` lo borra de la base de datos y no vuelve. El servidor solo
   * lo permite si ese negocio NUNCA vendió nada: con ventas dentro, lo que se
   * destruiría es la contabilidad de una persona.
   */
  borrarNegocio: (id: string, motivo: string, definitivo = false) =>
    api<{ ok: boolean; definitivo: boolean }>(NEG(id), {
      method: "DELETE",
      body: { motivo, definitivo },
    }),

  restaurarNegocio: (id: string) =>
    api<{ ok: boolean }>(NEG(id) + "/restaurar", { method: "POST" }),

  /**
   * Las sucursales que piden los negocios (2026-10-01). De fábrica, las que
   * esperan aprobación: una sucursal nueva no se usa hasta que se apruebe.
   */
  sucursales: (estado: "pendiente" | "aprobada" | "rechazada" = "pendiente") =>
    api<SucursalDelPanel[]>("/plataforma/sucursales", { params: { estado } }),

  /** Aprobarla la enciende y le avisa al dueño por correo. */
  aprobarSucursal: (id: string) =>
    api<SucursalDelPanel>(`/plataforma/sucursales/${id}/aprobar`, { method: "POST" }),

  /** El motivo lo ve el negocio en su pantalla de sucursales. */
  rechazarSucursal: (id: string, motivo: string) =>
    api<SucursalDelPanel>(`/plataforma/sucursales/${id}/rechazar`, {
      method: "POST",
      body: { motivo },
    }),

  /** Las sucursales de un negocio y lo que paga por ellas. */
  sucursalesDelNegocio: (id: string) => api<SucursalesDelNegocio>(NEG(id) + "/sucursales"),

  /** Su propio precio por sucursal adicional. `null` = vuelve al general. */
  precioDeSucursalDelNegocio: (id: string, precio: string | null) =>
    api<SucursalesDelNegocio>(NEG(id) + "/precio-sucursal", { method: "PATCH", body: { precio } }),

  ajustes: () =>
    api<AjustesDePlataforma>("/plataforma/ajustes"),

  guardarAjustes: (cambios: {
    /** Si no viene, la lista que haya se queda como está. */
    avisos_de_registro?: string[];
    /** La versión mínima de la aplicación. `""` = nadie está obligado. */
    version_minima?: string;
    /** Dónde llegan los avisos de que algo se cayó (la tasa del BCV). */
    whatsapp_de_avisos?: string;
    /**
     * Cuántos días dura la prueba gratis, de 1 a 365. Lo cambia todo a la vez:
     * registro, aprobación, correo de bienvenida y la página principal.
     */
    dias_de_prueba?: number;
    /**
     * Días de gracia tras vencer, de 0 a 30. Vale para todos al momento,
     * también para los que ya vencieron.
     */
    dias_de_gracia?: number;
    /** Lo que cuesta al mes cada sucursal adicional, en dólares (2026-10-01). */
    precio_sucursal?: string;
    /**
     * Si en la página principal sale «Empieza gratis N días».
     *
     * Cerrado deja SOLO el botón de WhatsApp: quien llega escribe y contesta
     * una persona. Se vende más y escala menos.
     */
    registro_abierto?: boolean;
    /**
     * Por dónde le llega el código a quien se registra.
     *
     * ⚠️ No es comodidad: es lo que frena el abuso de la prueba gratis. Un
     * correo es gratis e infinito; una línea de teléfono cuesta dinero.
     */
    verificacion?: "correo" | "whatsapp" | "ambas";
  }) =>
    api<AjustesDePlataforma>("/plataforma/ajustes", { method: "PATCH", body: cambios }),

  planes: () => api<PlanPlataforma[]>("/plataforma/planes"),

  guardarPlan: (plan: PlanPlataforma) =>
    api<PlanPlataforma>("/plataforma/planes", { method: "PUT", body: plan }),

  pagos: (limite = 100) => api<PagoPlataforma[]>("/plataforma/pagos", { params: { limite } }),

  /** Una página de pagos. Ver `todosLosPagos` en `admin/listasDelPanel.ts`. */
  pagosPorPaginas: (page: number, size: number) =>
    api<Page<PagoPlataforma>>("/plataforma/pagos", { params: { page, size } }),

  registrarPago: (datos: {
    tenant_id: string;
    plan_clave: string;
    monto: string;
    metodo?: string;
    pagado_el?: string;
    dias?: number;
    referencia?: string | null;
    nota?: string | null;
  }) => api<PagoPlataforma>("/plataforma/pagos", { method: "POST", body: datos }),

  anularPago: (id: string, motivo: string) =>
    api<PagoPlataforma>(`/plataforma/pagos/${id}/anular`, { method: "POST", body: { motivo } }),

  socios: () => api<SocioPlataforma[]>("/plataforma/socios"),

  crearSocio: (datos: Partial<SocioPlataforma>) =>
    api<SocioPlataforma>("/plataforma/socios", { method: "PUT", body: datos }),

  editarSocio: (id: string, datos: Partial<SocioPlataforma>) =>
    api<SocioPlataforma>(`/plataforma/socios/${id}`, { method: "PATCH", body: datos }),

  comisiones: (filtros: { socio_id?: string; estado?: string } = {}) =>
    api<ComisionPlataforma[]>("/plataforma/comisiones", { params: filtros }),

  /** Clientes que dicen que ya pagaron. Todavía no vale nada. */
  avisosDePago: (estado = "pendiente") =>
    api<AvisoDePago[]>("/plataforma/avisos-de-pago", { params: { estado } }),

  confirmarAviso: (
    id: string,
    datos: { plan_clave: string; monto?: string; dias?: number; nota?: string },
  ) =>
    api<PagoPlataforma>(`/plataforma/avisos-de-pago/${id}/confirmar`, {
      method: "POST",
      body: datos,
    }),

  rechazarAviso: (id: string, motivo: string) =>
    api<AvisoDePago>(`/plataforma/avisos-de-pago/${id}/rechazar`, {
      method: "POST",
      body: { motivo },
    }),

  /** Le monta la tienda a un cliente estando delante de él. */
  crearNegocioDeCliente: (datos: {
    nombre_negocio: string;
    business_type?: string;
    primary_currency_code?: string;
    country_code?: string;
    nombre_dueno: string;
    email: string;
    telefono?: string | null;
    password: string;
    socio_codigo?: string | null;
    /** Vacío = los días de prueba del panel. */
    dias_de_prueba?: number | null;
    /**
     * Si te pagó en la reunión, va aquí y el negocio nace pagado.
     *
     * `monto` y `dias` son independientes a propósito: una promoción de dos
     * meses por uno son 60 días con el monto de 30. Y cuando esto viene,
     * `dias_de_prueba` se ignora — quien pagó no está de prueba.
     */
    cobro?: {
      plan_clave: string;
      monto: string;
      dias: number;
      metodo?: string;
      referencia?: string | null;
      nota?: string | null;
    } | null;
  }) => api<NegocioCreado>("/plataforma/negocios", { method: "POST", body: datos }),

  pagarComisiones: (ids: string[], referencia: string) =>
    api<{ pagadas: number }>("/plataforma/comisiones/pagar", {
      method: "POST",
      body: { ids, referencia },
    }),
};

/** Lo que ve cualquiera, sin sesión: la página pública. */
export const publico = {
  planes: () => api<PlanPlataforma[]>("/publico/planes", { publico: true }),

  solicitarSerSocio: (datos: {
    nombre: string;
    email: string;
    telefono: string;
    documento?: string | null;
    mensaje?: string | null;
  }) =>
    api<{ message: string }>("/publico/socios/solicitud", {
      method: "POST",
      body: datos,
      publico: true,
    }),
};

export const proveedores = {
  listar: () => api<Supplier[]>("/tenant/suppliers"),

  crear: (datos: {
    name: string;
    phone?: string | null;
    contact_name?: string | null;
    email?: string | null;
    tax_id?: string | null;
    address?: string | null;
    notes?: string | null;
  }) => api<Supplier>("/tenant/suppliers", { method: "POST", body: datos }),

  actualizar: (id: string, cambios: Record<string, unknown>) =>
    api<Supplier>(`/tenant/suppliers/${id}`, { method: "PATCH", body: cambios }),
};

// =============================================================================
//  INVENTARIO
// =============================================================================

export const inventario = {
  /**
   * Las existencias del local en el que estás.
   *
   * ⚠️ `location_id` NO es opcional de verdad, aunque lo parezca: sin él el
   * servidor devuelve las del local principal. Eso hacía que quien abría una
   * sucursal viera siempre el inventario de la sede — con números que ahí no
   * existían, y sin ninguna pista de que estaba mirando otro sitio.
   *
   * Todas las llamadas de este bloque lo mandan. Ver `stores/sucursal.ts`.
   */
  stock: (
    filtros: {
      q?: string;
      /** Solo las tallas de un producto: lo que necesita el punto de venta. */
      item_id?: string;
      solo_alertas?: boolean;
      location_id?: string | null;
      /** Con el bulto y las cajas de venta de cada producto (Traslados). */
      con_medidas?: boolean;
      /** Solo los productos de esta categoría. Para contar por estantes. */
      category_id?: string;
      /** Solo los de esta marca, o los que no tienen (2026-10-18). Ver `filtrosDeProductos`. */
      marca?: string;
      sin_marca?: boolean;
      /**
       * Solo los del proveedor habitual indicado.
       *
       * Es la lista con la que se le llama: «de lo tuyo, ¿qué me falta?».
       */
      supplier_id?: string;
      /**
       * Filtro por estado: `disponible`, `bajo`, `agotado`, `reponer` (bajo +
       * agotado) o `desactivado`. Sin él, los activos de siempre.
       */
      estado?: "disponible" | "bajo" | "agotado" | "reponer" | "desactivado";
      /**
       * Cómo se ordena (2026-10-07). Sin él, por nombre. `valor_desc` solo
       * ordena con permiso de ver costos.
       */
      orden?: OrdenDelInventario;
      /**
       * Qué página. Sin esto se devuelve la primera.
       *
       * ⚠️ El inventario viene PAGINADO desde que se descubrió que un negocio
       * con 782 agotados solo veía treinta: el recorte pasaba antes del filtro,
       * así que el resto no se podía alcanzar de ninguna forma.
       */
      page?: number;
      size?: number;
    } = {},
  ) => api<Page<StockRow>>("/tenant/inventory/stock", { params: filtros }),

  resumen: () => api<StockSummary>("/tenant/inventory/summary"),

  /**
   * El libro mayor, **paginado** y con filtros.
   *
   * ⚠️ Es la tabla que más crece de todo Softfi —una fila por cada línea de
   * cada venta— y antes traía «los 200 últimos» sin paginador. En un negocio
   * con movimiento eso son dos días de historia.
   */
  movimientos: (
    filtros: {
      item_id?: string;
      variant_id?: string;
      location_id?: string;
      /** `salidas` = todo lo que restó, ventas incluidas. `entradas` = lo que sumó. */
      direccion?: "entradas" | "salidas";
      /** Un tipo concreto: `sale`, `waste`, `adjustment`… */
      tipo?: string;
      /** Busca por producto, SKU o el motivo escrito a mano. */
      q?: string;
      desde?: string;
      hasta?: string;
      page?: number;
      size?: number;
    } = {},
  ) =>
    api<Page<Movement>>("/tenant/inventory/movements", { params: filtros }),

  /** Ajuste tras un conteo físico: se envía la cantidad REAL contada. */
  ajustar: (datos: {
    item_id: string;
    variant_id?: string | null;
    new_qty: string;
    reason: string;
    location_id?: string | null;
  }) => api<StockRow | null>("/tenant/inventory/adjust", { method: "POST", body: datos }),

  /** Merma: se rompió, se venció o se perdió. */
  merma: (datos: {
    item_id: string;
    variant_id?: string | null;
    qty: string;
    reason: string;
    location_id?: string | null;
  }) => api<StockRow | null>("/tenant/inventory/waste", { method: "POST", body: datos }),

  /**
   * «Llegaron 20 más». SUMA lo que llegó, no pone un total.
   *
   * ⚠️ No confundir con `ajustar`. Ajustar pide el total contado, y calcularlo
   * obliga a restar contra el número de la pantalla, que puede estar viejo: si
   * mientras tanto se vendieron tres, mandar el total BORRA esas tres ventas.
   */
  cargar: (datos: {
    item_id: string;
    variant_id?: string | null;
    qty: string;
    reason?: string | null;
    location_id?: string | null;
  }) => api<StockRow | null>("/tenant/inventory/cargar", { method: "POST", body: datos }),

  /**
   * Salió por algo que no es una venta: uso interno, un regalo, un robo.
   *
   * Aparte de la merma a propósito: merma es lo que se DAÑÓ, y mezclarlas
   * infla el número con el que se decide dejar de comprarle a un proveedor.
   */
  salida: (datos: {
    item_id: string;
    variant_id?: string | null;
    qty: string;
    reason: string;
    location_id?: string | null;
  }) => api<StockRow | null>("/tenant/inventory/salida", { method: "POST", body: datos }),

  /**
   * Traslados entre sucursales (2026-09-29).
   *
   * Preparar no mueve nada. La sucursal que envía confirma (sale de su
   * inventario) y la que recibe confirma lo que contó (entra al suyo). Si
   * llega menos, el origen decide qué pasó con lo que falta.
   */
  traslados: {
    listar: (vista: "pendientes" | "historial", page = 1, size = 20) =>
      api<Page<TrasladoFila>>("/tenant/inventory/traslados", { params: { vista, page, size } }),
    conteo: () => api<ConteoDeTraslados>("/tenant/inventory/traslados/pendientes"),
    /**
     * TODAS las sucursales del negocio, también aquellas a las que no se entra
     * (2026-10-07): el destino de un traslado puede ser cualquiera. Con
     * `puede_entrar`, para saber de cuáles puede salir.
     */
    sucursales: () =>
      api<
        {
          id: string;
          name: string;
          tipo: "sucursal" | "almacen";
          is_default: boolean;
          is_active: boolean;
          puede_entrar: boolean;
        }[]
      >("/tenant/inventory/traslados/sucursales"),
    obtener: (id: string) => api<Traslado>(`/tenant/inventory/traslados/${id}`),
    crear: (datos: {
      origen_id: string;
      destino_id: string;
      observaciones?: string | null;
      empaques?: Empaque[] | null;
      /** `cantidad` en unidades; `medida` solo dice en qué se contó. */
      lineas: {
        item_id: string;
        variant_id?: string | null;
        cantidad: string;
        medida?: string | null;
        medida_unidades?: string | null;
      }[];
    }) => api<Traslado>("/tenant/inventory/traslados", { method: "POST", body: datos }),
    enviar: (
      id: string,
      datos: {
        cantidades: { linea_id: string; cantidad: string }[];
        observaciones?: string | null;
        /** Solo si se cambió al revisar; vacía = ya no se dice. */
        empaques?: Empaque[];
      },
    ) => api<Traslado>(`/tenant/inventory/traslados/${id}/enviar`, { method: "POST", body: datos }),
    recibir: (
      id: string,
      datos: {
        recibidas: { linea_id: string; cantidad: string }[];
        observaciones?: string | null;
        empaques_recibidos?: Empaque[] | null;
      },
    ) => api<Traslado>(`/tenant/inventory/traslados/${id}/recibir`, { method: "POST", body: datos }),
    resolver: (id: string, lineas: { linea_id: string; resolucion: "devuelto" | "perdido" }[]) =>
      api<Traslado>(`/tenant/inventory/traslados/${id}/resolver`, {
        method: "POST",
        body: { lineas },
      }),
    anular: (id: string, motivo: string) =>
      api<Traslado>(`/tenant/inventory/traslados/${id}/anular`, {
        method: "POST",
        body: { motivo },
      }),
  },

  /**
   * Las ubicaciones del almacén del selector (2026-10-07): pasillos, estantes
   * y niveles. Ver `docs/15-UBICACIONES.md`.
   */
  ubicaciones: {
    estado: () => api<import("./types").EstadoDeUbicaciones>("/tenant/inventory/ubicaciones/estado"),
    encender: (encendidas: boolean) =>
      api<import("./types").EstadoDeUbicaciones>("/tenant/inventory/ubicaciones/encender", {
        method: "POST",
        body: { encendidas },
      }),
    listar: (
      filtros: {
        texto?: string;
        tipo?: string;
        vista?: "ocupadas" | "vacias" | "inactivas";
        page?: number;
        size?: number;
      } = {},
    ) =>
      api<Page<import("./types").Ubicacion>>("/tenant/inventory/ubicaciones", {
        params: { ...filtros, texto: filtros.texto || undefined, tipo: filtros.tipo || undefined },
      }),
    crearLote: (lote: import("./ubicaciones").Lote) =>
      api<{ creadas: number; ya_existian: number; primera: string | null; ultima: string | null }>(
        "/tenant/inventory/ubicaciones/lote",
        { method: "POST", body: lote },
      ),
    crear: (datos: { codigo: string; tipo: string }) =>
      api<import("./types").Ubicacion>("/tenant/inventory/ubicaciones", { method: "POST", body: datos }),
    editar: (id: string, datos: { codigo?: string; tipo?: string; activa?: boolean }) =>
      api<import("./types").Ubicacion>(`/tenant/inventory/ubicaciones/${id}`, { method: "PATCH", body: datos }),
    eliminar: (id: string) => api<void>(`/tenant/inventory/ubicaciones/${id}`, { method: "DELETE" }),
    detalle: (id: string) =>
      api<import("./types").DetalleDeUbicacion>(`/tenant/inventory/ubicaciones/${id}`),
    porUbicar: (filtros: { texto?: string; page?: number; size?: number } = {}) =>
      api<Page<import("./types").ProductoEnUbicaciones>>("/tenant/inventory/ubicaciones/por-ubicar", {
        params: { ...filtros, texto: filtros.texto || undefined },
      }),
    buscar: (texto: string) =>
      api<import("./types").BusquedaEnUbicaciones>("/tenant/inventory/ubicaciones/buscar", {
        params: { texto },
      }),
    producto: (variantId: string) =>
      api<import("./types").ProductoEnUbicaciones>(`/tenant/inventory/ubicaciones/producto/${variantId}`),
    ubicar: (datos: { variant_id: string; ubicacion_id: string; cantidad: string; habitual?: boolean }) =>
      api<import("./types").ProductoEnUbicaciones>("/tenant/inventory/ubicaciones/ubicar", {
        method: "POST",
        body: datos,
      }),
    ubicarEnSuLugar: () =>
      api<{ productos: number; unidades: string }>("/tenant/inventory/ubicaciones/ubicar-en-su-lugar", {
        method: "POST",
      }),
    mover: (datos: {
      variant_id: string;
      desde_id: string;
      hacia_id: string;
      cantidad: string;
      habitual?: boolean;
    }) =>
      api<import("./types").ProductoEnUbicaciones>("/tenant/inventory/ubicaciones/mover", {
        method: "POST",
        body: datos,
      }),
    habitual: (
      variantId: string,
      ubicacionId: string | null,
      minimo?: string | null,
      maximo?: string | null,
    ) =>
      api<import("./types").ProductoEnUbicaciones>("/tenant/inventory/ubicaciones/habitual", {
        method: "POST",
        body: { variant_id: variantId, ubicacion_id: ubicacionId, minimo: minimo || null, maximo: maximo || null },
      }),
    // ── Fase 2 (2026-10-08) ──
    paraContar: (filtro: { texto?: string; ubicacionId?: string }) =>
      api<import("./types").ParaContar>("/tenant/inventory/ubicaciones/para-contar", {
        params: { texto: filtro.texto || undefined, ubicacion_id: filtro.ubicacionId || undefined },
      }),
    registrarConteo: (datos: {
      ubicaciones: string[];
      lineas: { ubicacion_id: string; variant_id: string; contado: string }[];
      dar_por_perdido: boolean;
      alcance?: string;
    }) =>
      api<import("./types").ResultadoDeConteo>("/tenant/inventory/ubicaciones/conteo", {
        method: "POST",
        body: datos,
      }),
    paraReponer: () => api<import("./types").ParaReponer[]>("/tenant/inventory/ubicaciones/para-reponer"),
    // ── El plano del almacén (2026-10-11) ──
    plano: () => api<import("./types").PlanoDelAlmacen>("/tenant/inventory/ubicaciones/plano"),
    /** El plano ENTERO, de una vez, con sus medidas (obligatorias). La primera vez lo crea. */
    guardarPlano: (
      elementos: Parameters<typeof mesas.guardarMapa>[1],
      plano: Parameters<typeof mesas.guardarMapa>[2],
    ) =>
      api<import("./types").PlanoDelAlmacen>("/tenant/inventory/ubicaciones/plano", {
        method: "PUT",
        body: { elementos, plano },
      }),
    /** Borra el dibujo. Las ubicaciones no se tocan. */
    borrarPlano: () => api<void>("/tenant/inventory/ubicaciones/plano", { method: "DELETE" }),
    subirFondoDelPlano: (archivo: File) =>
      api<import("./types").PlanoDelAlmacen>("/tenant/inventory/ubicaciones/plano/fondo", {
        method: "POST",
        body: conArchivo(archivo),
      }),
    quitarFondoDelPlano: () =>
      api<import("./types").PlanoDelAlmacen>("/tenant/inventory/ubicaciones/plano/fondo", { method: "DELETE" }),
    planDeRecogida: (trasladoId: string) =>
      api<import("./types").RecogidaDeTraslado>(`/tenant/inventory/traslados/${trasladoId}/recoger`),
    guardarRecogida: (
      trasladoId: string,
      lineas: { linea_id: string; origenes: { ubicacion_id: string | null; cantidad: string }[] }[],
    ) =>
      api<import("./types").RecogidaDeTraslado>(`/tenant/inventory/traslados/${trasladoId}/recoger`, {
        method: "POST",
        body: { lineas },
      }),
  },
};

// =============================================================================
//  ONBOARDING (plantillas por rubro)
// =============================================================================

export const onboarding = {
  /** Público: la página de registro las muestra antes de que exista la cuenta. */
  plantillas: () => api<BusinessTemplate[]>("/onboarding/templates", { publico: true }),

  plantilla: (code: string) =>
    api<BusinessTemplate>(`/onboarding/templates/${code}`, { publico: true }),

  estado: () => api<OnboardingStatus>("/tenant/onboarding/status"),

  instalar: (datos: {
    code?: string;
    with_categories?: boolean;
    with_taxes?: boolean;
    with_items?: boolean;
    with_settings?: boolean;
  }) => api<InstallResult>("/tenant/onboarding/install", { method: "POST", body: datos }),
};

// =============================================================================
//  CAJA DIARIA
// =============================================================================

export const caja = {
  /**
   * Los egresos de un período: gastos (de la caja o no), retiros, sueldos y
   * pagos a proveedores. Lo usan Gastos, Inicio y Reportes con la clave
   * `["gastos", desde, hasta]`, así que registrar un gasto los pone al día a
   * los tres.
   */
  gastos: (rango: { desde: string; hasta: string }) =>
    api<import("./types").EgresosDelRango>("/tenant/cash/expenses", { params: rango }),

  /**
   * Corregir un gasto: lo que dice, la categoría o el monto (2026-10-05). La
   * moneda y el método no: para eso se anula y se registra de nuevo.
   */
  editarGasto: (
    id: string,
    cambios: {
      description?: string;
      amount?: string;
      categoria_id?: string | null;
      categoria_nueva?: string;
    },
  ) => api<void>(`/tenant/cash/expenses/${id}`, { method: "PATCH", body: cambios }),

  /**
   * No se borra: queda en Gastos, tachado, y deja de contar en todo. Sirve
   * también para un retiro o un ingreso de efectivo hecho a mano (2026-10-22).
   */
  anularGasto: (id: string, motivo: string) =>
    api<void>(`/tenant/cash/expenses/${id}/anular`, { method: "POST", body: { motivo } }),

  /** Turno abierto con el arqueo calculado al momento. `null` si no hay caja abierta. */
  actual: () => api<CashSession | null>("/tenant/cash/current"),

  abrir: (datos: {
    register_id?: string | null;
    opening_amounts: { currency_code: string; amount: string }[];
    note?: string | null;
  }) => api<CashSession>("/tenant/cash/open", { method: "POST", body: datos }),

  /**
   * Las categorías de gasto del negocio.
   *
   * ⚠️ Cuelgan de `/tenant/gastos/...` y no de `/tenant/cash/...` aunque por
   * dentro vivan en el módulo de caja: la pantalla donde se usan es Gastos, y
   * una URL que dice «cash» es una pista falsa el día que alguien busque de
   * dónde sale este dato.
   */
  categoriasDeGasto: (incluirApagadas = false) =>
    api<import("./types").CategoriaDeGasto[]>("/tenant/gastos/categorias", {
      params: { incluir_apagadas: String(incluirApagadas) },
    }),

  crearCategoriaDeGasto: (nombre: string) =>
    api<import("./types").CategoriaDeGasto>("/tenant/gastos/categorias", {
      method: "POST",
      body: { nombre },
    }),

  editarCategoriaDeGasto: (id: string, datos: { nombre?: string; is_active?: boolean }) =>
    api<import("./types").CategoriaDeGasto>(`/tenant/gastos/categorias/${id}`, {
      method: "PATCH",
      body: datos,
    }),

  borrarCategoriaDeGasto: (id: string) =>
    api<void>(`/tenant/gastos/categorias/${id}`, { method: "DELETE" }),

  movimiento: (datos: {
    direction: CashDirection;
    category: CashCategory;
    currency_code: string;
    amount: string;
    description: string;
    /** En qué se gastó. Solo cuenta en un gasto, no en un retiro. */
    categoria_id?: string;
    /**
     * Una categoría escrita al vuelo, sin salir del formulario.
     *
     * ⚠️ «Por si a alguien le da pereza crear una categoría». Salir a otra
     * pantalla, crearla y volver es el paso donde la gente deja de registrar los
     * gastos. Si ya existe con ese nombre, se usa la que hay.
     */
    categoria_nueva?: string;
    /**
     * De qué método salió el dinero (2026-09-30). Vacío = el efectivo del
     * cajón. Uno que no es efectivo no toca el arqueo de la caja.
     */
    payment_method_id?: string;
    /**
     * Cuando el efectivo del cajón no alcanza para un GASTO (2026-10-12): de
     * dónde salió la diferencia. «metodo» la registra con
     * `excedente_metodo_id`; «aporte», como dinero que el dueño puso en la
     * caja. Ver `features/caja/ElRestoDelGasto.tsx`.
     */
    excedente?: "aporte" | "metodo";
    excedente_metodo_id?: string;
    /**
     * De dónde sale el efectivo (2026-10-13): una caja abierta (el id de su
     * turno) o el resguardo. Ver `features/caja/OrigenDelEfectivo.tsx`.
     */
    caja_id?: string;
    resguardo?: boolean;
  }) => api<CashSession>("/tenant/cash/movements", { method: "POST", body: datos }),

  /** Las cajas de la sucursal, para elegir cuál abrir si hay varias (2026-10-30). */
  paraAbrir: () => api<import("./types").CajaParaAbrir[]>("/tenant/cash/para-abrir"),

  /** Las cajas abiertas de la sucursal y el resguardo, con lo disponible (2026-10-13). */
  origenes: () => api<import("./types").OrigenesDelEfectivo>("/tenant/cash/origenes"),

  /**
   * Pasar efectivo entre una caja abierta y el resguardo (2026-10-13). El
   * arqueo de la caja cambia; el efectivo del negocio, no.
   */
  traslado: (datos: {
    caja_id: string;
    hacia: "resguardo" | "caja";
    currency_code: string;
    amount: string;
    description?: string;
  }) => api<CashSession>("/tenant/cash/traslados", { method: "POST", body: datos }),

  /** El conteo del resguardo: la diferencia con lo esperado queda registrada. */
  contarResguardo: (datos: {
    contados: { currency_code: string; counted: string }[];
    nota?: string | null;
  }) =>
    api<{ diferencias: number }>("/tenant/cash/resguardo/conteo", { method: "POST", body: datos }),

  /**
   * Un gasto en partes (2026-10-12): el mismo gasto pagado en varias monedas o
   * métodos. Cada parte en efectivo se compara con lo que hay en el cajón en su
   * moneda; `aporte` = el propietario cubrió lo que no alcanzaba.
   */
  gastoEnPartes: (datos: {
    description: string;
    categoria_id?: string;
    categoria_nueva?: string;
    partes: { currency_code: string; amount: string; payment_method_id: string | null }[];
    aporte?: boolean;
    /**
     * De dónde sale el efectivo (2026-10-13): una caja abierta (el id de su
     * turno) o el resguardo. Ver `features/caja/OrigenDelEfectivo.tsx`.
     */
    caja_id?: string;
    resguardo?: boolean;
  }) =>
    api<{ grupo_id: string | null; movimientos: number }>("/tenant/cash/expenses", {
      method: "POST",
      body: datos,
    }),

  cerrar: (datos: {
    counted_amounts: { currency_code: string; counted: string }[];
    note?: string | null;
  }) => api<CashSession>("/tenant/cash/close", { method: "POST", body: datos }),

  /**
   * El historial por páginas, con búsqueda en el servidor y las cifras de
   * TODO lo filtrado. Antes llegaban los 50 últimos y ya: el 51 no se veía.
   */
  historial: (filtros: { page: number; size: number; q?: string }) =>
    api<HistorialDeTurnos>("/tenant/cash/sessions", { params: filtros }),

  turno: (id: string) => api<CashSession>(`/tenant/cash/sessions/${id}`),
};

/** Una página del historial de devoluciones y las cifras de todas. */
export type HistorialDeDevoluciones = Page<DevolucionRow> & {
  resumen: {
    /** El dinero que salió del cajón hoy (`entregado`, no `total`). */
    hoy_entregado: string;
    hoy_cantidad: number;
    hoy_cambios: number;
    /** El dinero que salió del cajón en todo el historial. */
    entregado: string;
    registradas: number;
  };
};

/** Una página del historial de caja y las cifras de todo lo filtrado. */
export type HistorialDeTurnos = Page<CashSessionRow> & {
  resumen: {
    turnos: number;
    facturado: string;
    abiertos: number;
    /** `null` con cierre ciego: un cero se leería como «todo cuadró». */
    descuadrados: number | null;
  };
};

/** Un producto que todavía NO existe y nace con esta entrada. */
export type ProductoNuevoEnLaEntrada = {
  nombre: string;
  /** En qué se mide: `unit`, `kg`, `l`… Un saco de harina se mide en kilos. */
  unit?: string;
  /**
   * Si NO se vende suelto en el mostrador.
   *
   * ⚠️ La harina está en el inventario porque se gasta, no porque se venda.
   * Sin esto aparecería en la cuadrícula de cobrar junto a las arepas, y un
   * cajero con prisa toca «Harina» creyendo que es la arepa.
   */
  es_ingrediente?: boolean;
  base_price?: string;
  /** Los códigos con que venía en la factura: sin ellos, la próxima no lo encuentra. */
  codigo_barras?: string | null;
  sku?: string | null;
};

export type LineaDeEntrada = {
  /** El producto que ya existe. Vacío si viene `nuevo`. */
  item_id?: string;
  variant_id?: string | null;
  /**
   * Crear el producto en esta misma entrada.
   *
   * ⚠️ Uno de los dos —`item_id` o `nuevo`— tiene que venir. Antes había que
   * ir al Catálogo, crear la harina, volver e importar la entrada: dos
   * pantallas para una sola cosa que pasó, que llegó un saco.
   */
  nuevo?: ProductoNuevoEnLaEntrada;
  qty: string;
  /**
   * ⚠️ Vacío NO es cero: vacío se queda con el costo de hoy; un 0 escrito es
   * mercancía regalada y baja el promedio. Ver `PurchaseItemIn.unit_cost`.
   */
  unit_cost: string | null;
  en_bultos?: boolean;
  /**
   * Los precios de venta que cambian con esta entrada: el normal y los de
   * cada lista. Solo lo que cambió; `null` en una lista la devuelve al precio
   * normal. Pide `catalog.manage`. Ver `PreciosDeVentaIn` en el servidor.
   */
  precios_de_venta?: { precio?: string; listas?: Record<string, string | null> };
};

export type EntradaIn = {
  items: LineaDeEntrada[];
  supplier_id?: string | null;
  currency_code?: string | null;
  exchange_rate?: string | null;
  invoice_number?: string | null;
  notes?: string | null;

  /**
   * La mercancía entró pero todavía no se le pagó al proveedor.
   *
   * ⚠️ Con esto el servidor crea la cuenta por pagar ENLAZADA a la entrada.
   * Antes había que ir a «Por pagar» y escribir la misma factura otra vez — y
   * lo que hay que anotar dos veces no se anota la segunda.
   */
  queda_a_deber?: boolean;
  /** Para cuándo hay que pagarla. Solo se usa con `queda_a_deber`. */
  vence_el?: string | null;
  /** El borrador del que sale: el servidor lo borra al registrarla. */
  borrador_id?: string | null;
  /** En un almacén, con «Pendiente de pago»: el id de la sucursal que paga. */
  a_cargo_de?: string | null;
  /**
   * El día en que llega la mercancía (2026-10-03). Con él la entrada queda
   * «por recibir» y no suma existencia hasta confirmar su recepción.
   */
  llega_el?: string | null;
  /**
   * Los impuestos encendidos en la entrada (2026-10-20): el IVA de la factura.
   * Solo los que alcanzan al total, sin monedas. Ver `_impuestos_de_la_entrada`.
   */
  order_tax_ids?: string[];
};

/** Una entrada a medio cargar. Ver `BorradorDeEntrada` en el servidor. */
export type BorradorDeEntrada = {
  id: string;
  location_id: string;
  proveedor_nombre: string | null;
  factura: string | null;
  lineas: number;
  creado_por_nombre: string | null;
  created_at: string;
  updated_at: string;
  /** Lo que había en la ventana. Solo llega al abrir uno. */
  datos: Record<string, unknown> | null;
};

export type BorradorIn = {
  datos: Record<string, unknown>;
  proveedor_nombre?: string | null;
  factura?: string | null;
  lineas: number;
};

export const entradas = {
  /**
   * Las entradas de mercancía, **paginadas** y con filtros.
   *
   * El buscador mira el número de la entrada, el de la factura del proveedor y
   * el nombre del proveedor — las tres cosas que alguien tiene delante en un
   * papel cuando viene a buscar una compra vieja.
   */
  listar: (
    filtros: {
      q?: string;
      supplier_id?: string;
      desde?: string;
      hasta?: string;
      page?: number;
      size?: number;
    } = {},
  ) => api<Page<PurchaseRow>>("/tenant/purchases", { params: filtros }),

  /**
   * Los dos números del encabezado, **sobre lo filtrado**.
   *
   * Sumar las filas de la pantalla dejó de servir en cuanto hubo páginas.
   */
  resumen: (
    filtros: { q?: string; supplier_id?: string; desde?: string; hasta?: string } = {},
  ) =>
    api<{
      cuantas: number;
      comprado: string;
      debiendo: string;
      con_deuda: number;
      /** Cuántas todavía no llegan (2026-10-03). */
      por_recibir?: number;
    }>(
      "/tenant/purchases/resumen",
      { params: filtros },
    ),

  obtener: (id: string) => api<Purchase>(`/tenant/purchases/${id}`),

  crear: (datos: EntradaIn) => api<Purchase>("/tenant/purchases", { method: "POST", body: datos }),

  /**
   * Corrige una entrada ya registrada: se manda completa, como al registrarla.
   * La existencia se ajusta por la diferencia, el costo promedio se recalcula
   * y la deuda en «Por pagar» se pone al día (2026-09-30).
   */
  corregir: (id: string, datos: EntradaIn) =>
    api<Purchase>(`/tenant/purchases/${id}`, { method: "PUT", body: datos }),

  /**
   * Anula una entrada (2026-10-02): sale lo que metió, el costo vuelve a como
   * estaba y su deuda en «Por pagar» se anula. Queda en el historial, marcada.
   * No se puede si su deuda tiene pagos o si parte de la mercancía ya salió.
   */
  anular: (id: string, motivo: string) =>
    api<Purchase>(`/tenant/purchases/${id}/anular`, { method: "POST", body: { motivo } }),

  /**
   * Confirma que llegó una entrada por recibir (2026-10-03): entra al
   * inventario y se recalcula el costo. Se manda completa, como al corregirla,
   * por si llegó distinto: entra lo que se mande.
   */
  recibir: (id: string, datos: EntradaIn) =>
    api<Purchase>(`/tenant/purchases/${id}/recibir`, { method: "POST", body: datos }),

  /** Las entradas a medio cargar, para terminarlas después (2026-09-30). */
  borradores: {
    listar: () => api<BorradorDeEntrada[]>("/tenant/purchases/borradores"),
    obtener: (id: string) => api<BorradorDeEntrada>(`/tenant/purchases/borradores/${id}`),
    crear: (datos: BorradorIn) =>
      api<BorradorDeEntrada>("/tenant/purchases/borradores", { method: "POST", body: datos }),
    actualizar: (id: string, datos: BorradorIn) =>
      api<BorradorDeEntrada>(`/tenant/purchases/borradores/${id}`, { method: "PUT", body: datos }),
    eliminar: (id: string) =>
      api<void>(`/tenant/purchases/borradores/${id}`, { method: "DELETE" }),
  },
};

// =============================================================================
//  REPORTES
// =============================================================================

/**
 * Todos aceptan un rango de dos formas:
 *   - un atajo:  `{ periodo: "semana" }`
 *   - a mano:    `{ desde: "2026-08-01", hasta: "2026-08-05" }`
 *
 * ⚠️ `ganancia` y `equipo` exigen permisos que un empleado NO tiene. Las
 * pantallas que los usan tienen que comprobar `puede(...)` antes de pedirlos,
 * o el usuario verá un error 403 sin entender por qué.
 */
export type RangoReporte = {
  periodo?: import("./types").AtajoPeriodo;
  desde?: string;
  hasta?: string;
};

function comoQuery(rango: RangoReporte): string {
  const p = new URLSearchParams();
  if (rango.desde) p.set("desde", rango.desde);
  if (rango.hasta) p.set("hasta", rango.hasta);
  // El atajo solo se manda si no hay fechas: mandar los dos confunde la lectura
  // de la URL cuando hay que depurar algo.
  else if (rango.periodo) p.set("periodo", rango.periodo);
  const texto = p.toString();
  return texto ? `?${texto}` : "";
}

/**
 * Las llamadas de «Todas las sucursales», con su dirección base.
 *
 * ⚠️ Las mismas para «Todos mis negocios» (2026-10-03): el servidor responde
 * con la misma forma en `/tenant/reports/grupo/…`, con un negocio donde aquí
 * va una sucursal (ver `reports/todos_los_negocios.py`). Así las pantallas son
 * las mismas. Allí no existen `resumen` (tiene el suyo), `ahora`, `faltan` ni
 * `guardarMetas`: la pantalla no las pide.
 */
function todasDe(base: string) {
  return {
    /**
     * El calendario de todas (2026-10-07): el de Administrar, con cada cosa
     * con el nombre de su sucursal y «Lo atrasado» de toda la empresa.
     */
    calendario: (rango: { desde: string; hasta: string }) =>
      api<import("@/features/calendario/calendario").DatosDelCalendario>(`${base}/calendario`, {
        params: rango,
      }),
    resumen: (rango: { desde: string; hasta: string }) =>
      api<import("./types").TodasResumen>(`${base}/resumen`, {
        params: rango,
      }),
    dinero: (rango: { desde: string; hasta: string }) =>
      api<import("./types").TodasDinero>(`${base}/dinero`, {
        params: rango,
      }),
    inventario: () =>
      api<import("./types").TodasInventario>(`${base}/inventario`),
    // ── Las pestañas de detalle (2026-10-02) ──────────────────────────────
    /** Productos, formas de pago, horarios y equipo; de todas o de una. */
    ventas: (rango: { desde: string; hasta: string }, location_id?: string | null) =>
      api<import("./types").TodasVentas>(`${base}/ventas`, {
        params: { ...rango, ...(location_id ? { location_id } : {}) },
      }),
    /** Cada categoría de gasto en cada sucursal. */
    gastos: (rango: { desde: string; hasta: string }) =>
      api<import("./types").TodasGastos>(`${base}/gastos`, {
        params: rango,
      }),
    // ── Segunda tanda (2026-10-02) ─────────────────────────────────────────
    /** Lo de hoy en cada sucursal y lo que requiere atención. */
    ahora: () => api<import("./types").TodasAhora>(`${base}/ahora`),
    /** Quién compra, quién vuelve, quién compra en varias y quién dejó de venir. */
    clientes: (rango: { desde: string; hasta: string }) =>
      api<import("./types").TodasClientes>(`${base}/clientes`, {
        params: rango,
      }),
    /** La mercancía que no se mueve, y su dinero. */
    parados: () =>
      api<import("./types").TodasParados>(`${base}/inventario/parados`),
    /** El inventario de cada categoría en cada local. */
    inventarioPorCategoria: () =>
      api<import("./types").TodasInventarioPorCategoria>(
        `${base}/inventario/categorias`,
      ),
    /** Los cierres de caja y sus descuadres. */
    cajas: (rango: { desde: string; hasta: string }) =>
      api<import("./types").TodasCajas>(`${base}/cajas`, {
        params: rango,
      }),
    /** Cada producto con lo que hay en cada local, por páginas. */
    existencias: (filtros: {
      q?: string;
      solo?: "agotados" | "por_acabarse" | "negativos";
      location_id?: string;
      /** Categoría, marca y proveedor (2026-10-18). Solo en «Todas las sucursales». */
      categoria_id?: string;
      marca?: string;
      sin_marca?: boolean;
      proveedor_id?: string;
      page?: number;
      size?: number;
    }) =>
      api<import("./types").TodasExistencias>(`${base}/existencias`, {
        params: filtros,
      }),
    /**
     * Consultor de productos (2026-10-07): lo escrito o escaneado, en todos
     * los locales. Solo existe en «Todas las sucursales».
     */
    consultorBuscar: (texto: string) =>
      api<import("./types").BusquedaDelConsultor>(`${base}/consultor`, { params: { texto } }),
    /** Todo el producto en cada sucursal y almacén; lo vendido, en el período. */
    consultorFicha: (vinculo: string, rango: { desde: string; hasta: string }) =>
      api<import("./types").FichaDelConsultor>(`${base}/consultor/${vinculo}`, { params: rango }),
    /** Los traslados que esperan a alguien, en todas. */
    traslados: () =>
      api<import("./types").TodasTraslados>(`${base}/traslados`),
    porCobrar: () =>
      api<import("./types").TodasPorCobrar>(`${base}/por-cobrar`),
    porPagar: () =>
      api<import("./types").TodasPorPagar>(`${base}/por-pagar`),
    // ── Mercancía, tercera tanda (2026-10-02) ──────────────────────────────
    /** Mermas, salidas y lo que faltó al contar, en cada local. */
    mermas: (rango: { desde: string; hasta: string }) =>
      api<import("./types").TodasMermas>(`${base}/inventario/mermas`, {
        params: rango,
      }),
    /** Lo que compró cada local y a quién. */
    compras: (rango: { desde: string; hasta: string }) =>
      api<import("./types").TodasCompras>(`${base}/inventario/compras`, {
        params: rango,
      }),
    /** El mismo producto con distinto precio de una sucursal a otra. */
    precios: () =>
      api<import("./types").TodasPrecios>(`${base}/inventario/precios`),
    /** Lo que una sucursal tiene en su catálogo y otra no. */
    faltan: () =>
      api<import("./types").TodasFaltan>(`${base}/inventario/faltan`),
    // ── Ventas y Dinero, cuarta tanda (2026-10-02) ─────────────────────────
    /** Lo que se anuló, se descontó y se devolvió en cada sucursal. */
    control: (rango: { desde: string; hasta: string }) =>
      api<import("./types").TodasControl>(`${base}/control`, {
        params: rango,
      }),
    /** Cuántas cotizaciones hace cada una y cuántas se vuelven venta. */
    cotizaciones: (rango: { desde: string; hasta: string }) =>
      api<import("./types").TodasCotizaciones>(`${base}/cotizaciones`, {
        params: rango,
      }),
    /** La meta de ventas de cada una en el mes (sin `mes`, el de hoy). */
    metas: (mes?: string | null) =>
      api<import("./types").TodasMetas>(`${base}/metas`, {
        params: mes ? { mes } : {},
      }),
    /** Desde ese mes, la meta de cada una; 0 = sin meta. */
    guardarMetas: (mes: string, metas: { location_id: string; monto: string }[]) =>
      api<import("./types").TodasMetas>(`${base}/metas`, {
        method: "PUT",
        body: { mes, metas },
      }),
    /** Lo que cuesta el personal de cada local frente a lo que vende. */
    nomina: (rango: { desde: string; hasta: string }) =>
      api<import("./types").TodasNomina>(`${base}/nomina`, {
        params: rango,
      }),
  };
}

export const reportes = {
  /**
   * Cuánto entró y cuánto salió por cada forma de cobro.
   *
   * ⚠️ El crédito NO aparece: fiar es una promesa, no dinero.
   */
  /*
    ⚠️ Acepta atajo O rango, porque el backend siempre aceptó los dos y el
    cliente solo mandaba el atajo. La pantalla de Reportes trabaja con fechas
    exactas, y sin esto tenía que conformarse con «este mes».
  */
  cuentas: (rango: { periodo?: string; desde?: string; hasta?: string } = { periodo: "mes" }) =>
    api<ReporteDeCuentas>("/tenant/reports/cuentas", { params: rango }),

  /** El resumen de "cómo va el día". Es lo que alimenta la pantalla de Inicio. */
  panel: () => api<import("./types").Panel>("/tenant/reports/dashboard"),

  ventas: (rango: RangoReporte = { periodo: "hoy" }) =>
    api<import("./types").ReporteVentas>(`/tenant/reports/sales${comoQuery(rango)}`),

  /** El IVA del período: facturado, sin impuestos y cada impuesto (2026-10-20). */
  impuestos: (rango: RangoReporte = { periodo: "hoy" }) =>
    api<import("./types").ReporteDeImpuestos>(`/tenant/reports/impuestos${comoQuery(rango)}`),

  /** El mapa de calor: lo vendido en cada hora de cada día de la semana (2026-10-03). */
  horasPorDia: (rango: RangoReporte = { periodo: "hoy" }) =>
    api<import("@/components/Graficas").CasillaDelMapa[]>(`/tenant/reports/horas-por-dia${comoQuery(rango)}`),

  ganancia: (rango: RangoReporte = { periodo: "hoy" }) =>
    api<import("./types").ReporteGanancia>(`/tenant/reports/profit${comoQuery(rango)}`),

  items: (rango: RangoReporte = { periodo: "hoy" }, limite = 20) =>
    api<import("./types").ReporteItems>(
      `/tenant/reports/items${comoQuery(rango)}${comoQuery(rango) ? "&" : "?"}limite=${limite}`,
    ),

  /**
   * Quién te compra: cuántos son, cuáles son nuevos y cuáles dejaron de venir.
   *
   * Los reportes contestaban qué se vendió y cuánto se ganó, pero no a quién —
   * y «vendiste 3.000» no dice si fueron cien personas de paso o veinte que
   * vienen siempre.
   */
  clientes: (rango: RangoReporte = { periodo: "mes" }) =>
    api<import("./types").ReporteClientes>(`/tenant/reports/clientes${comoQuery(rango)}`),

  equipo: (rango: RangoReporte = { periodo: "hoy" }) =>
    api<import("./types").ReporteEquipo>(`/tenant/reports/staff${comoQuery(rango)}`),

  /** El salón en cifras: comensales, meseros, mesas y cocina (restaurantes). */
  salon: (rango: RangoReporte = { periodo: "hoy" }) =>
    api<import("./types").ReporteSalon>(`/tenant/reports/salon${comoQuery(rango)}`),

  /** Qué te está costando más que antes y a cuánto deberías venderlo. Premium. */
  costos: (dias = 90) =>
    api<import("./types").ReporteCostos>("/tenant/reports/costs", { params: { dias } }),

  /**
   * Qué revisar hoy, en TODOS tus negocios.
   *
   * ⚠️ Es la mitad que le faltaba a «Todos». El consolidado dice cuánto
   * vendiste —que se intuye—; esto dice dónde hay que meterse. Antes cada
   * negocio calculaba sus avisos y solo se veían estando dentro de él, así que
   * con cuatro negocios no los miraba nadie.
   */
  pendientesDelGrupo: () =>
    api<import("./types").PendientesDelGrupo>("/tenant/reports/grupo/pendientes"),

  /** Todos tus negocios en una pantalla. Premium. */
  /**
   * Las sucursales del negocio, comparadas.
   *
   * ⚠️ A diferencia del consolidado, aquí el total SÍ se suma: los locales
   * comparten la moneda de referencia del negocio.
   */
  sucursales: (periodo = "mes") =>
    api<import("./types").ReporteDeSucursales>("/tenant/reports/sucursales", {
      params: { periodo },
    }),

  /**
   * «Todas las sucursales» (2026-09-29): el negocio entero con cada sucursal
   * al lado. Cada fila sale de la misma cuenta que la sucursal por dentro.
   */
  // ⚠️ El período va como `desde`/`hasta` (el filtro de fechas de la app,
  // 2026-10-02), no como atajo: así «del 3 al 12» también se puede pedir.
  todasLasSucursales: todasDe("/tenant/reports/sucursales"),
  /** «Todos mis negocios», con las mismas pantallas (2026-10-03). */
  todosLosNegocios: todasDe("/tenant/reports/grupo"),

  consolidado: (periodo = "mes") =>
    api<import("./types").ReporteConsolidado>("/tenant/reports/consolidado", {
      params: { periodo },
    }),

  /**
   * «Mis negocios» como «Todas las sucursales» (2026-10-03): cada negocio sale
   * de las mismas cuentas que su «Todas las sucursales». Son de Premium (basta
   * con uno de los negocios): sin él responden 402.
   */
  grupo: {
    resumen: (rango: { desde: string; hasta: string }) =>
      api<import("./types").GrupoResumen>("/tenant/reports/grupo/resumen", { params: rango }),
  },

  /** Las cifras que encabezan el reporte: de ingresos a utilidad neta. */
  operativo: (desde: string, hasta: string) =>
    api<import("./types").ResumenOperativo>("/tenant/reports/operativo", {
      params: { desde, hasta },
    }),

  /**
   * Utilidad y pérdida: la cadena del período, los doce meses y el capital.
   *
   * ⚠️ Trae también la pérdida cambiaria — de lo que se cobró en otra moneda,
   * cuánto vale hoy. Llega vacía en un negocio que solo cobra en su moneda.
   */
  utilidad: (desde: string, hasta: string, ano?: number) =>
    api<import("./types").ReporteDeUtilidad>("/tenant/reports/utilidad", {
      // ⚠️ `ano` va suelto del rango: las cifras son del período elegido, la
      // gráfica es siempre de enero a diciembre.
      params: { desde, hasta, ...(ano ? { ano: String(ano) } : {}) },
    }),

  /**
   * Una pestaña entera de Estadísticas en una sola respuesta.
   *
   * ⚠️ Va por pestaña y no todo junto: inventario y finanzas valoran el estante
   * completo y reconstruyen lo que se debe, y pedir las cuatro en cada clic
   * sería cuatro veces el trabajo para enseñar una.
   */
  estadisticas: (
    pestana: "general" | "ventas" | "inventario" | "finanzas",
    desde: string,
    hasta: string,
  ) =>
    api<import("./types").Estadisticas>("/tenant/reports/estadisticas", {
      params: { pestana, desde, hasta },
    }),

  /**
   * El ranking COMPLETO de productos: el «Ver todo» de «Lo más vendido» y «Lo
   * menos vendido» (2026-10-12). El Excel recibe los mismos filtros, así baja
   * exactamente lo que está en pantalla.
   */
  rankingDeProductos: (f: import("./types").FiltrosDelRanking) =>
    api<import("./types").RankingDeProductos>("/tenant/reports/ranking-de-productos", { params: f }),
  rankingDeProductosExcel: (f: import("./types").FiltrosDelRanking) =>
    descargar("/tenant/reports/ranking-de-productos/excel", { params: f }),

  /**
   * El reporte de precios (2026-10-12): cada producto de la sucursal con su
   * precio normal y su precio en cada lista. El Excel recibe los mismos filtros.
   */
  precios: (f: import("./types").FiltrosDelReporteDePrecios) =>
    api<import("./types").ReporteDePrecios>("/tenant/reports/precios", { params: f }),
  preciosExcel: (f: import("./types").FiltrosDelReporteDePrecios) =>
    descargar("/tenant/reports/precios/excel", { params: f }),

  /** Las cifras de UN producto en el período: el buscador de la pestaña de ventas. */
  estadisticasDeItem: (itemId: string, desde: string, hasta: string) =>
    api<import("./types").EstadisticasDeItem>(
      `/tenant/reports/estadisticas/item/${itemId}`,
      { params: { desde, hasta } },
    ),

  /** Tus cuentas en un período: qué entró, qué salió y cuánto queda. */
  cuentasDelRango: (desde: string, hasta: string) =>
    api<import("./types").ReporteDeCuentasEnRango>("/tenant/reports/cuentas-del-rango", {
      params: { desde, hasta },
    }),

  /** Qué entró, qué salió y qué queda de cada producto en el rango. */
  inventarioDelRango: (desde: string, hasta: string) =>
    api<import("./types").ReporteDeInventario>("/tenant/reports/inventario", {
      params: { desde, hasta },
    }),

  /** El Excel con todo. Premium. Baja el archivo directamente. */
  respaldo: (dias = 90) => descargar("/tenant/reports/respaldo", { params: { dias } }),

  /**
   * El Excel de un rango de fechas. Premium.
   *
   * Sin `hoja` baja las cuatro pestañas; con `hoja`, solo esa. Bajar una sola
   * importa cuando el inventario tiene miles de productos y lo único que se
   * quiere son las ventas del mes.
   */
  exportar: (desde: string, hasta: string, hoja?: HojaDelReporte) =>
    descargar("/tenant/reports/exportar", {
      params: hoja ? { desde, hasta, hoja } : { desde, hasta },
    }),

  /** Rotación y análisis de productos (2026-10-04). Premium. */
  rotacion: (desde: string, hasta: string) =>
    api<import("./types").ReporteDeRotacion>("/tenant/reports/rotacion", {
      params: { desde, hasta },
    }),
  rotacionExcel: (desde: string, hasta: string) =>
    descargar("/tenant/reports/rotacion/excel", { params: { desde, hasta } }),

  /** Predicción de demanda y lista de compra (2026-10-04). Premium. */
  prediccion: (dias: 7 | 15 | 30) =>
    api<import("./types").ReporteDePrediccion>("/tenant/reports/prediccion", {
      params: { dias },
    }),
  prediccionExcel: (dias: 7 | 15 | 30) =>
    descargar("/tenant/reports/prediccion/excel", { params: { dias } }),

  /** Las dos mismas, en «Todas las sucursales» (2026-10-05). */
  rotacionDeSucursales: (desde: string, hasta: string) =>
    api<import("./types").RotacionDeSucursales>("/tenant/reports/sucursales/rotacion", {
      params: { desde, hasta },
    }),
  prediccionDeSucursales: (dias: 7 | 15 | 30) =>
    api<import("./types").PrediccionDeSucursales>("/tenant/reports/sucursales/prediccion", {
      params: { dias },
    }),
};

/** Las pestañas que se pueden bajar sueltas. Igual que `HOJAS` en el backend. */
export type HojaDelReporte = "ventas" | "detalle" | "cobros" | "cuentas" | "inventario" | "gastos";

// =============================================================================
//  WHATSAPP (SOFTFI BOT)
// =============================================================================

export const whatsapp = {
  /**
   * ¿Está viva la sesión de WhatsApp?
   *
   * Es la única forma de enterarse de que hay que volver a escanear el QR: una
   * sesión caída no se queja, simplemente deja de mandar mensajes.
   */
  estado: () => api<import("./types").EstadoWhatsApp>("/tenant/whatsapp/estado"),

  /**
   * El resumen de HOY con los datos de verdad, los menús y los valores de
   * fábrica de cada aviso. No manda nada: es para el teléfono de la pantalla
   * del bot (2026-10-04).
   */
  vistaPrevia: () =>
    api<{
      resumen: string | null;
      /** Un resumen por supervisor, con sus sucursales (2026-10-04). */
      resumenes_supervisores: { nombre: string; texto: string | null }[];
      menu_dueno: string;
      menu_supervisor: string;
      de_fabrica: Record<string, { modo: string; a_quien: string }>;
      /** Sucursales y almacenes abiertos, la sede primero. */
      ubicaciones: { id: string; nombre: string; tipo: string }[];
      /** `{sucursal: número}` del supervisor de cada una, tal como funciona hoy. */
      supervisores: Record<string, string>;
      /** El equipo, para elegir a quién más le llega un aviso. */
      equipo: { user_id: string; nombre: string; telefono: string }[];
    }>("/tenant/whatsapp/vista-previa"),

  /** Manda el resumen de hoy ahora mismo, para ver cómo se ve. */
  probar: () =>
    api<{ ok: boolean; mensaje: string; texto?: string }>("/tenant/whatsapp/probar", {
      method: "POST",
    }),
};

// =============================================================================
//  VENTAS FIADAS (CUENTAS POR COBRAR)
// =============================================================================

export const fiado = {
  /** Quién debe, cuánto y desde cuándo. Ordenado por antigüedad de la deuda. */
  cuentasPorCobrar: () =>
    api<import("./types").ResumenCuentasPorCobrar>("/tenant/credit/receivables"),

  estadoDeCuenta: (customerId: string) =>
    api<import("./types").EstadoDeCuenta>(`/tenant/credit/customers/${customerId}`),

  abonos: (limite = 100) =>
    api<import("./types").Abono[]>(`/tenant/credit/payments?limite=${limite}`),

  /**
   * Registra un abono.
   *
   * ⚠️ NO se manda la tasa de cambio: la pone el servidor con la vigente del
   * momento. Si se aceptara del cliente, cualquiera podría abonar 100 Bs
   * diciendo que valen 100 USD.
   */
  abonar: (datos: {
    /** La venta a crédito que se abona. O esto, o `cuenta_id`. */
    sale_id?: string;
    /** La cuenta por cobrar registrada a mano. */
    cuenta_id?: string;
    payment_method_id: string;
    currency_code: string;
    /** Cuánto llegó, en `currency_code`. O esto, o `monto_en_principal`. */
    amount?: string;
    /**
     * Cuánto se paga en la moneda principal (la cuota de Cashea «$0,32»). El
     * servidor calcula cuánto es en `currency_code` con su tasa.
     */
    monto_en_principal?: string;
    /** Si se entrega más de lo que se debe: el vuelto, en una o varias monedas (la última la calcula el servidor). */
    vuelto?: import("@/components/VueltoDelPago").ParteDeVueltoAEnviar[];
    reference?: string | null;
    note?: string | null;
  }) => api<import("./types").Abono>("/tenant/credit/payments", { method: "POST", body: datos }),

  /**
   * «Pagar todo» (2026-10-16): un solo pago para lo pendiente del cliente. El
   * servidor lo reparte desde la deuda más antigua; si no alcanza, lo que
   * queda pendiente es lo más reciente.
   */
  pagarVarias: (
    customerId: string,
    datos: {
      payment_method_id: string;
      currency_code: string;
      /** Cuánto llegó, en `currency_code`. O esto, o `monto_en_principal`. */
      amount?: string;
      /** Todo lo pendiente en otra moneda: el servidor convierte con su tasa. */
      monto_en_principal?: string;
      /** Si se entrega más de lo que se debe: el vuelto, en una o varias monedas (la última la calcula el servidor). */
      vuelto?: import("@/components/VueltoDelPago").ParteDeVueltoAEnviar[];
      reference?: string | null;
    },
  ) =>
    api<import("./types").PagoDeVarias>(`/tenant/credit/customers/${customerId}/payments`, {
      method: "POST",
      body: datos,
    }),

  /**
   * Registra una cuenta por cobrar a mano: lo que alguien debe sin una venta
   * a crédito detrás. No mueve dinero; el dinero entra con los abonos. En otra
   * moneda, el servidor la convierte con la tasa de hoy.
   */
  registrarCuenta: (datos: {
    customer_id: string;
    concepto: string;
    monto: string;
    moneda?: string | null;
    fecha?: string | null;
    vence_el?: string | null;
    nota?: string | null;
  }) =>
    api<import("./types").CuentaPorCobrar>("/tenant/credit/cuentas", {
      method: "POST",
      body: datos,
    }),

  anularCuenta: (id: string, reason: string) =>
    api<import("./types").CuentaPorCobrar>(`/tenant/credit/cuentas/${id}/void`, {
      method: "POST",
      body: { reason },
    }),

  anularAbono: (id: string, reason: string) =>
    api<import("./types").Abono>(`/tenant/credit/payments/${id}/void`, {
      method: "POST",
      body: { reason },
    }),

  /**
   * Enciende, apaga o cambia el cobro automático de una financiera en la
   * sucursal del equipo (2026-10-12). Al encenderlo se registra enseguida lo
   * que ya tocaba; las cuotas vencidas de antes, solo con `incluir_atrasadas`.
   */
  guardarCobroAutomatico: (
    metodoId: string,
    datos: { activo: boolean; destino_id: string | null; incluir_atrasadas?: boolean },
  ) =>
    api<import("./types").CobroAutomatico>(`/tenant/credit/cobro-automatico/${metodoId}`, {
      method: "PUT",
      body: datos,
    }),
};

// =============================================================================
//  AVISOS (la campanita)
// =============================================================================

export const avisos = {
  /**
   * A quién le escribe ESTE negocio para cambiar de plan.
   *
   * ⚠️ Si lo trajo un socio, es su socio y no el soporte general: esa
   * persona lo instaló y cobra comisión por él.
   */
  asesor: () => api<import("./types").ContactoDelAsesor>("/tenant/notifications/asesor"),

  /**
   * Lo que este usuario debería saber ahora mismo.
   *
   * No se guardan: los calcula el servidor en cada consulta, así que un aviso
   * desaparece solo cuando el problema se arregla.
   */
  listar: () => api<import("./types").ListaDeAvisos>("/tenant/notifications"),
};

// =============================================================================
//  VENTAS EN ESPERA
// =============================================================================

export const carritos = {
  listar: () => api<import("./types").CarritoGuardado[]>("/pos/parked"),

  guardar: (datos: {
    etiqueta: string;
    lineas: {
      item_id: string;
      variant_id?: string | null;
      qty: string;
      discount?: string;
      staff_user_id?: string | null;
      monto?: string | null;
      variant_name?: string | null;
      staff_nombre?: string | null;
      opciones?: { id: string; nombre: string; precio: string }[];
    }[];
    nota?: string | null;
    customer_id?: string | null;
    ajustes?: import("./types").AjustesDeCuentaEnEspera | null;
  }) => api<import("./types").CarritoGuardado>("/pos/parked", { method: "POST", body: datos }),

  descartar: (id: string) =>
    api<{ message: string }>(`/pos/parked/${id}`, { method: "DELETE" }),
};

// =============================================================================
//  NORA — LA ASISTENTE
// =============================================================================

export const sofi = {
  /**
   * ¿Está disponible, cuánto cupo queda y qué preguntarle?
   *
   * Se pide ANTES de dibujar el chat: enseñar una conversación que no va a poder
   * responder es peor que no enseñarla.
   */
  estado: () => api<import("./types").EstadoSofi>("/tenant/sofi/estado"),

  /**
   * @param alcance `negocio` (por defecto) o `grupo`.
   *
   * Con `grupo`, Sofi mira TODOS los negocios del dueño y sus locales, con los
   * problemas ya encontrados por el servidor. Es lo que usa «Mis negocios»:
   * allí la pregunta nunca es «cómo va este», es «cuál va mal».
   */
  preguntar: (
    pregunta: string,
    historial: import("./types").TurnoSofi[] = [],
    alcance: "negocio" | "grupo" | "sucursales" = "negocio",
    /** El menú tal como lo ve esta persona: ver `app/menuVisible.ts`. */
    menu: string[] = [],
  ) =>
    api<import("./types").RespuestaSofi>("/tenant/sofi", {
      method: "POST",
      body: {
        pregunta,
        // Solo el texto: las gráficas de respuestas anteriores no le dicen
        // nada al modelo y harían la petición más pesada.
        historial: historial.map(({ papel, texto }) => ({ papel, texto })),
        alcance,
        menu,
      },
    }),
};


// =============================================================================
//  MODO RESTAURANTE
// =============================================================================

/** Lo que se manda al abrir o cambiar un delivery del salón. */
export type DatosDeDelivery = {
  direccion: string | null;
  referencia: string | null;
  cliente: string | null;
  telefono: string | null;
  costo: string;
};

export const mesas = {
  salones: (locationId: string) =>
    api<import("./types").Salon[]>("/tenant/mesas/salones", {
      params: { location_id: locationId },
    }),

  crearSalon: (
    locationId: string,
    datos: { nombre: string; ancho_cm?: number; alto_cm?: number },
  ) =>
    api<import("./types").Salon>("/tenant/mesas/salones", {
      method: "POST",
      params: { location_id: locationId },
      body: datos,
    }),

  /** Renombrar, ordenar, cerrar o reabrir. No se cierra con mesas ocupadas. */
  editarSalon: (salonId: string, cambios: { nombre?: string; orden?: number; activo?: boolean }) =>
    api<import("./types").Salon>(`/tenant/mesas/salones/${salonId}`, {
      method: "PATCH",
      body: cambios,
    }),

  /** Con todas sus mesas libres. Las ventas ya cobradas no se tocan. */
  eliminarSalon: (salonId: string) =>
    api<void>(`/tenant/mesas/salones/${salonId}`, { method: "DELETE" }),

  /** La foto del plano del local, para calcar encima al dibujar. */
  subirFondo: (salonId: string, archivo: File) =>
    api<import("./types").Salon>(`/tenant/mesas/salones/${salonId}/fondo`, {
      method: "POST",
      body: conArchivo(archivo),
    }),

  quitarFondo: (salonId: string) =>
    api<import("./types").Salon>(`/tenant/mesas/salones/${salonId}/fondo`, {
      method: "DELETE",
    }),

  /**
   * Guarda el salón ENTERO, tal como quedó en el editor.
   *
   * Se manda todo de una vez y no elemento por elemento, por dos razones:
   * dibujar un salón son veinte gestos —y con el wifi de un local, mandando uno
   * por gesto se acaba con la mitad guardada y la otra no— y porque así el
   * deshacer vive en el navegador y no le pide nada al servidor.
   *
   * ⚠️ Responde 409 `mesa_ocupada` si se intenta borrar una mesa con cuenta
   * abierta. Rechaza el guardado entero, no solo esa mesa.
   *
   * Todo en CENTÍMETROS, y `plano` (las medidas y el piso del local) va
   * siempre: el servidor lo exige para no confundir este editor con el de
   * antes, que contaba en casillas.
   */
  guardarMapa: (
    salonId: string,
    elementos: {
      id?: string | null;
      tipo: string;
      nombre?: string | null;
      x: number;
      y: number;
      ancho: number;
      alto: number;
      rotacion?: number;
      forma?: string;
      capacidad?: number;
      estilo?: import("./types").EstiloDePieza;
    }[],
    plano: {
      ancho_cm: number;
      alto_cm: number;
      piso: import("./types").PisoDelPlano;
      cuadricula_cm: number;
      fondo_opacidad: number;
    },
  ) =>
    api<import("./types").ElementoMapa[]>(`/tenant/mesas/salones/${salonId}/mapa`, {
      method: "PUT",
      body: { elementos, plano },
    }),

  // ── Comandas ──────────────────────────────────────────────────────────────

  abiertas: () => api<import("./types").Comanda[]>("/tenant/mesas/comandas"),

  ver: (id: string) => api<import("./types").Comanda>(`/tenant/mesas/comandas/${id}`),

  /** Sin `mesa_id` abre una cuenta suelta: para llevar o delivery. */
  abrir: (datos: {
    mesa_id?: string | null;
    comensales?: number;
    nota?: string | null;
    delivery?: DatosDeDelivery;
    mesero_id?: string | null;
    cliente?: string | null;
    telefono?: string | null;
  }) =>
    api<import("./types").Comanda>("/tenant/mesas/comandas", {
      method: "POST",
      body: datos,
    }),

  /** Apunta platos. **Todavía no van a la cocina**: quedan en `pedido`. */
  /**
   * Poner o quitar la nota de un plato: «sin tomate», «bien cocido».
   *
   * Se puede hasta que el plato este LISTO, no solo antes de enviarlo: el caso
   * real es el cliente que se acuerda treinta segundos tarde.
   */
  cambiarNota: (lineaId: string, nota: string | null) =>
    api<import("./types").LineaComanda>(`/tenant/mesas/lineas/${lineaId}/nota`, {
      method: "PATCH",
      body: { nota },
    }),

  /** «Ah, y sin cebolla»: la lista ENTERA de lo quitado (vacía = completo). */
  cambiarSin: (lineaId: string, sin: string[]) =>
    api<import("./types").LineaComanda>(`/tenant/mesas/lineas/${lineaId}/sin`, {
      method: "PATCH",
      body: { sin },
    }),

  apuntar: (
    comandaId: string,
    lineas: {
      item_id: string;
      variant_id?: string | null;
      cantidad: string;
      nota?: string | null;
      opciones?: string[];
      /** «Sin tomate»: `variant_id` de los ingredientes quitados. */
      sin?: string[];
    }[],
  ) =>
    api<import("./types").Comanda>(`/tenant/mesas/comandas/${comandaId}/lineas`, {
      method: "POST",
      body: lineas,
    }),

  /** Manda a preparar todo lo apuntado. A partir de aquí ya cuesta dinero. */
  enviar: (comandaId: string) =>
    api<import("./types").Comanda>(`/tenant/mesas/comandas/${comandaId}/enviar`, {
      method: "POST",
    }),

  /**
   * El mesero se lo llevó del pase.
   *
   * ⚠️ «Listo» y «entregado» no son lo mismo: listo es que la cocina
   * terminó, entregado es que salió del pase. Entre los dos hay un rato
   * en el que el plato está solo, y esa es la ventana donde se enfría.
   */
  marcarEntregado: (lineaId: string) =>
    api<import("./types").LineaComanda>(`/tenant/mesas/lineas/${lineaId}/entregado`, {
      method: "POST",
    }),

  marcarListo: (lineaId: string) =>
    api<import("./types").LineaComanda>(`/tenant/mesas/lineas/${lineaId}/listo`, {
      method: "POST",
    }),

  /**
   * Quita un plato de la cuenta.
   *
   * Lo que está en `pedido` se borra sin más: nadie lo cocinó. Lo ya enviado se
   * marca `anulada` con motivo y con nombre, y puede pedir un supervisor.
   */
  anular: (lineaId: string, motivo: string) =>
    api<import("./types").LineaComanda | null>(`/tenant/mesas/lineas/${lineaId}/anular`, {
      method: "POST",
      body: { motivo },
    }),

  /** Lo que está esperando en cocina, agrupado por comanda y ronda. */
  cocina: (area?: string) =>
    api<import("./types").Comanda[]>("/tenant/mesas/cocina", { params: { area } }),

  // ── Cerrar la cuenta ──────────────────────────────────────────────────────

  /** Pidieron la cuenta. **No cobra nada**: solo cambia el color de la mesa. */
  precuenta: (comandaId: string) =>
    api<import("./types").Comanda>(`/tenant/mesas/comandas/${comandaId}/precuenta`, {
      method: "POST",
    }),

  /**
   * Cobra la cuenta creando una **venta normal**.
   *
   * ⚠️ No se mandan las líneas ni los precios: los saca el servidor de la
   * comanda, congelados desde que se pidieron. La mesa que pidió a las 7 paga
   * lo de las 7.
   *
   * Si la cuenta ya se cobró devuelve la venta que ya existe, no un error: el
   * mesero cobra con la tablet y el wifi se cae.
   */
  cobrar: (
    comandaId: string,
    datos: {
      client_uuid: string;
      payments: PagoIn[];
      customer_id?: string | null;
      location_id?: string | null;
      register_id?: string | null;
      cobrar_servicio?: boolean | null;
      nota?: string | null;
      /**
       * Qué impuestos de pedido encendió el mesero (el IGTF y parientes).
       *
       * ⚠️ Los identificadores, nunca el monto: el servidor lo calcula sobre
       * lo que se pagó en cada moneda. Mismo criterio que en el mostrador.
       */
      order_tax_ids?: string[];
      /** Con qué lista se cobra. Vacío = los precios de la cuenta. */
      lista_precios_id?: string | null;
    },
  ) =>
    api<Sale>(`/tenant/mesas/comandas/${comandaId}/cobrar`, { method: "POST", body: datos }),

  /** Cómo quedaría la cuenta con cada lista activa. No cambia nada. */
  preciosPorLista: (comandaId: string) =>
    api<import("./types").CuentaEnLista[]>(
      `/tenant/mesas/comandas/${comandaId}/precios-por-lista`,
    ),

  /** Se fueron sin consumir, o sin pagar. Pide supervisor y motivo. */
  meseros: () => api<{ user_id: string; nombre: string }[]>("/tenant/mesas/meseros"),

  cambiarMesero: (comandaId: string, meseroId: string) =>
    api<import("./types").Comanda>(`/tenant/mesas/comandas/${comandaId}/mesero`, {
      method: "PUT",
      body: { mesero_id: meseroId },
    }),

  ponerCliente: (comandaId: string, datos: { cliente: string | null; telefono: string | null }) =>
    api<import("./types").Comanda>(`/tenant/mesas/comandas/${comandaId}/cliente`, {
      method: "PUT",
      body: datos,
    }),

  ponerDelivery: (comandaId: string, datos: DatosDeDelivery) =>
    api<import("./types").Comanda>(`/tenant/mesas/comandas/${comandaId}/delivery`, {
      method: "PUT",
      body: datos,
    }),

  quitarDelivery: (comandaId: string) =>
    api<import("./types").Comanda>(`/tenant/mesas/comandas/${comandaId}/delivery`, {
      method: "DELETE",
    }),

  cerrarSinCobrar: (comandaId: string, motivo: string) =>
    api<import("./types").Comanda>(
      `/tenant/mesas/comandas/${comandaId}/cerrar-sin-cobrar`,
      { method: "POST", body: { motivo } },
    ),

  /** Mover la cuenta a otra mesa. Sin `mesaId`, la cuenta se queda sin mesa. */
  trasladar: (comandaId: string, mesaId: string | null) =>
    api<import("./types").Comanda>(`/tenant/mesas/comandas/${comandaId}/trasladar`, {
      method: "POST",
      body: { mesa_id: mesaId },
    }),

  /**
   * «Esto lo pago yo y aquello él.»
   *
   * Saca las líneas elegidas a una cuenta nueva en la MISMA mesa. La mesa no
   * queda libre hasta que se cierren las dos.
   *
   * ⚠️ «Somos cuatro, divide entre cuatro» NO usa esto: es una sola cuenta
   * pagada con cuatro pagos, y eso ya lo hace la pantalla de cobro.
   */
  dividir: (comandaId: string, lineas: string[]) =>
    api<import("./types").Comanda>(`/tenant/mesas/comandas/${comandaId}/dividir`, {
      method: "POST",
      body: { lineas },
    }),

  /** Junta dos cuentas: la de `absorbida` pasa entera a `comandaId`. */
  unir: (comandaId: string, absorbida: string) =>
    api<import("./types").Comanda>(`/tenant/mesas/comandas/${comandaId}/unir`, {
      method: "POST",
      body: { comanda_id: absorbida },
    }),

  /** «Ya la limpié.» De por limpiar a libre. */
  limpia: (mesaId: string) =>
    api<import("./types").ElementoMapa>(`/tenant/mesas/mesas/${mesaId}/limpia`, {
      method: "POST",
    }),
};

// =============================================================================
//  CUENTAS POR PAGAR
// =============================================================================

export type PagoAProveedor = {
  id: string;
  cuenta_id: string;
  metodo_nombre: string;
  afecta_caja: boolean;
  currency_code: string;
  monto: string;
  tasa: string;
  monto_en_principal: string;
  referencia: string | null;
  nota: string | null;
  pagado_por_nombre: string | null;
  pagado_at: string;
  anulado_at: string | null;
  anulado_motivo: string | null;
};

export type CuentaPorPagar = {
  id: string;
  concepto: string;
  proveedor_nombre: string;
  supplier_id: string | null;
  categoria_id: string | null;
  /** En blanco si no se le puso tipo. La fila lo pinta como «Sin categoría». */
  categoria_nombre: string | null;
  monto: string;
  pagado: string;
  /** `monto − Σ pagos no anulados`. Lo calcula el servidor, nunca se guarda. */
  saldo: string;
  moneda_original: string | null;
  monto_original: string | null;
  /** Cada cuántos meses vuelve. Vacío = de una sola vez. */
  repite_cada_meses: number | null;
  /**
   * Si nació de otra cuenta anterior, sin que nadie la anotara.
   *
   * ⚠️ Se dice en la pantalla porque cambia lo que significa: una deuda que
   * apareció sola no la escribió nadie, y quien la ve por primera vez tiene
   * que saber de dónde salió antes de pagarla.
   */
  viene_de_una_repeticion: boolean;
  emitida_el: string;
  vence_el: string | null;
  /** Negativo = ya venció. Vacío = sin fecha. Lo calcula el servidor con la
   *  zona horaria del negocio: «vence hoy» a las once de la noche en Caracas
   *  saldría como «venció ayer» si el aparato está en otra zona. */
  dias_para_vencer: number | null;
  esta_pagada: boolean;
  estado: string;
  referencia: string | null;
  nota: string | null;
  purchase_id: string | null;
  pagos: PagoAProveedor[];
  created_at: string;
  /** La sucursal que la paga. En un almacén, siempre otra (2026-10-01). */
  a_cargo_de?: string | null;
  /** El almacén donde se registró, si se registró en uno. */
  desde_almacen?: string | null;
  anulada_at?: string | null;
  anulada_motivo?: string | null;
};

/**
 * Todo lo de quien se le paga: sus cuentas pendientes, pagadas y anuladas, cada
 * una con sus pagos (2026-10-24). Es la respuesta a «yo te pagué».
 */
export type HistorialDeBeneficiario = {
  supplier_id: string | null;
  nombre: string;
  saldo: string;
  vencido: string;
  total_pagado: string;
  cuantas_pendientes: number;
  /** Las más nuevas primero. */
  cuentas: CuentaPorPagar[];
};

export type DeudaPorProveedor = {
  supplier_id: string | null;
  nombre: string;
  saldo: string;
  cuantas: number;
  vencido: string;
};

export type ResumenPorPagar = {
  total_por_pagar: string;
  vencido: string;
  vence_esta_semana: string;
  sin_fecha: string;
  cuantas_cuentas: number;
  cuantas_vencidas: number;
  cuentas: CuentaPorPagar[];
  por_proveedor: DeudaPorProveedor[];
};

export const porPagar = {
  listar: (incluirPagadas = false) =>
    api<ResumenPorPagar>("/tenant/por-pagar", {
      params: { incluir_pagadas: incluirPagadas },
    }),

  obtener: (id: string) => api<CuentaPorPagar>(`/tenant/por-pagar/${id}`),

  /** Por el proveedor si lo es; si no, por el nombre con que se registró. */
  historial: (quien: { supplier_id: string | null; nombre: string }) =>
    api<HistorialDeBeneficiario>("/tenant/por-pagar/beneficiario", {
      params: quien.supplier_id ? { supplier_id: quien.supplier_id } : { nombre: quien.nombre },
    }),

  crear: (datos: {
    concepto: string;
    monto: string;
    /** De qué tipo de gasto es. Opcional, como en el gasto de caja. */
    categoria_id?: string;
    /** Una categoría escrita al vuelo, sin salir del formulario. */
    categoria_nueva?: string;
    proveedor_nombre?: string | null;
    supplier_id?: string | null;
    currency_code?: string | null;
    emitida_el?: string | null;
    vence_el?: string | null;
    /**
     * Cada cuántos meses vuelve. Vacío = de una sola vez.
     *
     * ⚠️ Softfi crea LA SIGUIENTE Y SOLO LA SIGUIENTE cuando esta venza —
     * pagada o no. Crear el alquiler de febrero al pagar el de enero dejaría
     * a quien no pagó enero creyendo que debe un alquiler cuando debe dos.
     */
    repite_cada_meses?: number | null;
    referencia?: string | null;
    nota?: string | null;
    /**
     * En un almacén: el id de la sucursal que la paga. Vacío = la principal.
     * Un almacén no maneja dinero (2026-10-01).
     */
    a_cargo_de?: string | null;
  }) => api<CuentaPorPagar>("/tenant/por-pagar", { method: "POST", body: datos }),

  actualizar: (id: string, cambios: Record<string, unknown>) =>
    api<CuentaPorPagar>(`/tenant/por-pagar/${id}`, { method: "PATCH", body: cambios }),

  anular: (id: string, motivo: string) =>
    api<CuentaPorPagar>(`/tenant/por-pagar/${id}/anular`, {
      method: "POST",
      body: { motivo },
    }),

  /**
   * ⚠️ Si el método toca el cajón, esto **descuenta del arqueo** del turno
   * abierto. Es la mitad que más se olvida: el dueño paga con la plata de la
   * caja y al cerrar faltan esos billetes.
   */
  pagar: (
    id: string,
    datos: {
      payment_method_id: string;
      currency_code: string;
      monto: string;
      referencia?: string | null;
      nota?: string | null;
      /** Si se entrega más de lo que se debe: el vuelto, en una o varias monedas (la última la calcula el servidor). */
      vuelto?: import("@/components/VueltoDelPago").ParteDeVueltoAEnviar[];
      /**
       * De dónde sale el efectivo (2026-10-13): una caja abierta (el id de su
       * turno) o el resguardo. Ver `features/caja/OrigenDelEfectivo.tsx`.
       */
      caja_id?: string;
      resguardo?: boolean;
    },
  ) => api<CuentaPorPagar>(`/tenant/por-pagar/${id}/pagos`, { method: "POST", body: datos }),

  anularPago: (pagoId: string, motivo: string) =>
    api<CuentaPorPagar>(`/tenant/por-pagar/pagos/${pagoId}/anular`, {
      method: "POST",
      body: { motivo },
    }),
};

// =============================================================================
//  NÓMINA
// =============================================================================
//
// Las formas salen de `nomina/schemas.py` en el backend, campo por campo.

import type { Frecuencia, TipoDeConcepto } from "@/features/nomina/calculo";

export type ConceptoDeNomina = {
  id: string;
  tipo: TipoDeConcepto;
  nombre: string;
  /** El monto, o la tarifa en «por día» y «por hora». */
  valor: string | null;
  /** Solo en descuentos y aportes. */
  porcentaje: string | null;
  /** A quién se le entrega: con esto, al pagar se anota solo en «Por pagar». */
  pagar_a: string | null;
};

export type ValeDeNomina = {
  id: string;
  trabajador_id: string;
  trabajador_nombre: string;
  /** En la moneda de referencia: es lo que se descuenta. */
  monto: string;
  /** Lo que falta por descontar. Lo calcula el servidor. */
  saldo: string;
  currency_code: string;
  monto_en_moneda: string;
  metodo_nombre: string;
  afecta_caja: boolean;
  nota: string | null;
  entregado_at: string;
  entregado_por_nombre: string | null;
  anulado_at: string | null;
  anulado_motivo: string | null;
};

export type Trabajador = {
  id: string;
  nombre: string;
  /** La sucursal donde trabaja: allí se ve y allí cuentan sus sueldos. */
  location_id: string | null;
  documento: string | null;
  telefono: string | null;
  cargo: string | null;
  user_id: string | null;
  usuario_nombre: string | null;
  frecuencia: Frecuencia;
  inicio: string;
  payment_method_id: string | null;
  metodo_nombre: string | null;
  metodo_afecta_caja: boolean;
  currency_code: string | null;
  activo: boolean;
  nota: string | null;
  conceptos: ConceptoDeNomina[];
  /** El próximo período sin pagar: el atrasado, si lo hay. */
  periodo_desde: string;
  periodo_hasta: string;
  /** Negativo = ya cumplió el período y no se le ha pagado. */
  dias_para_cobrar: number;
  devengado_estimado: string;
  /** Lo que recibiría hoy, con los vales pendientes descontados. */
  estimado: string;
  /** Cobra por día, por hora o comisión: falta saber cuánto. */
  estimado_incompleto: boolean;
  vales_pendientes: string;
  vales: ValeDeNomina[];
  ultimo_pago_at: string | null;
};

export type LineaDePagoNomina = {
  tipo: TipoDeConcepto;
  nombre: string;
  cantidad: string | null;
  valor: string | null;
  porcentaje: string | null;
  /** Siempre positivo: si suma o resta lo dice el tipo. */
  monto: string;
  pagar_a: string | null;
  vale_id: string | null;
  cuenta_por_pagar_id: string | null;
};

export type PagoDeNomina = {
  id: string;
  /** «NOM-000012». */
  numero: string;
  trabajador_id: string;
  trabajador_nombre: string;
  trabajador_documento: string | null;
  trabajador_cargo: string | null;
  trabajador_telefono: string | null;
  periodo_desde: string;
  periodo_hasta: string;
  devengado: string;
  descuentos: string;
  vales: string;
  neto: string;
  aportes: string;
  metodo_nombre: string;
  afecta_caja: boolean;
  currency_code: string;
  tasa: string;
  neto_en_moneda: string;
  referencia: string | null;
  nota: string | null;
  pagado_por_nombre: string | null;
  pagado_at: string;
  anulado_at: string | null;
  anulado_motivo: string | null;
  lineas: LineaDePagoNomina[];
  /** Algo que hay que saber al anular: una deuda de «Por pagar» ya pagada. */
  aviso: string | null;
  /** La sucursal del trabajador: arriba en la hoja carta. */
  sucursal_nombre?: string | null;
  /** ¿Es la sede principal? Si no, la hoja carta lleva el domicilio fiscal a la izquierda. */
  sucursal_es_principal?: boolean;
  sucursal_direccion?: string | null;
  sucursal_telefono?: string | null;
};

export type PropuestaDeNomina = {
  trabajador: Trabajador;
  periodo_desde: string;
  periodo_hasta: string;
  lineas: LineaDePagoNomina[];
  /** Los vales pendientes, del más viejo al más nuevo. */
  vales: ValeDeNomina[];
  devengado: string;
  descuentos: string;
  aportes: string;
};

export type ResumenNomina = {
  moneda: string;
  trabajadores: Trabajador[];
  toca_pagar: string;
  cuantos_toca: number;
  proxima_semana: string;
  vales_pendientes: string;
  pagado_este_mes: string;
};

export type ConceptoDeNominaIn = {
  tipo: TipoDeConcepto;
  nombre: string;
  valor?: string | null;
  porcentaje?: string | null;
  pagar_a?: string | null;
};

export type TrabajadorIn = {
  nombre: string;
  /** Vacía = la sucursal de este aparato. */
  location_id?: string | null;
  documento?: string | null;
  telefono?: string | null;
  cargo?: string | null;
  user_id?: string | null;
  frecuencia: Frecuencia;
  inicio?: string | null;
  payment_method_id?: string | null;
  currency_code?: string | null;
  nota?: string | null;
  conceptos: ConceptoDeNominaIn[];
};

/**
 * El Calendario del negocio (2026-10-06): traslados, entradas, nómina, gastos
 * y cuentas por pagar, lo que pasó y lo que viene. Con la zona del aparato.
 */
export const calendario = {
  ver: (rango: { desde: string; hasta: string }) =>
    api<import("@/features/calendario/calendario").DatosDelCalendario>("/tenant/calendario", {
      params: rango,
    }),
};

export const nomina = {
  resumen: (incluirInactivos = false) =>
    api<ResumenNomina>("/tenant/nomina", { params: { incluir_inactivos: incluirInactivos } }),

  /** Uno solo, con TODOS sus vales: tambien los descontados y los anulados. */
  trabajador: (id: string) => api<Trabajador>(`/tenant/nomina/trabajadores/${id}`),

  crear: (datos: TrabajadorIn) =>
    api<Trabajador>("/tenant/nomina/trabajadores", { method: "POST", body: datos }),

  /** `conceptos` reemplaza la lista entera. Los pagos ya hechos no cambian. */
  actualizar: (id: string, cambios: Partial<TrabajadorIn> & { activo?: boolean }) =>
    api<Trabajador>(`/tenant/nomina/trabajadores/${id}`, { method: "PATCH", body: cambios }),

  /** Solo si nunca se le pagó ni se le dio un vale. Si no, se desactiva. */
  borrar: (id: string) =>
    api<void>(`/tenant/nomina/trabajadores/${id}`, { method: "DELETE" }),

  /** Sin fechas, el próximo período sin pagar con su comisión y sus vales. */
  propuesta: (id: string, periodo?: { desde: string; hasta: string }) =>
    api<PropuestaDeNomina>(`/tenant/nomina/trabajadores/${id}/propuesta`, {
      params: periodo ?? {},
    }),

  /**
   * ⚠️ Los días × tarifa y los porcentajes los rehace el servidor, y si el
   * método toca el cajón, el neto sale del arqueo.
   */
  pagar: (
    id: string,
    datos: {
      periodo_desde: string;
      periodo_hasta: string;
      lineas: {
        tipo: TipoDeConcepto;
        nombre: string;
        cantidad?: string | null;
        valor?: string | null;
        porcentaje?: string | null;
        monto?: string;
        pagar_a?: string | null;
      }[];
      vales: { vale_id: string; monto: string }[];
      payment_method_id: string;
      currency_code: string;
      referencia?: string | null;
      nota?: string | null;
      /**
       * De dónde sale el efectivo (2026-10-13): una caja abierta (el id de su
       * turno) o el resguardo. Ver `features/caja/OrigenDelEfectivo.tsx`.
       */
      caja_id?: string;
      resguardo?: boolean;
    },
  ) =>
    api<PagoDeNomina>(`/tenant/nomina/trabajadores/${id}/pagos`, { method: "POST", body: datos }),

  darVale: (
    id: string,
    datos: {
      monto: string;
      currency_code: string;
      payment_method_id: string;
      nota?: string | null;
      caja_id?: string;
      resguardo?: boolean;
    },
  ) => api<ValeDeNomina>(`/tenant/nomina/trabajadores/${id}/vales`, { method: "POST", body: datos }),

  anularVale: (valeId: string, motivo: string) =>
    api<ValeDeNomina>(`/tenant/nomina/vales/${valeId}/anular`, {
      method: "POST",
      body: { motivo },
    }),

  /** Los recibos por páginas. Antes llegaban los 100 últimos y ya. */
  pagos: (filtro: { page: number; size: number; trabajador_id?: string }) =>
    api<Page<PagoDeNomina>>("/tenant/nomina/pagos", { params: filtro }),

  /** Un recibo de nómina: el que abre el detalle de un movimiento de Cuentas. */
  pago: (pagoId: string) => api<PagoDeNomina>(`/tenant/nomina/pagos/${pagoId}`),

  anularPago: (pagoId: string, motivo: string) =>
    api<PagoDeNomina>(`/tenant/nomina/pagos/${pagoId}/anular`, {
      method: "POST",
      body: { motivo },
    }),
};

/**
 * Opciones de un plato: «de pollo», «con tocineta +$1».
 *
 * Los grupos son DEL NEGOCIO, no del plato: «Extras» se define una vez y se
 * engancha a las ocho hamburguesas. Por eso hay dos familias de llamadas —unas
 * para los grupos y otras para engancharlos— en vez de una sola.
 */
export const opcionesDePlato = {
  listarGrupos: () =>
    api<import("./types").GrupoDeOpciones[]>("/tenant/opciones/grupos"),

  crearGrupo: (datos: { nombre: string; minimo: number; maximo: number; orden?: number }) =>
    api<import("./types").GrupoDeOpciones>("/tenant/opciones/grupos", {
      method: "POST",
      body: datos,
    }),

  cambiarGrupo: (
    grupoId: string,
    datos: Partial<{ nombre: string; minimo: number; maximo: number; orden: number; activo: boolean }>,
  ) =>
    api<import("./types").GrupoDeOpciones>(GRUPO(grupoId), {
      method: "PATCH",
      body: datos,
    }),

  borrarGrupo: (grupoId: string) => api<void>(GRUPO(grupoId), { method: "DELETE" }),

  /**
   * Qué se lleva del inventario esta opción.
   *
   * Se manda la lista COMPLETA: lo que no venga, se quita. Quitar un
   * ingrediente es tan normal como añadirlo, y con endpoints separados la
   * pantalla tendría que llevar la cuenta de qué borró y qué añadió.
   */
  ponerComponentes: (
    opcionId: string,
    componentes: { item_id: string; cantidad: string }[],
  ) =>
    api<import("./types").OpcionDePlato>(
      `/tenant/opciones/opciones/${opcionId}/componentes`,
      { method: "PUT", body: { componentes } },
    ),

  crearOpcion: (grupoId: string, datos: { nombre: string; precio_extra: string; orden?: number }) =>
    api<import("./types").OpcionDePlato>(GRUPO(grupoId) + "/opciones", {
      method: "POST",
      body: datos,
    }),

  cambiarOpcion: (
    opcionId: string,
    datos: Partial<{ nombre: string; precio_extra: string; orden: number; activa: boolean }>,
  ) =>
    api<import("./types").OpcionDePlato>(OPCION(opcionId), { method: "PATCH", body: datos }),

  borrarOpcion: (opcionId: string) => api<void>(OPCION(opcionId), { method: "DELETE" }),

  /** Los grupos que lleva un plato, ya filtrados a lo que está activo. */
  gruposDelPlato: (itemId: string) =>
    api<import("./types").GrupoDeOpciones[]>(ITEM_GRUPOS(itemId)),

  enganchar: (itemId: string, grupoId: string) =>
    api<{ ok: boolean }>(ITEM_GRUPOS(itemId), {
      method: "POST",
      body: { grupo_id: grupoId },
    }),

  desenganchar: (itemId: string, grupoId: string) =>
    api<void>(ITEM_GRUPOS(itemId) + "/" + grupoId, { method: "DELETE" }),
};

// Las rutas se arman con funciones y no con plantillas dentro de cada llamada:
// se repiten cuatro veces cada una y así no se pueden desincronizar.
function GRUPO(id: string): string {
  return "/tenant/opciones/grupos/" + id;
}
function OPCION(id: string): string {
  return "/tenant/opciones/opciones/" + id;
}
function ITEM_GRUPOS(itemId: string): string {
  return "/tenant/opciones/items/" + itemId + "/grupos";
}

/** La ruta de un negocio en el panel. Se repite cinco veces. */
function NEG(id: string): string {
  return "/plataforma/negocios/" + id;
}


// =============================================================================
//  APARTADOS
// =============================================================================

export const apartados = {
  /**
   * Los guardados van del más viejo al más nuevo: a ese cliente es al que hay
   * que llamar hoy. Entregados y anulados, lo más reciente primero.
   */
  listar: (filtro: { estado?: "guardados" | "entregados" | "anulados"; q?: string } = {}) =>
    api<ResumenApartados>("/tenant/apartados", {
      params: { estado: filtro.estado ?? "guardados", q: filtro.q?.trim() || undefined },
    }),

  /** Lo guardado y cada pago, de todas sus versiones si se editó. */
  obtener: (saleId: string) => api<ApartadoDetalle>(`/tenant/apartados/${saleId}`),

  /**
   * La mercancía al estante y la deuda borrada. `pagos` es lo que se le
   * devuelve al cliente; vacío, el negocio se queda con lo abonado.
   */
  anular: (
    saleId: string,
    datos: {
      motivo: string;
      pagos: { payment_method_id: string; currency_code: string; amount: string }[];
      caja_id?: string;
      resguardo?: boolean;
    },
  ) =>
    api<ApartadoDetalle>(`/tenant/apartados/${saleId}/anular`, { method: "POST", body: datos }),

  /**
   * Lo apartado como queda. Lo ya pagado pasa entero y el resto queda como
   * saldo; los precios los pone el servidor.
   */
  editar: (
    saleId: string,
    datos: Pick<VentaIn, "items" | "lista_precios_id" | "order_tax_ids" | "discount_total" | "note">,
  ) =>
    api<ApartadoDetalle>(`/tenant/apartados/${saleId}/editar`, { method: "POST", body: datos }),

  /**
   * El cliente terminó de pagar y se lleva la mercancía.
   *
   * ⚠️ El servidor NO deja entregar con saldo pendiente. Un apartado a medio
   * pagar que sale por la puerta se convierte en un fiado que nadie decidió.
   */
  entregar: (saleId: string) =>
    api<Apartado>(`/tenant/apartados/${saleId}/entregar`, { method: "POST" }),
};


// =============================================================================
//  CÓDIGOS DE DESCUENTO
// =============================================================================

export const codigosDescuento = {
  listar: () => api<CodigoDescuento[]>("/tenant/codigos-descuento"),

  crear: (datos: {
    codigo: string;
    descripcion?: string | null;
    tipo: "porcentaje" | "monto";
    valor: string;
    vence_el?: string | null;
    usos_maximos?: number | null;
    minimo_de_compra?: string | null;
    /** A qué productos aplica. Vacío o sin mandar = a toda la venta. */
    solo_items?: string[];
    /** A qué categorías aplica. Se SUMA a `solo_items`, no se cruza con él. */
    solo_categorias?: string[];
    /** Quién puede aplicarlo al cobrar. Sin mandar = cualquiera. */
    quien_aplica?: QuienAplicaDescuento;
    /** Quiénes, cuando `quien_aplica` es «elegidos». Obligatorio en ese caso. */
    solo_personas?: string[];
  }) => api<CodigoDescuento>("/tenant/codigos-descuento", { method: "POST", body: datos }),

  /**
   * Cambiar un código. **También la palabra.**
   *
   * ⚠️ Antes la palabra estaba bloqueada «porque está en volantes y en
   * WhatsApp», y eso Softfi no lo sabe: puede estar repartido en la calle,
   * dicho de palabra a tres clientes, o ser interno («PERSONAL15»). Esa
   * suposición dejaba al dueño sin poder arreglar un dedazo.
   *
   * Renombrar no toca ninguna venta vieja: cada venta congela el texto que se
   * escribió al cobrar. Lo único que pasa es que la palabra anterior deja de
   * funcionar, y eso lo decide él.
   */
  actualizar: (
    id: string,
    cambios: {
      codigo?: string;
      descripcion?: string | null;
      tipo?: "porcentaje" | "monto";
      valor?: string;
      activo?: boolean;
      vence_el?: string | null;
      usos_maximos?: number | null;
      minimo_de_compra?: string | null;
      /**
       * ⚠️ No mandarlo = no tocar los productos. Mandarlo vacío = que vuelva a
       * aplicar a toda la venta. Son cosas distintas: si no mandarlo los
       * borrara, cambiar la fecha convertiría una promoción de café en un
       * descuento sobre la compra entera.
       */
      solo_items?: string[];
      /** ⚠️ Mismo trato que `solo_items`: no mandarlo = no tocarlo. */
      solo_categorias?: string[];
      quien_aplica?: QuienAplicaDescuento;
      /** ⚠️ Mismo trato que `solo_items`: no mandarlo = no tocarlo. */
      solo_personas?: string[];
    },
  ) => api<CodigoDescuento>(`/tenant/codigos-descuento/${id}`, { method: "PATCH", body: cambios }),

  /** Solo si nunca se usó. Si ya se usó, se apaga. */
  borrar: (id: string) =>
    api<void>(`/tenant/codigos-descuento/${id}`, { method: "DELETE" }),

  /**
   * Vista previa para el cajero: ¿sirve y cuánto rebaja?
   *
   * ═══════════════════════════════════════════════════════════════════════════
   *  ⚠️ SE MANDAN LAS LÍNEAS DEL CARRITO, Y ES LO QUE FALTABA
   * ═══════════════════════════════════════════════════════════════════════════
   *
   * Sin ellas, un código acotado a ciertos productos o categorías no se puede
   * calcular: el servidor no sabe qué se lleva el cliente. Y no descontaba «por
   * si acaso» —que es lo correcto, descontar sobre lo que el dueño dijo que no
   * sería peor— pero dejaba la promoción inservible.
   *
   * Lo encontró el dueño: hizo un código para una categoría, puso ese producto
   * en el carrito, escribió el código y le salió «esta venta no dice cuáles se
   * llevó» con el producto delante. Ninguna promoción acotada se había podido
   * aplicar nunca desde el mostrador.
   *
   * ⚠️ Solo es la vista previa. Lo que se cobra lo vuelve a calcular el
   * servidor al guardar la venta, con el mismo código y las mismas líneas.
   */
  validar: (
    codigo: string,
    subtotal: string,
    lineas?: { item_id: string; line_subtotal: string }[],
  ) =>
    api<DescuentoCalculado>("/tenant/codigos-descuento/validar", {
      method: "POST",
      body: { codigo, subtotal, lineas },
    }),
};

// =============================================================================
//  CUENTAS
// =============================================================================

export const cuentas = {
  listar: () => api<import("./types").ResumenDeCuentas>("/tenant/cuentas"),

  crear: (datos: {
    name: string;
    currency_code: string;
    saldo_inicial?: string;
    nota?: string;
    /** Las formas de cobro que caen aquí, elegidas de una vez al crear. */
    metodo_ids?: string[];
  }) => api<import("./types").Cuenta>("/tenant/cuentas", { method: "POST", body: datos }),

  actualizar: (
    id: string,
    datos: {
      name?: string;
      nota?: string;
      is_active?: boolean;
      metodo_ids?: string[];
      /** Solo se acepta mientras la cuenta esté virgen. */
      currency_code?: string;
    },
  ) =>
    api<import("./types").Cuenta>(`/tenant/cuentas/${id}`, { method: "PATCH", body: datos }),

  /** Solo si está virgen: sin movimientos y con saldo inicial en cero. */
  borrar: (id: string) => api<void>(`/tenant/cuentas/${id}`, { method: "DELETE" }),

  movimientos: (id: string) =>
    api<import("./types").MovimientoDeCuenta[]>(`/tenant/cuentas/${id}/movimientos`),

  /** Todos los movimientos de la cuenta, del más nuevo al más viejo, paginados. */
  libro: (
    id: string,
    filtros: import("./types").FiltrosDelLibro & { page?: number; size?: number },
  ) =>
    api<import("./types").Page<import("./types").LineaDelLibro>>(
      `/tenant/cuentas/${id}/libro`,
      { params: filtros },
    ),

  /** Las métricas de la cuenta con los MISMOS filtros de la lista. */
  resumenDelLibro: (id: string, filtros: import("./types").FiltrosDelLibro) =>
    api<import("./types").ResumenDelLibro>(`/tenant/cuentas/${id}/libro/resumen`, {
      params: filtros,
    }),

  /**
   * Los movimientos de TODAS las cuentas, juntos y paginados (2026-10-21). Con
   * `cuenta`, solo esa.
   */
  libroDeTodas: (
    filtros: import("./types").FiltrosDelLibro & { page?: number; size?: number },
  ) =>
    api<import("./types").Page<import("./types").LineaDeTodas>>("/tenant/cuentas/libro", {
      params: filtros,
    }),

  /** Las métricas de todas las cuentas, con los MISMOS filtros de la lista. */
  resumenDeTodas: (filtros: import("./types").FiltrosDelLibro) =>
    api<import("./types").ResumenDeTodas>("/tenant/cuentas/libro/resumen", { params: filtros }),

  /**
   * El detalle de un movimiento —una fila del libro o de Gastos— y lo que quien
   * mira puede hacer con él (2026-10-22). Ver `features/cuentas/DetalleDelMovimiento.tsx`.
   */
  detalle: (id: string) =>
    api<import("./types").DetalleDeMovimiento>(`/tenant/cuentas/detalle/${id}`),

  /**
   * Anular un ingreso, un retiro, un traspaso o un ajuste hecho a mano en un
   * banco. No se borra: deja de sumar en el saldo. El efectivo va por
   * `caja.anularGasto`.
   */
  anularMovimiento: (id: string, motivo: string) =>
    api<void>(`/tenant/cuentas/movimientos/${id}/anular`, { method: "POST", body: { motivo } }),

  /** Ingresar o retirar. El signo lo pone el servidor según el tipo. */
  mover: (
    id: string,
    datos: {
      tipo: "ingreso" | "retiro";
      amount: string;
      descripcion?: string;
      /**
       * De qué caja sale (o a cuál entra) el efectivo, o el resguardo
       * (2026-10-13). Ver `features/caja/OrigenDelEfectivo.tsx`.
       */
      caja_id?: string;
      resguardo?: boolean;
      /** Solo en el efectivo: en qué moneda entra o sale (2026-10-20). Vacía = la de la cuenta. */
      currency_code?: string;
    },
  ) =>
    api<import("./types").MovimientoDeCuenta>(`/tenant/cuentas/${id}/movimientos`, {
      method: "POST",
      body: datos,
    }),

  /** Dos movimientos atados: sale de una y entra en la otra. */
  traspasar: (datos: {
    origen_id: string;
    destino_id: string;
    amount: string;
    /** Lo que entró, en la moneda del destino. Solo (y siempre) entre monedas distintas. */
    monto_destino?: string;
    /** La tasa como se dice: `900` en «1 USD = 900 VES». Se guarda tal cual. */
    tasa?: string;
    descripcion?: string;
    /** Si una de las dos es el efectivo (2026-10-30): de qué caja sale o en cuál entra… */
    caja_id?: string;
    /** …o el resguardo. */
    resguardo?: boolean;
    /** Y en qué moneda: el cajón tiene varias. */
    currency_code?: string;
  }) =>
    api<import("./types").MovimientoDeCuenta[]>("/tenant/cuentas/traspasos", {
      method: "POST",
      body: datos,
    }),

  /** Se manda el saldo REAL, no la diferencia. */
  ajustar: (id: string, datos: { saldo_real: string; motivo: string }) =>
    api<import("./types").MovimientoDeCuenta>(`/tenant/cuentas/${id}/ajuste`, {
      method: "POST",
      body: datos,
    }),
};

// =============================================================================
//  CATÁLOGO WEB — la página pública de la tienda, en su subdominio
// =============================================================================

export type CatalogoWebAjustes = {
  publicado: boolean;
  subdominio: string | null;
  direccion: string | null;
  dominio: string;
  /** Vacío mientras el dueño no toque nada: se completa con `completarDiseno`. */
  diseno: Partial<import("@/catalogo/temas").Diseno>;
  titulo: string | null;
  eslogan: string | null;
  whatsapp: string | null;
  telefono_del_negocio: string | null;
  pais: string;
  redes: { instagram?: string; tiktok?: string; facebook?: string };
  logo: string | null;
  portada: string | null;
  mostrar_agotados: boolean;
  pedidos_por_whatsapp: boolean;
  /** Qué se dice del inventario en la ficha del producto. */
  existencias_en_ficha: "no" | "donde" | "cuantas";
  /** Si cada producto dice su proveedor, en la lista y en la ficha. */
  mostrar_proveedor?: boolean;
  /** Si cada producto dice su marca. No puede ir junto con `mostrar_proveedor`. */
  mostrar_marca?: boolean;
  mostrar_variaciones?: boolean;
  /**
   * Qué precio enseña la página cuando cada sucursal tiene el suyo
   * (2026-09-27). Un ajuste del dueño, no una regla.
   */
  precio_de?: PrecioDeLaPagina;
  /** La sucursal de `precio_de = "sucursal"`. */
  precio_sucursal_id?: string | null;
  /** Las sucursales abiertas, para elegir de cuál es el precio. */
  sucursales?: { id: string; nombre: string }[];
  /**
   * Qué lista de precios enseña la página (2026-10-05). `null` = el precio de
   * venta normal. Lo que no tiene precio en la lista sale con el normal.
   */
  lista_precios_id?: string | null;
  /** Las listas activas, para elegirla. */
  listas_de_precios?: { id: string; nombre: string }[];
  monedas: string[];
  modo: string;
  nombre_del_negocio: string;
  total_productos: number;
  fondo_disponible: boolean;
  /** El título de «Tendencias». `null` = el de siempre. */
  destacados_titulo: string | null;
  /** Qué datos pide el pedido y cuáles son obligatorios (2026-10-07). */
  datos_del_cliente?: DatosDelClienteWeb;
  /** Retiro o delivery (2026-11-02). Sin modalidades, no se pregunta. */
  entrega?: import("@/catalogo/tipos").EntregaDelPedido;
};

export type DatosDelClienteWeb = {
  pedir: import("@/catalogo/tipos").DatoDelCliente[];
  obligatorios: import("@/catalogo/tipos").DatoDelCliente[];
};

/** El tráfico de la página (2026-10-07): contadores, nunca personas. */
export type TraficoWeb = {
  desde: string;
  hasta: string;
  dias: { dia: string; visitas: number; visitantes: number; vistas_de_productos: number; pedidos: number }[];
  visitas: number;
  visitantes: number;
  vistas_de_productos: number;
  pedidos: number;
  mas_vistos: ProductoVistoWeb[];
  menos_vistos: ProductoVistoWeb[];
  contando_desde: string | null;
};

export type ProductoVistoWeb = {
  producto_id: string;
  nombre: string;
  foto: string | null;
  vistas: number;
  visitantes: number;
};

export type PrecioDeLaPagina = "principal" | "sucursal" | "menor" | "cada_sucursal";

export type CatalogoWebCambios = Partial<{
  publicado: boolean;
  subdominio: string;
  titulo: string;
  eslogan: string;
  whatsapp: string;
  redes: { instagram?: string | null; tiktok?: string | null; facebook?: string | null };
  mostrar_agotados: boolean;
  pedidos_por_whatsapp: boolean;
  existencias_en_ficha: "no" | "donde" | "cuantas";
  mostrar_proveedor: boolean;
  mostrar_marca: boolean;
  /** Si salen los platos con variaciones (solo restaurantes). */
  mostrar_variaciones?: boolean;
  precio_de: PrecioDeLaPagina;
  precio_sucursal_id: string | null;
  lista_precios_id: string | null;
  monedas: string[];
  diseno: import("@/catalogo/temas").Diseno;
  destacados_titulo: string;
  datos_del_cliente: DatosDelClienteWeb;
  entrega: import("@/catalogo/tipos").EntregaDelPedido;
}>;

export type ProductoWeb = {
  id: string;
  item_id: string;
  tipo: string;
  nombre: string;
  nombre_en_caja: string;
  descripcion: string | null;
  categoria: string | null;
  categoria_en_caja: string | null;
  etiqueta: string | null;
  visible: boolean;
  agotado: boolean;
  sin_existencia: boolean;
  lleva_inventario: boolean;
  orden: number;
  precio: string;
  tiene_variantes: boolean;
  foto: string | null;
  foto_grande: string | null;
  tiene_original: boolean;
  con_fondo: boolean;
  /** Las fotos de más, en orden. La principal es `foto`. */
  galeria: FotoDeGaleria[];
  /** Su puesto en «Tendencias». `null` = no está. */
  destacado_orden: number | null;
};

export type FotoDeGaleria = {
  clave: string;
  foto: string;
  foto_grande: string;
  con_fondo: boolean;
};

/** Lo que sale arriba de la página, en su orden. */
export type DestacadosWeb = { titulo: string | null; productos: ProductoWeb[] };

/** Un producto que se vendió mucho: candidato a «Tendencias». */
export type SugerenciaWeb = { producto: ProductoWeb; vendidos: string };

/** Fotos por producto, contando la principal. Igual que en el servidor. */
export const MAX_FOTOS_WEB = 4;

/** Cuántos caben en «Tendencias». Igual que en el servidor. */
export const MAX_DESTACADOS_WEB = 24;

export type ProductoWebCambios = Partial<{
  nombre: string;
  descripcion: string;
  categoria: string;
  etiqueta: string;
  visible: boolean;
  agotado: boolean;
  orden: number;
}>;

export type FiltrosProductoWeb = {
  q?: string;
  /** Una categoría de la página. */
  categoria?: string;
  /** Los que no tienen categoría. */
  sin_categoria?: boolean;
  /** Por la marca del producto (2026-10-18). */
  marca?: string;
  sin_marca?: boolean;
  page?: number;
  size?: number;
};

export type CategoriaWeb = {
  /** `null` = los que no tienen categoría. */
  nombre: string | null;
  total: number;
  ocultos: number;
};

export type ResumenProductosWeb = {
  total: number;
  ocultos: number;
  sin_foto: number;
  categorias: CategoriaWeb[];
};

/**
 * Lo mismo a muchos: una categoría, los sin categoría, una lista, o el
 * catálogo entero (`todos`, que tiene que venir dicho: vacío no es «todos»).
 */
export type LoteWeb = {
  accion: "ocultar" | "mostrar" | "quitar";
  categoria?: string;
  sin_categoria?: boolean;
  ids?: string[];
  todos?: boolean;
};

/** Qué parte de una categoría va en una página. */
export type TramoWeb = {
  categoria: string | null;
  desde: number;
  hasta: number;
  total: number;
};

/**
 * Una página de productos que NO corta categorías: como `Page`, más los
 * tramos que dicen qué categorías van en ella.
 */
export type PaginaDeProductosWeb = Page<ProductoWeb> & { tramos: TramoWeb[] };

export type CandidatoWeb = {
  id: string;
  tipo: string;
  nombre: string;
  nombre_sugerido: string;
  categoria: string | null;
  marca?: string | null;
  precio: string;
};

function conArchivo(archivo: File): FormData {
  const datos = new FormData();
  datos.append("archivo", archivo);
  return datos;
}

export const catalogoWeb = {
  obtener: () => api<CatalogoWebAjustes>("/tenant/catalogo-web"),
  cambiar: (cambios: CatalogoWebCambios) =>
    api<CatalogoWebAjustes>("/tenant/catalogo-web", { method: "PATCH", body: cambios }),
  disponibilidad: (nombre: string) =>
    api<{ subdominio: string; disponible: boolean; motivo: string | null }>(
      "/tenant/catalogo-web/subdominio",
      { params: { nombre } },
    ),
  subirImagen: (para: "logo" | "portada", archivo: File) =>
    api<CatalogoWebAjustes>(`/tenant/catalogo-web/${para}`, { method: "POST", body: conArchivo(archivo) }),
  quitarImagen: (para: "logo" | "portada") =>
    api<CatalogoWebAjustes>(`/tenant/catalogo-web/${para}`, { method: "DELETE" }),
  vistaPrevia: () => api<import("@/catalogo/tipos").CatalogoPublico>("/tenant/catalogo-web/vista-previa"),
  /** Los últimos `dias` días, hoy incluido (2026-10-07). */
  trafico: (dias: number) => api<TraficoWeb>("/tenant/catalogo-web/trafico", { params: { dias } }),

  productos: (filtros: FiltrosProductoWeb) =>
    api<PaginaDeProductosWeb>("/tenant/catalogo-web/productos", { params: filtros }),
  resumenProductos: () => api<ResumenProductosWeb>("/tenant/catalogo-web/productos/resumen"),
  lote: (datos: LoteWeb) =>
    api<{ afectados: number }>("/tenant/catalogo-web/productos/lote", { method: "POST", body: datos }),
  candidatos: (filtros: {
    q?: string;
    category_id?: string;
    marca?: string;
    sin_marca?: boolean;
    page?: number;
    size?: number;
  }) =>
    api<Page<CandidatoWeb>>("/tenant/catalogo-web/candidatos", { params: filtros }),
  agregar: (datos: { item_ids?: string[]; category_id?: string; todos?: boolean }) =>
    api<{ agregados: number; ya_estaban: number }>("/tenant/catalogo-web/productos", {
      method: "POST",
      body: datos,
    }),
  cambiarProducto: (id: string, cambios: ProductoWebCambios) =>
    api<ProductoWeb>(`/tenant/catalogo-web/productos/${id}`, { method: "PATCH", body: cambios }),
  quitarProducto: (id: string) =>
    api<void>(`/tenant/catalogo-web/productos/${id}`, { method: "DELETE" }),
  renombrarCategoria: (de: string, a: string) =>
    api<{ cambiados: number }>("/tenant/catalogo-web/categorias", { method: "PATCH", body: { de, a } }),

  subirFoto: (id: string, archivo: File) =>
    api<ProductoWeb>(`/tenant/catalogo-web/productos/${id}/foto`, {
      method: "POST",
      body: conArchivo(archivo),
    }),
  ponerFondo: (id: string, color: string, forma: string) =>
    api<ProductoWeb>(`/tenant/catalogo-web/productos/${id}/foto/fondo`, {
      method: "POST",
      body: { color, forma },
    }),
  volverAOriginal: (id: string) =>
    api<ProductoWeb>(`/tenant/catalogo-web/productos/${id}/foto/original`, { method: "POST" }),
  quitarFoto: (id: string) =>
    api<ProductoWeb>(`/tenant/catalogo-web/productos/${id}/foto`, { method: "DELETE" }),

  // ── Las fotos de más ──────────────────────────────────────────────────────
  sumarFoto: (id: string, archivo: File) =>
    api<ProductoWeb>(`/tenant/catalogo-web/productos/${id}/fotos`, {
      method: "POST",
      body: conArchivo(archivo),
    }),
  quitarDeGaleria: (id: string, clave: string) =>
    api<ProductoWeb>(`/tenant/catalogo-web/productos/${id}/fotos/quitar`, { method: "POST", body: { clave } }),
  hacerPrincipal: (id: string, clave: string) =>
    api<ProductoWeb>(`/tenant/catalogo-web/productos/${id}/fotos/principal`, { method: "POST", body: { clave } }),
  ordenarGaleria: (id: string, claves: string[]) =>
    api<ProductoWeb>(`/tenant/catalogo-web/productos/${id}/fotos/orden`, { method: "PUT", body: { claves } }),
  fondoDeGaleria: (id: string, clave: string, color: string, forma: string) =>
    api<ProductoWeb>(`/tenant/catalogo-web/productos/${id}/fotos/fondo`, {
      method: "POST",
      body: { clave, color, forma },
    }),
  originalDeGaleria: (id: string, clave: string) =>
    api<ProductoWeb>(`/tenant/catalogo-web/productos/${id}/fotos/original`, { method: "POST", body: { clave } }),

  // ── «Tendencias» ──────────────────────────────────────────────────────────
  destacados: () => api<DestacadosWeb>("/tenant/catalogo-web/destacados"),
  guardarDestacados: (ids: string[]) =>
    api<DestacadosWeb>("/tenant/catalogo-web/destacados", { method: "PUT", body: { ids } }),
  sugerencias: () => api<SugerenciaWeb[]>("/tenant/catalogo-web/destacados/sugerencias"),
};

// =============================================================================
//  CONSULTOR DE PRECIOS — la tablet colgada en un pilar del local
// =============================================================================

/**
 * Un producto, como lo ve un cliente de pie en el pasillo.
 *
 * ⚠️ No trae costo ni cuántas unidades quedan, y no es descuido: esa pantalla
 * la lee cualquiera que pase, incluida la competencia. Ver `consultor/` en el
 * backend.
 */
export type PrecioConsultado = {
  item_id: string;
  variante_id: string | null;
  nombre: string;
  /** La variante, cuando el código es de una: «Talla M». */
  presentacion: string | null;
  categoria: string | null;
  /** Con impuesto, en la moneda de los precios. Lo mismo que cobra la caja. */
  precio: string;
  /**
   * Lo mismo con cada lista pedida en `listas`, por su id, y con el precio
   * normal (`normal`): el precio de cada moneda (2026-10-07).
   */
  precios?: Record<string, string>;
  /** `kg`, `l`, `unit`… Para decir «por kilo». */
  unidad: string;
  foto: string | null;
  /** El peso que traía la etiqueta de la balanza. */
  cantidad: string | null;
  /** Lo que cuesta ESE paquete ya pesado. */
  total: string | null;
  /** El de ese paquete con cada lista de `precios`. */
  totales?: Record<string, string>;
};

export type ConsultaDePrecio = {
  texto: string;
  encontrado: PrecioConsultado | null;
  parecidos: PrecioConsultado[];
};

/** Cómo se puede ordenar la lista del Inventario (2026-10-07). */
export type OrdenDelInventario =
  | "nombre"
  | "nombre_desc"
  | "existencia_desc"
  | "existencia_asc"
  | "valor_desc"
  | "recientes";

export const consultor = {
  /**
   * `lista`: la lista de precios que enseña ESTE consultor, elegida al ponerlo.
   * Sin ella, el precio normal. Si la lista ya no está activa, el servidor da
   * el normal sin error: la pantalla del pasillo nunca se queda sin precio.
   */
  buscar: (texto: string, lista?: string | null, listas: string[] = []) =>
    api<ConsultaDePrecio>("/tenant/consultor", {
      // `listas`: las de cada moneda (2026-10-07). Vacías no viajan.
      params: { texto, lista: lista || undefined, listas: listas.join(",") || undefined },
    }),
};

// =============================================================================
//  AUTOSERVICIO (2026-10-15): la pantalla del restaurante donde el cliente pide
// =============================================================================

type AutoservicioOut = import("@/features/autoservicio/tipos").Autoservicio;

export const autoservicio = {
  obtener: () => api<AutoservicioOut>("/tenant/autoservicio"),
  guardar: (ajustes: import("@/features/autoservicio/tipos").AjustesAutoservicio) =>
    api<AutoservicioOut>("/tenant/autoservicio", { method: "PUT", body: ajustes }),
  subirFoto: (cual: "imagen" | "logo", archivo: File) =>
    api<AutoservicioOut>(`/tenant/autoservicio/${cual}`, { method: "POST", body: conArchivo(archivo) }),
  quitarFoto: (cual: "imagen" | "logo") =>
    api<AutoservicioOut>(`/tenant/autoservicio/${cual}`, { method: "DELETE" }),
  /** `todas`: para la vista previa del panel, con lo oculto y lo agotado. */
  carta: (todas = false) =>
    api<import("@/features/autoservicio/tipos").Carta>("/tenant/autoservicio/carta", {
      params: todas ? { todas: true } : undefined,
    }),
  pedir: (pedido: import("@/features/autoservicio/tipos").PedidoIn) =>
    api<import("@/features/autoservicio/tipos").PedidoHecho>("/tenant/autoservicio/pedidos", {
      method: "POST",
      body: pedido,
    }),
};

// =============================================================================
//  ETIQUETAS (2026-10-21): el precio y el código de barras para el estante
// =============================================================================

/** Las listas de cada moneda, separadas por comas. Vacías no viajan. */
const conListas = (listas: string[]) => listas.join(",") || undefined;

export const etiquetas = {
  /** Primero como código exacto (el lector) y, si no, por el nombre. */
  buscar: (texto: string, listas: string[] = []) =>
    api<import("@/features/etiquetas/tipos").BusquedaDeEtiquetas>("/tenant/etiquetas/buscar", {
      params: { texto, listas: conListas(listas) },
    }),
  /** La lista guardada en el equipo, con el precio de hoy. */
  datos: (variantes: string[], listas: string[] = []) =>
    api<import("@/features/etiquetas/tipos").DatosDeEtiqueta[]>("/tenant/etiquetas/datos", {
      method: "POST",
      body: { variantes, listas },
    }),
  varios: (
    filtro: { q?: string; category_id?: string; marca?: string; sin_marca?: true; supplier_id?: string },
    listas: string[] = [],
  ) =>
    api<import("@/features/etiquetas/tipos").VariosParaEtiquetas>("/tenant/etiquetas/productos", {
      params: { ...filtro, listas: conListas(listas) },
    }),
  entradas: () => api<import("@/features/etiquetas/tipos").EntradaParaEtiquetas[]>("/tenant/etiquetas/entradas"),
  deLaEntrada: (id: string, listas: string[] = []) =>
    api<import("@/features/etiquetas/tipos").FilaDeEntrada[]>(`/tenant/etiquetas/entradas/${id}`, {
      params: { listas: conListas(listas) },
    }),
};

/**
 * Producción por tandas (2026-10-21): las fórmulas y cada producción.
 * Todo de la sucursal del aparato. Ver `features/produccion`.
 */
export const produccion = {
  /** El tablero. `desde` = el comienzo de hoy en este aparato («producido hoy»). */
  formulas: (desde?: string) =>
    api<import("@/features/produccion/tipos").FormulaResumen[]>("/tenant/produccion/formulas", {
      params: { desde },
    }),
  formula: (id: string) =>
    api<import("@/features/produccion/tipos").Formula>(`/tenant/produccion/formulas/${id}`),
  crearFormula: (datos: import("@/features/produccion/tipos").FormulaAGuardar) =>
    api<import("@/features/produccion/tipos").Formula>("/tenant/produccion/formulas", {
      method: "POST",
      body: datos,
    }),
  editarFormula: (id: string, datos: import("@/features/produccion/tipos").FormulaAGuardar) =>
    api<import("@/features/produccion/tipos").Formula>(`/tenant/produccion/formulas/${id}`, {
      method: "PUT",
      body: datos,
    }),
  eliminarFormula: (id: string) =>
    api<void>(`/tenant/produccion/formulas/${id}`, { method: "DELETE" }),
  registrar: (datos: { formula_id: string; tandas: string; producido?: string; notas?: string }) =>
    api<import("@/features/produccion/tipos").Produccion>("/tenant/produccion", {
      method: "POST",
      body: datos,
    }),
  listar: (filtros: { desde?: string; hasta?: string; q?: string; page?: number; size?: number }) =>
    api<import("@/features/produccion/tipos").Producciones>("/tenant/produccion", { params: filtros }),
  ver: (id: string) =>
    api<import("@/features/produccion/tipos").Produccion>(`/tenant/produccion/${id}`),
  anular: (id: string, motivo: string) =>
    api<import("@/features/produccion/tipos").Produccion>(`/tenant/produccion/${id}/anular`, {
      method: "POST",
      body: { motivo },
    }),
};

// ── Reservaciones de mesas (2026-10-27) ──────────────────────────────────────
//
// La sucursal viaja en la cabecera, como en el resto del restaurante.

export const reservaciones = {
  listar: (desde: string, hasta: string) =>
    api<import("@/features/reservaciones/tipos").Reservaciones>("/tenant/reservaciones", {
      params: { desde, hasta },
    }),
  ver: (id: string) =>
    api<import("@/features/reservaciones/tipos").Reservacion>(`/tenant/reservaciones/${id}`),
  /** Lo que se sabe de ese teléfono: para autocompletar el nombre. */
  cliente: (telefono: string) =>
    api<import("@/features/reservaciones/tipos").ClienteDeReservacion>(
      "/tenant/reservaciones/cliente",
      { params: { telefono } },
    ),
  crear: (datos: import("@/features/reservaciones/tipos").ReservacionAGuardar) =>
    api<import("@/features/reservaciones/tipos").Reservacion>("/tenant/reservaciones", {
      method: "POST",
      body: datos,
    }),
  editar: (id: string, cambios: Partial<import("@/features/reservaciones/tipos").ReservacionAGuardar>) =>
    api<import("@/features/reservaciones/tipos").Reservacion>(`/tenant/reservaciones/${id}`, {
      method: "PATCH",
      body: cambios,
    }),
  cambiarEstado: (id: string, estado: "reservada" | "confirmada" | "no_vino" | "cancelada") =>
    api<import("@/features/reservaciones/tipos").Reservacion>(
      `/tenant/reservaciones/${id}/estado`,
      { method: "POST", body: { estado } },
    ),
  /** «Llegó»: abre la cuenta en la mesa con las personas de la reservación. */
  sentar: (id: string, mesaId: string | null) =>
    api<import("@/features/reservaciones/tipos").ReservacionSentada>(
      `/tenant/reservaciones/${id}/sentar`,
      { method: "POST", body: { mesa_id: mesaId } },
    ),
};

// ── Control de merma: conteos de inventario (2026-10-27) ─────────────────────
export const conteos = {
  listar: () => api<import("@/features/conteos/tipos").Conteos>("/tenant/conteos"),
  productos: () =>
    api<import("@/features/conteos/tipos").ProductoParaContar[]>("/tenant/conteos/productos"),
  desviacion: (limite = 12) =>
    api<import("@/features/conteos/tipos").Desviacion>("/tenant/conteos/desviacion", {
      params: { limite },
    }),
  /** Si ya hay uno abierto en la sucursal, devuelve ese. */
  nuevo: (nota: string | null) =>
    api<import("@/features/conteos/tipos").Conteo>("/tenant/conteos", {
      method: "POST",
      body: { nota },
    }),
  ver: (id: string) => api<import("@/features/conteos/tipos").Conteo>(`/tenant/conteos/${id}`),
  /** Anota lo contado de un producto. `null` lo borra del conteo. */
  anotar: (id: string, variantId: string, contado: string | null) =>
    api<import("@/features/conteos/tipos").LineaDeConteo | null>(`/tenant/conteos/${id}/lineas`, {
      method: "PUT",
      body: { variant_id: variantId, contado },
    }),
  aplicar: (id: string) =>
    api<import("@/features/conteos/tipos").Conteo>(`/tenant/conteos/${id}/aplicar`, {
      method: "POST",
    }),
  descartar: (id: string) =>
    api<import("@/features/conteos/tipos").Conteo>(`/tenant/conteos/${id}/descartar`, {
      method: "POST",
    }),
};

// ── Modo consultorio (2026-10-30) ────────────────────────────────────────────
//
// La sucursal viaja en la cabecera, como en todo lo demás. Lo clínico (el
// expediente, las notas, los adjuntos) pide «Atender pacientes» en el servidor:
// la recepción recibe un 403 aunque escriba la dirección a mano.

const C = "/tenant/consultorio";

export const consultorio = {
  ajustes: () => api<Consultorio.AjustesConsultorio>(`${C}/ajustes`),
  guardarAjustes: (
    cambios: Partial<
      Pick<
        Consultorio.AjustesConsultorio,
        "hora_inicio" | "hora_fin" | "intervalo" | "mensaje_recordatorio" | "mensaje_control"
      >
    >,
  ) => api<Consultorio.AjustesConsultorio>(`${C}/ajustes`, { method: "PUT", body: cambios }),

  profesionales: (todos = false) =>
    api<Consultorio.Profesional[]>(`${C}/profesionales`, {
      params: todos ? { todos: "true" } : undefined,
    }),
  crearProfesional: (datos: Consultorio.ProfesionalAGuardar) =>
    api<Consultorio.Profesional>(`${C}/profesionales`, { method: "POST", body: datos }),
  editarProfesional: (id: string, cambios: Consultorio.ProfesionalAGuardar) =>
    api<Consultorio.Profesional>(`${C}/profesionales/${id}`, { method: "PATCH", body: cambios }),
  subirFirma: (id: string, archivo: Blob) => {
    const datos = new FormData();
    datos.append("archivo", archivo, "firma.png");
    return api<Consultorio.Profesional>(`${C}/profesionales/${id}/firma`, {
      method: "POST",
      body: datos,
    });
  },

  salas: () => api<Consultorio.Sala[]>(`${C}/salas`),
  crearSala: (nombre: string) =>
    api<Consultorio.Sala>(`${C}/salas`, { method: "POST", body: { nombre } }),
  editarSala: (id: string, cambios: Partial<Pick<Consultorio.Sala, "nombre" | "activa" | "orden">>) =>
    api<Consultorio.Sala>(`${C}/salas/${id}`, { method: "PATCH", body: cambios }),

  agenda: (desde: string, hasta: string, profesionalId?: string | null) =>
    api<Consultorio.Agenda>(`${C}/agenda`, {
      params: { desde, hasta, ...(profesionalId ? { profesional_id: profesionalId } : {}) },
    }),
  porCobrar: () => api<Consultorio.Cita[]>(`${C}/por-cobrar`),
  cita: (id: string) => api<Consultorio.Cita>(`${C}/citas/${id}`),
  agendar: (datos: Consultorio.CitaAGuardar) =>
    api<Consultorio.Cita>(`${C}/citas`, { method: "POST", body: datos }),
  sinCita: (datos: Consultorio.SinCitaAGuardar) =>
    api<Consultorio.Cita>(`${C}/citas/sin-cita`, { method: "POST", body: datos }),
  editarCita: (id: string, cambios: Consultorio.CitaCambios) =>
    api<Consultorio.Cita>(`${C}/citas/${id}`, { method: "PATCH", body: cambios }),
  cambiarEstado: (
    id: string,
    estado: "agendada" | "confirmada" | "en_espera" | "no_vino" | "cancelada",
    motivo?: string | null,
  ) =>
    api<Consultorio.Cita>(`${C}/citas/${id}/estado`, { method: "POST", body: { estado, motivo } }),
  recordada: (id: string) =>
    api<Consultorio.Cita>(`${C}/citas/${id}/recordada`, { method: "POST" }),
  atender: (id: string) =>
    api<{ cita: Consultorio.Cita; nota: Consultorio.Nota }>(`${C}/citas/${id}/atender`, {
      method: "POST",
    }),
  terminar: (id: string, sinCobro = false) =>
    api<Consultorio.Cita>(`${C}/citas/${id}/terminar`, {
      method: "POST",
      body: { sin_cobro: sinCobro },
    }),
  agregarCargo: (id: string, itemId: string, cantidad = "1", variantId?: string | null) =>
    api<Consultorio.Cita>(`${C}/citas/${id}/cargos`, {
      method: "POST",
      body: { item_id: itemId, cantidad, variant_id: variantId ?? null },
    }),
  quitarCargo: (id: string, cargoId: string) =>
    api<Consultorio.Cita>(`${C}/citas/${id}/cargos/${cargoId}`, { method: "DELETE" }),
  /** Una venta normal con lo pendiente de la cita. Los precios los pone el servidor. */
  cobrar: (
    id: string,
    datos: {
      client_uuid: string;
      payments: PagoIn[];
      location_id?: string | null;
      order_tax_ids?: string[];
      cargo_ids?: string[];
    },
  ) => api<Sale>(`${C}/citas/${id}/cobrar`, { method: "POST", body: datos }),

  controles: (todos = false) =>
    api<Consultorio.Control[]>(`${C}/controles`, { params: todos ? { todos: "true" } : undefined }),
  editarControl: (
    id: string,
    cambios: { estado?: "pendiente" | "descartado"; fecha?: string; avisado?: boolean },
  ) => api<Consultorio.Control>(`${C}/controles/${id}`, { method: "PATCH", body: cambios }),

  pacientes: (q: string, pagina = 1) =>
    api<Consultorio.Pacientes>(`${C}/pacientes`, {
      params: { ...(q ? { q } : {}), pagina: String(pagina) },
    }),
  registrarPaciente: (datos: Consultorio.PacienteNuevo) =>
    api<Consultorio.Paciente>(`${C}/pacientes`, { method: "POST", body: datos }),
  paciente: (id: string) => api<Consultorio.Paciente>(`${C}/pacientes/${id}`),
  editarPaciente: (id: string, cambios: Consultorio.PacienteCambios) =>
    api<Consultorio.Paciente>(`${C}/pacientes/${id}`, { method: "PATCH", body: cambios }),
  citasDelPaciente: (id: string) => api<Consultorio.Cita[]>(`${C}/pacientes/${id}/citas`),

  expediente: (id: string) => api<Consultorio.Expediente>(`${C}/pacientes/${id}/expediente`),
  guardarFicha: (id: string, datos: Consultorio.DatosDeFicha) =>
    api<Consultorio.Ficha>(`${C}/pacientes/${id}/ficha`, { method: "PUT", body: datos }),
  versionesDeLaFicha: (id: string) =>
    api<Consultorio.Ficha[]>(`${C}/pacientes/${id}/ficha/versiones`),
  notas: (id: string, pagina = 1) =>
    api<Consultorio.Notas>(`${C}/pacientes/${id}/notas`, { params: { pagina: String(pagina) } }),
  nuevaNota: (pacienteId: string, datos: { cita_id?: string | null; plantilla?: string | null } = {}) =>
    api<Consultorio.Nota>(`${C}/pacientes/${pacienteId}/notas`, { method: "POST", body: datos }),
  nota: (id: string) => api<Consultorio.Nota>(`${C}/notas/${id}`),
  guardarNota: (
    id: string,
    cambios: {
      plantilla?: string;
      contenido?: Consultorio.ContenidoDeNota;
      nota_privada?: string | null;
      fecha_atencion?: string;
    },
  ) => api<Consultorio.Nota>(`${C}/notas/${id}`, { method: "PATCH", body: cambios }),
  firmarNota: (id: string) => api<Consultorio.Nota>(`${C}/notas/${id}/firmar`, { method: "POST" }),
  aclarar: (id: string, texto: string) =>
    api<Consultorio.Nota>(`${C}/notas/${id}/aclaratoria`, { method: "POST", body: { texto } }),
  descartarNota: (id: string) => api<void>(`${C}/notas/${id}/descartar`, { method: "POST" }),

  adjuntos: (id: string, conAnulados = false) =>
    api<Consultorio.Adjunto[]>(`${C}/pacientes/${id}/adjuntos`, {
      params: conAnulados ? { con_anulados: "true" } : undefined,
    }),
  subirAdjunto: (
    pacienteId: string,
    archivo: Blob,
    datos: {
      tipo: "foto" | "examen" | "documento";
      titulo?: string;
      zona?: string;
      nota_id?: string | null;
    },
  ) => {
    const cuerpo = new FormData();
    cuerpo.append("archivo", archivo, archivo instanceof File ? archivo.name : "foto.jpg");
    cuerpo.append("tipo", datos.tipo);
    if (datos.titulo) cuerpo.append("titulo", datos.titulo);
    if (datos.zona) cuerpo.append("zona", datos.zona);
    if (datos.nota_id) cuerpo.append("nota_id", datos.nota_id);
    return api<Consultorio.Adjunto>(`${C}/pacientes/${pacienteId}/adjuntos`, {
      method: "POST",
      body: cuerpo,
    });
  },
  anularAdjunto: (id: string, motivo: string) =>
    api<Consultorio.Adjunto>(`${C}/adjuntos/${id}/anular`, { method: "POST", body: { motivo } }),

  plantillasDeConsentimiento: (todas = false) =>
    api<Consultorio.PlantillaConsentimiento[]>(`${C}/plantillas-consentimiento`, {
      params: todas ? { todas: "true" } : undefined,
    }),
  guardarPlantillaDeConsentimiento: (
    datos: { titulo: string; texto: string; activa: boolean },
    id?: string,
  ) =>
    api<Consultorio.PlantillaConsentimiento>(
      id ? `${C}/plantillas-consentimiento/${id}` : `${C}/plantillas-consentimiento`,
      { method: id ? "PUT" : "POST", body: datos },
    ),
  consentimientos: (id: string) =>
    api<Consultorio.Consentimiento[]>(`${C}/pacientes/${id}/consentimientos`),
  firmarConsentimiento: (
    pacienteId: string,
    datos: {
      plantilla_id?: string | null;
      titulo?: string | null;
      texto?: string | null;
      firmante_nombre: string;
      firmante_documento?: string | null;
      firmante_relacion: string;
      firma: string;
      cita_id?: string | null;
    },
  ) =>
    api<Consultorio.Consentimiento>(`${C}/pacientes/${pacienteId}/consentimientos`, {
      method: "POST",
      body: datos,
    }),

  bitacora: (id: string) => api<Consultorio.EventoDeBitacora[]>(`${C}/pacientes/${id}/bitacora`),
  impreso: (id: string, que: "expediente" | "receta") =>
    api<void>(`${C}/pacientes/${id}/impreso`, { method: "POST", body: { que } }),
  medicamentos: (q: string) =>
    api<Consultorio.MedicamentoFrecuente[]>(`${C}/medicamentos`, { params: q ? { q } : undefined }),
  diagnosticos: (q: string) =>
    api<Consultorio.DiagnosticoBuscado[]>(`${C}/diagnosticos`, { params: q ? { q } : undefined }),
};

// =============================================================================
//  TURNOS (2026-11-02): el dispensador, el televisor y quien atiende
// =============================================================================

export const turnos = {
  obtener: () => api<import("@/features/turnos/tipos").Turnos>("/tenant/turnos"),
  guardar: (ajustes: import("@/features/turnos/tipos").AjustesTurnos) =>
    api<import("@/features/turnos/tipos").Turnos>("/tenant/turnos", { method: "PUT", body: ajustes }),
  /** Lo que leen el dispensador y el televisor de la sucursal del aparato. */
  equipo: () => api<import("@/features/turnos/tipos").EquipoDeTurnos>("/tenant/turnos/equipo"),
  tomar: (servicio_id: string) =>
    api<import("@/features/turnos/tipos").TurnoTomado>("/tenant/turnos/tomar", {
      method: "POST",
      body: { servicio_id },
    }),
  estado: (historial = 5) =>
    api<import("@/features/turnos/tipos").EstadoDeTurnos>("/tenant/turnos/estado", { params: { historial } }),
  llamar: (datos: import("@/features/turnos/tipos").LlamarTurno) =>
    api<import("@/features/turnos/tipos").Atencion>("/tenant/turnos/llamar", { method: "POST", body: datos }),
  repetir: (id: string) =>
    api<import("@/features/turnos/tipos").Atencion>(`/tenant/turnos/${id}/repetir`, { method: "POST" }),
  terminar: (id: string, estado: "atendido" | "ausente") =>
    api<import("@/features/turnos/tipos").Atencion>(`/tenant/turnos/${id}/terminar`, {
      method: "POST",
      body: { estado },
    }),
  quitar: (id: string) =>
    api<import("@/features/turnos/tipos").Atencion>(`/tenant/turnos/${id}/quitar`, { method: "POST" }),
  resumen: () => api<import("@/features/turnos/tipos").ResumenDeTurnos>("/tenant/turnos/resumen"),
  /**
   * El teléfono del cliente, SIN sesión: lo identifica la llave del QR, que
   * viaja en una cabecera y no en la dirección (ver `turnos/router.py`).
   */
  publico: (llave: string) =>
    api<import("@/features/turnos/tipos").TurnoPublico>("/turno", {
      publico: true,
      cabeceras: { "X-Softfi-Turno": llave },
    }),
};

// =============================================================================
//  PANTALLA PUBLICITARIA (2026-11-02): los anuncios en un televisor del local
// =============================================================================

export const publicidad = {
  obtener: () => api<import("@/features/publicidad/tipos").Publicidad>("/tenant/publicidad"),
  guardar: (datos: import("@/features/publicidad/tipos").GuardarPublicidad) =>
    api<import("@/features/publicidad/tipos").Publicidad>("/tenant/publicidad", { method: "PUT", body: datos }),
  subir: (archivo: File) =>
    api<import("@/features/publicidad/tipos").Publicidad>("/tenant/publicidad/anuncios", {
      method: "POST",
      body: conArchivo(archivo),
    }),
  quitar: (id: string) =>
    api<import("@/features/publicidad/tipos").Publicidad>(`/tenant/publicidad/anuncios/${id}`, {
      method: "DELETE",
    }),
};
